import { useState, useEffect, useRef, useCallback } from "react";
import { resolveValue, resolveBestOffer } from "./lib/valuation";
import { solveLoan, loanTotals, addOnFinancedCost } from "./lib/loanMath";
import { estimateTTL } from "./lib/stateTax";
import { ProvenanceBadge } from "./components/Atoms";
import { toApiMessages, redactStaleFigures } from "./lib/clydeAssistant";
import { UNTRUSTED_TEXT_RULE } from "./lib/untrustedText";
import { buildDealerResponsesBlock } from "./lib/dealerResponses";
import { SendToDealerPanel, DealerResponsesPanel } from "./components/DealerLinkPanels";

/* ── Design tokens — matches HomeFleet exactly ── */
const T={bg:"#f0f4f8",card:"#ffffff",cardAlt:"#f8fafc",border:"#e2e8f0",text:"#1e293b",textMd:"#475569",textSm:"#94a3b8",teal:"#0d9488",tealL:"#ccfbf1",amber:"#b45309",amberL:"#fef3c7",red:"#dc2626",redL:"#fee2e2",green:"#16a34a",greenL:"#dcfce7",blue:"#1d6fa8",blueL:"#dbeafe",slateL:"#f1f5f9",purple:"#7c3aed",purpleL:"#ede9fe",slate:"#334155",border2:"#cbd5e1",shadow:"0 1px 3px rgba(0,0,0,.07)"};
const CT={navy:"#0F1B2D",navyMid:"#1A2E4A",navyLt:"#243B55",slate:"#4A6080",sky:"#4A9ECC",white:"#FFFFFF",offWhite:"#F0F4F8",muted:"#A8BCCF",border:"rgba(255,255,255,0.08)",card:"rgba(255,255,255,0.04)"};
const uid=()=>Math.random().toString(36).slice(2,9);
const fmt=(n,d=0)=>(isFinite(+n)?+n:0).toLocaleString("en-US",{style:"currency",currency:"USD",maximumFractionDigits:d,minimumFractionDigits:d});
const num=v=>{const n=typeof v==="number"?v:parseFloat(String(v??"").replace(/[^0-9.-]/g,""));return isFinite(n)?n:0;};

// Same {live:"VinAudit",researched:"researched",estimated:"estimated"}
// source-tagging clydeAssistant.js's own buildCtx already uses for the
// general-chat persona — kept as a small, separate local copy rather
// than importing across these two files (this codebase's own existing
// convention — see e.g. each file's own separate T/CT theme constants —
// is to keep clydeAssistant.js and this file loosely coupled). This is
// deliberately NOT the same kind of duplication that caused the
// trade-value bug below: a two-line label lookup drifting is harmless:
// two independent dollar-figure CALCULATIONS drifting is not.
const SOURCE_TAG={live:"VinAudit",researched:"researched",estimated:"estimated"};
function srcTag(source){return SOURCE_TAG[source]||source||"estimated";}

const STYLES=`
/* No @font-face here, and no font import either. The faces are declared
   once, for every screen, by public/fonts/fonts.css, which index.html
   links. The Google @import that used to sit on this line was a second
   copy of a request the app had already made. */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body,#root{background:#f0f4f8;min-height:100vh;font-family:'Plus Jakarta Sans',sans-serif}
.mono{font-family:'DM Mono',monospace}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes clydePulse{0%,100%{box-shadow:0 0 0 0 rgba(74,158,204,0.5)}60%{box-shadow:0 0 0 10px rgba(74,158,204,0)}}
@keyframes clydeTyping{0%,80%,100%{transform:scale(0.7);opacity:0.4}40%{transform:scale(1);opacity:1}}
@keyframes clydeIn{from{transform:translateX(105%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes feedIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes recalc{0%{opacity:1}50%{opacity:.3}100%{opacity:1}}
@keyframes clydeFlash{0%{background:rgba(13,148,136,0.14);box-shadow:0 0 0 2px rgba(13,148,136,0.4)}100%{background:rgba(13,148,136,0);box-shadow:0 0 0 2px rgba(13,148,136,0)}}
.fade-up{animation:fadeUp .38s cubic-bezier(.22,1,.36,1) both}
.slide-in{animation:slideIn .3s cubic-bezier(.34,1.05,.64,1) both}
.feed-in{animation:feedIn .35s cubic-bezier(.22,1,.36,1) both}
.spin{animation:spin .75s linear infinite}
.blink{animation:pulse 1.8s ease-in-out infinite}
.recalc{animation:recalc .6s ease-in-out}
.clyde-flash{animation:clydeFlash 1.6s ease-out}
.ci2:focus{outline:none}.ci2::placeholder{color:#4A6080}
input:focus,select:focus{outline:2px solid #0d9488;outline-offset:1px;border-color:transparent!important}
button{cursor:pointer;font-family:inherit}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px}
`;

/* ── Demo fleet ── */
const FLEET=[
  {id:uid(),nickname:"Work Truck",year:2020,make:"Ford",model:"F-150",trim:"XLT",mileage:58200,value:31400,loan:24200,rate:7.4,payment:620,insurance:187,color:"Silver"},
  {id:uid(),nickname:"Family SUV",year:2021,make:"Chevrolet",model:"Tahoe",trim:"LT",mileage:48200,value:46300,loan:29800,rate:6.9,payment:690,insurance:210,color:"White"},
  {id:uid(),nickname:"Weekend Car",year:1965,make:"Ford",model:"Mustang",trim:"Fastback",mileage:99999,value:68000,loan:0,rate:0,payment:0,insurance:58,color:"Red"},
];

/* ── Demo incentives by make/model ── */
const INCENTIVES={
  "Ford|F-150":{year:"2025",apr:{rate:2.9,term:72,label:"Ford Motor Credit 2.9% APR"},cash:{amount:2500,label:"$2,500 Customer Cash"},conquest:{amount:1500,label:"Conquest Bonus (non-Ford trade)"},regional:{amount:800,label:"Texas Dealer Cash"},loyalty:{amount:750,label:"Ford Loyalty Bonus (current Ford owner)"}},
  "Chevrolet|Silverado":{year:"2025",apr:{rate:3.9,term:60,label:"GM Financial 3.9% APR"},cash:{amount:3000,label:"$3,000 Retail Bonus Cash"},conquest:{amount:1000,label:"Conquest Allowance"},regional:{amount:500,label:"Regional Performance Allowance"},loyalty:{amount:500,label:"Chevrolet Loyalty Bonus"}},
  "Toyota|RAV4":{year:"2025",apr:{rate:4.9,term:60,label:"Toyota Financial 4.9% APR"},cash:{amount:1000,label:"$1,000 Customer Cash"},conquest:null,regional:{amount:500,label:"Southeast Regional Bonus"},loyalty:{amount:500,label:"Toyota Loyalty Cash"}},
  "Honda|CR-V":{year:"2025",apr:{rate:3.9,term:60,label:"Honda Financial 3.9% APR"},cash:{amount:1500,label:"$1,500 Customer Cash"},conquest:{amount:1000,label:"Competitive Conquest Bonus"},regional:null,loyalty:{amount:500,label:"Honda Loyalty Cash"}},
  "Chevrolet|Tahoe":{year:"2025",apr:{rate:3.9,term:72,label:"GM Financial 3.9% APR"},cash:{amount:2000,label:"$2,000 Retail Bonus Cash"},conquest:{amount:1500,label:"Conquest Allowance"},regional:{amount:1000,label:"Texas Fleet & Retail Allowance"},loyalty:{amount:1000,label:"Chevrolet Loyalty Bonus"}},
  "BMW|3 Series":{year:"2025",apr:{rate:3.9,term:36,label:"BMW Financial 3.9% APR (36mo)"},cash:null,conquest:{amount:2000,label:"Competitive Conquest Bonus"},regional:null,loyalty:null},
};

/* ── Demo pre-owned listings ── */
const LISTINGS={
  "Ford|F-150":[
    {id:"l1",year:2022,make:"Ford",model:"F-150",trim:"XLT SuperCrew 4WD",mileage:31200,price:36900,dealer:"Lewisville Ford",dist:6.4,partner:true,vin:"1FTFW1E55NFC12345",color:"Oxford White",cpo:true,savings:-550,note:"CPO eligible — Ford 7yr/100k warranty available"},
    {id:"l2",year:2022,make:"Ford",model:"F-150",trim:"XLT SuperCrew 4WD",mileage:28400,price:35500,dealer:"Private Party (AutoTrader)",dist:null,partner:false,vin:null,color:"Carbonized Gray",cpo:false,savings:-1900,note:"Private sale — no warranty. Factor in pre-purchase inspection (~$150)."},
    {id:"l3",year:2022,make:"Ford",model:"F-150",trim:"XLT SuperCrew 4WD",mileage:37800,price:37200,dealer:"Five Star Ford",dist:9.2,partner:false,vin:"1FTFW1E55NFC98765",color:"Race Red",cpo:true,savings:250,note:"Above market average. Room to negotiate — Clyde will push on price."},
  ],
  "Chevrolet|Silverado":[
    {id:"l4",year:2022,make:"Chevrolet",model:"Silverado 1500",trim:"LT Crew Cab 4WD",mileage:34100,price:38500,dealer:"Hendrick Chevrolet",dist:4.2,partner:true,vin:"3GCUYDEDXNG123456",color:"Summit White",cpo:true,savings:-400,note:"GM Certified — 6yr/100k bumper-to-bumper"},
    {id:"l5",year:2021,make:"Chevrolet",model:"Silverado 1500",trim:"LT Crew Cab 4WD",mileage:41200,price:36200,dealer:"Classic Chevrolet",dist:12.8,partner:true,vin:null,color:"Black",cpo:false,savings:-1100,note:"One owner, clean Carfax. Well below market for the mileage."},
    {id:"l6",year:2022,make:"Chevrolet",model:"Silverado 1500",trim:"LT Crew Cab 4WD",mileage:29800,price:39900,dealer:"Private Party (Cars.com)",dist:null,partner:false,vin:null,color:"Northsky Blue",cpo:false,savings:900,note:"Priced above market. Inspection recommended."},
  ],
  "Chevrolet|Tahoe":[
    {id:"l7",year:2022,make:"Chevrolet",model:"Tahoe",trim:"LT 4WD",mileage:38200,price:49900,dealer:"Hendrick Chevrolet",dist:4.2,partner:true,vin:"1GNSKCKD0NR234567",color:"Iridescent Pearl",cpo:true,savings:-800,note:"GM Certified Plus — 6yr/100k. Strong deal for the spec."},
    {id:"l8",year:2021,make:"Chevrolet",model:"Tahoe",trim:"LT 4WD",mileage:52100,price:44500,dealer:"Classic Chevrolet",dist:12.8,partner:true,vin:null,color:"Black",cpo:false,savings:-1200,note:"Slightly higher mileage but $5k below comparable certified units."},
    {id:"l9",year:2022,make:"Chevrolet",model:"Tahoe",trim:"LT 4WD",mileage:31000,price:52200,dealer:"Private Party (AutoTrader)",dist:null,partner:false,vin:null,color:"Summit White",cpo:false,savings:1500,note:"Priced above dealer retail. Likely overpriced — Clyde can cite comps."},
  ],
};

// Returns [] for any make/model not in the static LISTINGS map above —
// NEVER a different vehicle's demo data. This used to fall back to
// LISTINGS["Ford|F-150"], which meant shopping for e.g. a Honda CR-V
// silently substituted a Ford F-150's price/mileage/VIN with no
// indication anything had gone wrong (confirmed live: a real session's
// worksheet showed "$36,900 Dealer price" for a 2020 CR-V — an exact
// match to LISTINGS["Ford|F-150"][0].price). Every caller below already
// tolerates an empty array (basePrice/mileage/vin all fall back further,
// and the "listings" screen render filters out missing slots) — see
// each call site's own comment for how it degrades instead of crashing.
function getListings(make,model){return LISTINGS[make+"|"+model]||[];}
function getIncentives(make,model){return INCENTIVES[make+"|"+model]||INCENTIVES["Ford|F-150"];}

// buildLiveInv (a reshape of researchIncentives's raw array down to one
// fixed apr/cash/conquest/regional/loyalty slot each) used to live here.
// Removed — the financing/incentive selector (BuyingModal's own
// researchedPrograms, see the "Financing/incentive program selector"
// comment below) reads the raw array directly now, keeping every distinct
// program instead of discarding all but the first match per type.

/* ── Add-ons — illustrative dealer-add-on catalog, same "labeled demo
   data" status as INCENTIVES/LISTINGS above. Real per-dealer add-on
   pricing isn't a data source this app has access to; these are
   reasonable, commonly-seen line items so the Add-ons tile has something
   real to demonstrate the financed-cost-over-term math against. ── */
const ADD_ONS=[
  {id:"warranty",label:"Extended Warranty (7yr/100k)",price:2200},
  {id:"gap",label:"GAP Insurance",price:800},
  {id:"protection",label:"Paint & Fabric Protection",price:600},
  {id:"tint",label:"Window Tint",price:400},
  {id:"nitrogen",label:"Nitrogen-Filled Tires",price:150},
];

/* ── Credit tiers — static offsets layered on the real, live FRED
   national-average auto-loan rate (functions/api/fred-rate.js), not a
   second guessed baseline. Offsets approximate widely-published
   tier spreads (e.g. Experian's State of the Automotive Finance
   Market); not sourced from a live per-tier feed, so always shown
   `estimated`. "prime" is the 0-point tier — it's meant to read close
   to the FRED baseline itself. ── */
// qualifiesForApr: manufacturer-subvented APR offers (Ford Motor Credit,
// GM Financial, etc.) are advertised "for well-qualified buyers" — only
// prime and better actually get approved at the advertised rate. Below
// that, real dealer financing falls back to standard-rate lending, so
// the financing/incentive selector (BuyingModal's own programRate) only
// honors an "apr"-type program's own rate for these two tiers.
const CREDIT_TIERS=[
  {id:"superPrime",label:"Super Prime (781+)",offset:-1.8,qualifiesForApr:true},
  {id:"prime",label:"Prime (661-780)",offset:0,qualifiesForApr:true},
  {id:"nonPrime",label:"Nonprime (601-660)",offset:2.9,qualifiesForApr:false},
  {id:"subPrime",label:"Subprime (501-600)",offset:6.8,qualifiesForApr:false},
  {id:"deepSubPrime",label:"Deep Subprime (≤500)",offset:10.5,qualifiesForApr:false},
];
const FALLBACK_BASE_RATE=7.5; // used only if the live FRED fetch hasn't resolved yet

/* ── Payment calculator ── */
function calcPayment(principal,annualRate,termMonths){
  if(annualRate===0)return Math.round(principal/termMonths);
  const r=annualRate/100/12;return Math.round(principal*r*Math.pow(1+r,termMonths)/(Math.pow(1+r,termMonths)-1));
}

async function fetchMarketValueLive(vin,mileage,type){
  if(!vin||vin.replace(/\s/g,"").length<11)return null;
  try{
    const q=new URLSearchParams({vin:vin.replace(/\s/g,"").toUpperCase()});
    if(mileage)q.set("mileage",String(mileage));
    if(type)q.set("type",type);
    const res=await fetch(`/api/vinaudit?${q.toString()}`);
    if(!res.ok)return null;
    const d=await res.json();
    if(!d.success||!d.prices?.average)return null;
    return{amount:Math.round(d.prices.average/100)*100,low:d.prices.below,high:d.prices.above,source:"vinaudit"};
  }catch{return null;}
}

async function fetchRealListings(year,make,model,priceMax,zip){
  try{
    const q=new URLSearchParams({year:String(year||""),make:make||"",model:model||""});
    if(priceMax)q.set("price_max",String(priceMax));
    if(zip)q.set("zip",zip);
    const res=await fetch(`/api/market-listings?${q.toString()}`);
    // A 429 is the shared daily research budget being spent, not a failed
    // search -- the endpoint refused before looking at anything. Returning
    // null here is what made the panel announce 'No real-time listings
    // found for this vehicle right now', a confirmed negative about the
    // market that nothing had checked. See the unchecked tier in
    // HOMEFLEET_PROVENANCE_PLAN.md. Every other failure still returns
    // null and is still reported as a failed search, unchanged.
    if(res.status===429)return{source:"unchecked",listings:null};
    if(!res.ok)return null;
    const d=await res.json();
    // Defense in depth. The endpoint no longer substitutes a make-only
    // search for a missed exact match (see functions/api/market-listings.js),
    // so this should never be true -- but if it is ever reintroduced,
    // those listings describe a different vehicle and must not reach the
    // worksheet. Treated as no listings rather than shown with a caveat:
    // a substitute is not weaker evidence about this car, it is evidence
    // about another one. Same reasoning as the proposal screen's own
    // guard -- should be unreachable is not what belongs between wrong
    // data and a household's deal.
    if(d.usedFallback)return null;
    if(!d.listings||!d.listings.length)return null;
    const rawListings=d.listings.slice(0,3);
    const avg=rawListings.reduce(function(s,l){return s+num(l.listing_price);},0)/rawListings.length;
    // usedFallback means the endpoint could not match this exact vehicle
    // and re-queried on the make alone -- dropping year AND model -- so
    // these listings may be entirely different cars. The server has always
    // said so (see functions/api/market-listings.js, which also returns
    // the spec_id it tried); this is the first time the client keeps it.
    // Carried, not yet acted on: whether to drop these or label them is a
    // behaviour decision that needs evidence about what the fallback
    // actually returns, and guessing would bake in the wrong answer.
    return{source:"researched",
      // True only when the server had no real zip and fell back to its
      // hardcoded Frisco/DFW default (functions/api/market-listings.js) --
      // never set from anything computed here. See that file's own
      // comment for why this and usedFallback are different, unrelated
      // flags: this is about WHERE the search centered, not WHICH vehicle
      // it matched.
      usedDefaultLocation:!!d.usedDefaultLocation,
      defaultLocationLabel:d.defaultLocationLabel||null,
      listings:rawListings.map(function(l,i){
      return{
        id:l.id||("live-"+i),
        year:l.vehicle_year||year,
        make:l.vehicle_make||make,
        model:l.vehicle_model||model,
        trim:l.vehicle_trim||"",
        mileage:num(l.listing_mileage),
        color:l.vehicle_color_exterior||"",
        price:num(l.listing_price),
        savings:Math.round(num(l.listing_price)-avg),
        dealer:l.name||"Dealer",
        dist:null,
        partner:false,
        cpo:!!l.certified_flag,
        vin:l.vin||null,
        url:l.listing_vdp_url||null,
      };
    })};
  }catch{return null;}
}

async function fetchRealIncentives(year,make,model,trim){
  try{
    const res=await fetch("/api/research-incentives",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({year:year||"",make:make||"",model:model||"",trim:trim||""})});
    if(!res.ok)return null;
    const d=await res.json();
    if(!d.incentives)return null;
    // Returns the whole {incentives,source,origin} object, not the bare
    // array it used to. research-incentives.js already distinguishes a
    // real search that came back empty (source:"researched", incentives:
    // []) from one that failed or returned nothing usable (source:
    // "estimated") — see that file's own two return statements. Throwing
    // source away here is what collapsed those two into one indistinguish-
    // able "no incentives" on screen, which is how a failed check got
    // presented to a household as a confirmed absence while a real 2.99%
    // APR offer and up to $3,000 cash back existed. Note an empty array
    // is truthy, so the `!d.incentives` guard above still only catches a
    // genuinely malformed response, not a legitimately empty result.
    return d;
  }catch{return null;}
}

// Real per-trim price lookup for the wizard's trim step "dig deeper"
// button — reuses the existing /api/research-value endpoint exactly as
// BuyingModal's own retailLookup effect already does (that endpoint
// already accepts and researches by trim, confirmed by reading it), not
// a new backend call. Nothing in functions/ needed adding for this.
async function fetchRealTrimPrice(year,make,model,trim){
  try{
    const res=await fetch("/api/research-value",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({year:year||"",make:make||"",model:model||"",trim:trim||""})});
    if(!res.ok)return null;
    const d=await res.json();
    if(d.amount==null)return null;
    return d;
  }catch{return null;}
}

// Guided-intake wizard steps (ClydeChat) — trim only now. Trade/down
// payment/budget/new-used/YMM moved to Kate's own wizard (ClydePanel.jsx)
// once she took ownership of the intake — Clyde now only ever sees a
// household after a confirmed handoff already gave him YMM (see
// HANDOFF_CONFIRMED's own step transition below), so trim is genuinely
// the only question left for him to ask. Routed through the real Claude
// turn (send()) since it needs genuine trim understanding, not just a
// number — extract_deal_intent already exists for exactly this. Typing
// free text here that isn't a real answer just falls through to normal
// free-form chat, same escape-hatch reasoning as before, just with one
// step instead of six.
const WIZARD_STEPS=[
  {key:"trim",prompt:"Any specific trim in mind? If not, tell me your must-haves and I'll help find the best match."},
];
const CLYDE_GREETING="Hi, I'm Clyde — your personal car buying advisor.\n\nI can see your fleet and I already know your trade equity and current payments. Tell me what you're looking for and I'll research current incentives, find the best available inventory, build your deal structure, and write the offer you send to dealers.\n\nI don't contact anyone myself — you send it, from your own email, when you decide it's ready.\n\nWhat are you looking for?";
// Same mechanism as ClydePanel's, see HOMEFLEET_WIZARD_MESSAGING_PLAN.md.
// The greeting is a message and lives where messages live; the wizard
// question is appended when the wizard actually starts, so ordering is
// array order rather than JSX position.
function clydeGreetingMessage(){return{role:"assistant",kind:"greeting",content:CLYDE_GREETING};}
function clydeWizardQuestion(step){return{role:"assistant",kind:"wizard-question",stepKey:step.key,content:step.prompt};}

/* ── Clyde: the deal-focused specialist persona — deal generation,
   research, dealer outreach. Renamed from this function's old name,
   buildBuyingSystem. Inherits the "favorite uncle who knows the car
   business" voice that used to live in clydeAssistant.js's own general
   persona (now Kate's function, buildKateSystem) — that voice belongs to
   Clyde specifically, not to a general-purpose concierge, so it moved
   here rather than being written twice. ── */
// pendingHandoff: Kate's distilled extract_deal_intent capture, resolved
// down to the specific fields she's meant to gather (trade/down payment/
// budget/YMM — trim stays Clyde's own deep-dive). The trade figure is
// re-resolved here via resolveBestOffer against THIS conversation's own
// fleet, same as Kate's own formatHandoffSummary (ClydePanel.jsx) —
// never taken from pendingHandoff itself, since extract_deal_intent only
// ever captures a trade vehicle's nickname, never a dollar amount. This
// is what drives Clyde's hard confirmation gate: his very first move is
// to recap ONLY these exact fields and wait, never to start researching
// on the strength of a handoff nobody's confirmed yet.
function formatHandoff(pendingHandoff,fleet){
  if(!pendingHandoff)return "";
  const parts=[];
  if(pendingHandoff.year||pendingHandoff.make||pendingHandoff.model)parts.push("vehicle: "+[pendingHandoff.year,pendingHandoff.make,pendingHandoff.model].filter(Boolean).join(" "));
  if(pendingHandoff.isNew!=null)parts.push(pendingHandoff.isNew?"new":"pre-owned");
  if(pendingHandoff.tradeVehicleNickname){
    const v=fleet.find(function(x){return x.nickname.toLowerCase()===pendingHandoff.tradeVehicleNickname.toLowerCase();});
    const resolvedValue=v?resolveValue(v):null;
    const bo=(v&&resolvedValue&&resolvedValue.amount!=null)?resolveBestOffer(v,resolvedValue.amount):null;
    parts.push("trade-in: "+pendingHandoff.tradeVehicleNickname+(bo?" (real offer $"+bo.amount.toLocaleString()+")":" (offer not yet resolved)"));
  }
  if(pendingHandoff.downPayment)parts.push("down payment: $"+pendingHandoff.downPayment.toLocaleString());
  if(pendingHandoff.budget)parts.push("budget: $"+pendingHandoff.budget.toLocaleString()+(pendingHandoff.budgetType==="monthly"?"/mo":" total"));
  if(!parts.length)return "\n\nHANDOFF FROM KATE (critical, first move): Kate has handed this conversation to you, but hadn't actually captured any specific details yet. Your very first reply MUST say so plainly and ask what they're looking for -- do not invent or assume a vehicle, budget, or trade. Do not start any research or mention inventory, pricing, or dealers until they've told you something real to work from.";
  return "\n\nHANDOFF FROM KATE (critical, first move): Kate has already gathered the following from the household -- "+parts.join(", ")+". Your very first reply in this conversation MUST: (1) warmly recap these exact figures back to them, restated plainly, using ONLY the numbers given here -- never recalculate, estimate, or add anything not listed; (2) ask directly whether this is still accurate; (3) then STOP and wait for their answer. Do NOT start any research, do NOT mention inventory, pricing, or dealers, and do NOT call extract_deal_intent to restate what's already given, until they've explicitly confirmed. Once they confirm it's accurate (a clear yes, 'that's right', 'looks good', or similar), append the exact token HANDOFF_CONFIRMED at the very end of that same reply, then continue normally into the actual vehicle -- researching it and, if trim isn't already known, asking about it. If they say something is wrong or different from what's listed here, do NOT append the token -- update your understanding, confirm the correction back to them, and only append HANDOFF_CONFIRMED once they've confirmed the corrected picture is accurate.";
}

function buildClydeSystem(fleet,mode,tradeId,liveTradeOffer,householdState,householdZip,justResumed,pendingHandoff,worksheetSummary,dealerResponsesBlock){
  // One-turn-only hint, mirrors clydeAssistant.js's buildSystem — set by
  // ClydeChat only for the single reply right after a past buying
  // conversation is loaded back into msgs, then cleared client-side.
  const resumeNote=justResumed?"NOTE: this conversation was just resumed from stored history — treat any dollar figure recalled from it as not current; re-verify via the appropriate tool or resolver before restating it.\n\n":"";
  // Real resolvers only (resolveValue/resolveBestOffer, imported at the
  // top of this file — already what BuyingModal's own worksheet math
  // uses below) — never the old local formula-only helpers this
  // function used to call directly, which never checked for real
  // enriched/researched vehicle_financials data at all. resolveBestOffer
  // specifically, not resolveValue, is the authoritative trade-in figure
  // — resolveValue is what the vehicle is worth to KEEP, not what a
  // dealer would pay for it as a trade. This is the same fix as
  // clydeAssistant.js's own TRADE VALUE RULE correction below.
  const fleetStr=fleet.map(function(v){
    // isTrade no longer swaps liveTradeOffer IN for bo — it used to
    // (see git history), which meant TRADE-IN OFFER itself would
    // silently flip between the formula-based resolveBestOffer figure
    // and a live VinAudit figure depending on timing, with nothing
    // telling the household the number's basis had changed under the
    // same label. bo/tradeInLine now always come from resolveBestOffer,
    // unconditionally — TRADE-IN OFFER means one consistent thing. The
    // live VinAudit figure (when this IS the vehicle currently being
    // traded, and that lookup has resolved) is surfaced as its own,
    // separately-labeled, informational-only line instead — see
    // marketCheckLine below.
    const isTrade=v.id===tradeId&&liveTradeOffer;
    const resolvedValue=resolveValue(v);
    const bo=resolvedValue.amount!=null?resolveBestOffer(v,resolvedValue.amount):null;
    const net=bo?bo.amount-num(v.loan):null;
    const tradeInLine=bo?"TRADE-IN OFFER: $"+bo.amount.toLocaleString()+" ("+srcTag(bo.source)+")":"TRADE-IN OFFER: not available (no value on file to estimate from)";
    const netLine=net!=null?" | Net trade equity after payoff: $"+net.toLocaleString():"";
    // Informational only, per this file's own TRADE VALUE RULE below —
    // never the figure to do trade math with, never a substitute for
    // TRADE-IN OFFER above. A real, live, paid VinAudit call already
    // happens for whichever vehicle the household is actively trading
    // (BuyingModal's own liveTradeOffer fetch) — this is what makes that
    // result actually reach Clyde instead of being fetched and discarded.
    const marketCheckLine=isTrade?" | Current market check (VinAudit, informational only — not the trade-in offer): $"+liveTradeOffer.amount.toLocaleString():"";
    return v.nickname+" — "+v.year+" "+v.make+" "+v.model+" | VIN "+(v.vin||"not on file")+" | "+v.mileage.toLocaleString()+" mi | Loan payoff $"+num(v.loan).toLocaleString()+" | Rate "+v.rate+"% | $"+v.payment+"/mo | "+tradeInLine+netLine+marketCheckLine;
  }).join("\n");
  const handoffNote=formatHandoff(pendingHandoff,fleet);
  const text="You are Clyde, HomeFleet's own car-business expert — the uncle in the family everyone calls before they buy a vehicle. You're helping a household buy their next vehicle: real deal structure, real research, real offers the household sends themselves.\n\nVoice: you're the favorite uncle who happens to know the car business inside out — warm, genuinely glad to help, a little excited when you spot a good deal, and completely honest when something is a bad idea. Use their name and their vehicles' nicknames naturally. React to good news ('Oh that's a strong trade position'). Deliver hard truths kindly and plainly. Never lecture. Short paragraphs, plain talk, no jargon dumps. Never use markdown (no asterisks, no bold, no bullet lists) — this chat renders plain text only.\n\nThe household's current fleet:\n"+fleetStr+"\n\nCRITICAL — TRADE VALUE RULE: each vehicle above has exactly one TRADE-IN OFFER figure, one Loan payoff figure, and one Net trade equity figure. When discussing or proposing a trade value, you MUST use the TRADE-IN OFFER dollar amount exactly as listed — never the loan payoff, never a recalculated or estimated number, never a rounded-off guess. The loan payoff is what is OWED, not what the trade is worth — do not confuse the two or present the payoff as the trade value. A vehicle currently being actively traded may also show a \"Current market check (VinAudit, informational only)\" figure — that is a separate, real, live valuation pull, useful context you can mention (e.g. if it's notably different from the trade-in offer, that's worth pointing out to the household), but it is never the number to use for trade math or to state as \"the trade value\" — TRADE-IN OFFER remains the one figure for that, always.\n\nDEAL MATH DISCIPLINE (critical, no exceptions): every single time you calculate or restate a deal — target price, amount financed, monthly payment, or trade equity — you MUST explicitly restate, in that same message, the exact loan payoff and trade-in offer figures for the vehicle involved, straight from the fleet data above, before doing any arithmetic with them. Never do deal math silently or from memory of an earlier turn — always re-pull the numbers from the fleet data above fresh, every time, even late in a long conversation. If you're unsure which vehicle is being traded, ask rather than guess or omit the payoff. The same discipline applies to a dollar figure recalled from a stored past conversation — it is never current; re-run the appropriate tool or resolver before restating it as fact. If a WORKSHEET SNAPSHOT block appears separately below, that's a different case entirely -- it reflects exactly what's on screen right now, freshly rebuilt this very turn, not something recalled or stored -- treat every figure in it as current and authoritative on sight, never as something to re-verify, recompute, or wait on a tool for. When you call calculate_loan for the vehicle currently under discussion, you no longer need to (and shouldn't) supply a price yourself -- once a real deal is in progress, it automatically uses the worksheet's own current financed basis: the net price in that snapshot plus any add-ons rolled into the same loan, so your result and the worksheet can never disagree on the payment. When add-ons are selected that figure is deliberately higher than the snapshot's own 'Net price' line, by exactly the add-ons total -- if you refer to it, call it the amount being financed rather than the net price, so the two never read as conflicting answers to the same question. The same applies to down_payment: omit it for a plain 'what's my payment right now' question -- it automatically uses the worksheet's own current trade equity plus any down payment already on file, so your result and the worksheet can never disagree on that either. Only supply down_payment yourself when the household explicitly proposes a specific different amount to explore ('what if I put $2,000 down instead') -- that's the one case where your own number should override the worksheet's. Rate and term remain yours to vary freely either way; only pass a price yourself for a general hypothetical with no specific vehicle/deal in progress yet. HARD RULE, no exceptions: never state a specific monthly payment, amount financed, or total interest figure in a reply unless you called calculate_loan in that exact same turn to produce it -- not even one that feels quick or obvious enough to work out yourself. This applies even when the household combines a price and a payment question into one message (e.g. 'at $53,500 with a $1,200 discount, what's my payment?') -- that combined question still requires calling the tool before you answer the payment part, never mental math as a shortcut, and never partway (stating the price in prose is fine, but the payment/financed/interest numbers only ever come from the tool's own result). If you haven't called it yet this turn and are about to state one of those figures, call it now instead.\n\nPRICE COMPARISONS, same kind of rule: never state that one price is higher, lower, cheaper, more expensive, above or below another, or by how much, unless you called compare_prices in that exact same turn and are using its result -- its figures, its difference and its direction, exactly as returned. Work out no difference yourself, however simple it looks. Give the worksheet's figures, the offer that was sent, and dealer counter-offers to the tool only as refs, never as a number you remember: a remembered figure can be out of date, and the tool looks up the current one. If the tool says a figure does not exist, do not state one -- say it is not on record. The household sees the tool's result beside your reply, so a figure in your prose that differs from it will be visible as wrong.\n\nWORKSHEET FIGURE PROVENANCE: the WORKSHEET SNAPSHOT tags where its price figures came from, and you must carry that tag into how you talk about them. Retail value is followed by its own tag in parentheses -- live, researched, estimated, or placeholder. When you state that figure, name the tag with it (e.g. 'the researched retail value is $X'), and never present it as a confirmed, agreed, or negotiated price. A 'placeholder' tag means there is no real data behind that number at all -- say that plainly rather than quoting it as though it had been researched. Separately, if the Dealer price line says no real listing was found, you MUST say so any time you reference that number: it is the figure the household would walk into a dealership and negotiate from, so they need to know it is standing in from the retail value rather than a real asking price someone is actually offering.\n\nMSRP RULE (hard, no exceptions): an MSRP you cannot point at is forbidden, and the only way to state one you CAN point at is the cite_sourced_msrp tool. Never state an MSRP or sticker figure -- not for a trim, not as a range, not as an 'around' number -- from your own knowledge, memory, or general sense of what a vehicle costs, no matter how confident you feel. That prohibition is absolute and nothing below softens it. The one permitted case is a figure a real, specific source in THIS turn's own research actually states for this vehicle and trim: a manufacturer's own page, a dealer's listing for a specific car, a pricing site's spec page. In that case call cite_sourced_msrp with the figure, the specific source, the trim, and the url if your results gave you a real one -- then you may discuss it in prose. Calling the tool is what makes the number sayable; stating one in prose without having called it is the same violation as inventing it. Never call the tool to launder a remembered number: if your search did not turn up a figure, you do not have one. The household is shown your citation with the source named beside it and labelled as yours rather than something HomeFleet verified, and if you cite a second, different MSRP for the same vehicle they are shown both and told the two disagree -- so keep to what your source actually says, and if the snapshot shows you have already cited one, do not quietly replace it with another. When you have no sourced figure, say plainly that you do not have a confirmed sticker for that trim, then give the retail value instead with its tag named, framed as a market estimate rather than a sticker -- e.g. 'I don't have a confirmed sticker for this trim. What I do have is a researched retail value of $X -- that's a market estimate, not MSRP, so treat it as a starting point.' If that retail value is itself tagged placeholder, say so rather than presenting it as though it had been researched. A number you cannot source is worse than no number: the household may quote it at a dealership.\n\nINCENTIVE CREDIBILITY: manufacturer incentives, APR offers, and cash-back figures you reference or search for are estimates until confirmed at the dealer — always say so in plain language when you state one (e.g. 'estimated — confirm with the dealer') rather than presenting them as locked-in numbers. Never state an incentive figure as guaranteed. Beyond hedging the figures themselves, the WORKSHEET SNAPSHOT carries an 'Incentive research' line telling you which of three situations you are actually in, and what you say MUST match it. If a real search ran and found offers, hedge them exactly as above. If a real search ran and genuinely found none, you may say plainly that you checked and there is nothing current for this vehicle. If the search did NOT complete, you must never say there are no incentives -- say you could not verify them right now, and that it is worth checking the manufacturer's own offers page before assuming none exist. Presenting a check that failed as a confirmed absence is the worst error available to you here: it has already cost a real household a live 2.99% APR offer and thousands in cash back. That line describes the AUTOMATED check only, and it is not the only source: your own web search regularly finds offers it misses. When your own research turns up a real offer, call report_found_incentive so it lands on the worksheet instead of living only in this conversation -- otherwise the panel keeps stating the automated result as though it were the whole answer, and the household sees the sheet contradicting you. Everything above applies to what you report exactly as it applies to the automated results: these are estimates until confirmed at the dealer, never guaranteed, and you say so in plain language every time you state one. Two rules specific to reporting. Only report what a source in this conversation's own search actually states -- never a figure from memory or from a general sense of what manufacturers usually run. And never report an offer you have already judged expired: if the end date has passed or the page is months stale, say that in conversation and do not put it on the sheet, because a household will quote a worksheet figure at a dealership. Once you have reported something, the snapshot says so, and you must not then tell the household nothing was found -- the automated check and your own research are separate sources, the sheet shows both, and they can differ about what was found without disagreeing about whether anything was.\n\nPRESENTING INVENTORY (live mode only): when you find real vehicle listings via web search, present each one as plain conversational text AND append a machine-readable line directly after, in exactly this format, one per listing: LISTING: {\"year\":2024,\"make\":\"Ford\",\"model\":\"F-150\",\"trim\":\"XLT\",\"price\":42000,\"mileage\":15000,\"dealer\":\"Example Ford\",\"url\":\"https://actual-url-from-search\"}. Rules: the url MUST be a real URL that appeared in your search results — never invent, guess, or construct one. Prefer sources that give a specific per-vehicle page (a dealer's own inventory listing, or a Cars.com/Autotrader/CarGurus/TrueCar listing page) over a general article or homepage, since those are more likely to link to the exact vehicle rather than a generic page. If you cannot find a real URL for a vehicle you're describing, do not include a LISTING line for it — describe it in text only and say the link isn't available. Never emit a LISTING line in demo mode.\n\nSELL-ONLY INTENT (no replacement wanted): if the household says they want to sell, get rid of, or stop having an existing vehicle WITHOUT wanting a replacement for it -- this is different from a trade-in, which always implies buying something new in the same breath -- do not start researching a purchase target and do not call extract_deal_intent for a buy-side make/model. Instead, identify which of their current vehicles they mean, state its TRADE-IN OFFER figure from the fleet data above as your best estimate of what it's worth to sell, and tell them plainly that HomeFleet doesn't have an active sell-negotiation or listing flow yet -- just that estimate. If it's genuinely ambiguous whether they want a replacement (e.g. they only said 'I want to sell my truck' with no other context), ask directly rather than assuming either way.\n\nOFFER PRICE -- WHO DECIDES (hard rule, no exceptions): the worksheet separates two different numbers, and you own neither of them. The ASKING PRICE is what a seller wants: a dealer quote, a listing price, a figure the household says they were quoted. That is a fact, you may record it with extract_deal_intent's askingPrice field, and you should whenever one is actually stated. YOUR OFFER is what the household will pay. It is theirs to decide, and you have NO tool that writes it -- not extract_deal_intent, not calculate_loan, none. This is not a matter of waiting your turn or asking permission first; there is simply no mechanism, so do not try to route an offer through any other field. Never say or imply that you have set, updated, changed, or synced the offer, and never say a figure is now on the worksheet unless the WORKSHEET SNAPSHOT in front of you already shows it. Saying it is done when it is not is worse than not doing it: the household may walk into a dealership believing the sheet agrees with them. The snapshot always tells you the truth about both figures -- read it before describing either. If the household wants the offer changed, they type it into the Your offer field on the worksheet themselves, and you can say so plainly. When you think they should offer a particular number -- whether they asked what to offer or you spotted the opening yourself -- call suggest_offer_price rather than only saying it in prose. That puts your number on the worksheet as a card beside the offer field, with the supporting figures, and a button they can press. It does NOT set anything: they accept or dismiss it, so say you have suggested it, never that you have set it. Every figure you cite must come from the WORKSHEET SNAPSHOT, because the worksheet re-checks each one against its own values and shows the household where you differ -- a number you half-remembered will be visible to them as a discrepancy, sitting under your reasoning. If the snapshot has no researched retail value to reason from, say that plainly and ask what they want to do, rather than suggesting a figure you cannot source. Evaluating a number THEY propose is the same job in reverse: say plainly whether it looks high, low or reasonable against the retail value and the listings in the snapshot, naming those figures, rather than simply agreeing with it.\n\nDEAL INTENT CAPTURE: call the extract_deal_intent tool any time you learn a concrete, new, or changed deal detail -- replacing an existing vehicle or adding, new or pre-owned, make/model preference, must-have features, or a monthly payment target or price ceiling -- even if the picture is still incomplete. Call it again whenever something changes, not just once; this is what keeps the worksheet in sync with the conversation as it develops, instead of only appearing once everything has been asked. Do not wait to gather everything first, and do not withhold calling it while you keep asking follow-up questions -- capture what you have now, then keep talking. This applies just as much once you're deep into structuring the deal, not just during intake: the moment a seller's asking price, a discount, an incentive total, a financing rate or term, or a trade allowance is actually CONFIRMED or firmly proposed -- by the household, by a dealer response, or by you and the household settling on one together -- call extract_deal_intent again right then with that field (askingPrice/discount/incentives/financingRate/financingTermMonths/tradeAllowance). This is what puts real deal terms on the worksheet instead of leaving them sitting only in this conversation. Don't call it for a number you're still exploring with calculate_loan -- only once something is actually settled on.\n\nDEAL COMPLETION: if the household directly and unambiguously says the purchase is done -- e.g. 'we bought it', 'we signed the papers', 'picking it up tomorrow', 'the dealer accepted our offer and we're done' -- append the exact token MARK_PURCHASED at the very end of that same reply. A direct, unambiguous statement that it's done is always sufficient on its own, no need to ask first. Separately, if the conversation organically suggests the deal may be finished but it's not fully clear (e.g. they mention picking up keys or a delivery date without directly confirming), ask directly whether the deal is done and they'd like it marked purchased -- only append MARK_PURCHASED once they confirm yes. Never append it while still asking a question in that same message, and never guess -- if genuinely unsure, ask rather than emit it.\n\nTRIM SELECTION: when the household is settling on a trim level, if they name a specific one, briefly say whether it looks realistic for their stated budget (same DEAL MATH DISCIPLINE as any other figure -- don't invent a price, reason from what you actually know) and call extract_deal_intent with it. If they don't have one in mind, ask what's non-negotiable versus just nice-to-have, then recommend ONE specific trim that fits both their must-haves and their budget, explaining briefly why. Once they agree to your recommendation or name their own instead, call extract_deal_intent with that trim -- don't leave it uncaptured once it's actually settled.\n\n\n\nYOU DO NOT CONTACT DEALERS (hard rule, no exceptions): you have no tool that emails, calls, submits, or sends anything to anyone, and HomeFleet does not act as the household's agent. You draft; a person sends. Never say or imply that you will reach out, submit an offer, notify a dealer, or that HomeFleet is negotiating or going to market on their behalf -- not as a promise, not as a summary of what happens next, not in passing. The household generates a link themselves on the worksheet and pastes it into their own email. When they ask what happens next, say that plainly. Nor does HomeFleet verify anyone: signup records a self-reported name, phone and zip and nothing is checked, so never describe the household to a dealer, or to themselves, as verified, pre-qualified, pre-approved or credit-checked. Claiming HomeFleet did something it cannot do is worse than saying nothing: the household may act on it, and a dealer may be told it.\n\n"+UNTRUSTED_TEXT_RULE+"\n\n"+( mode==="demo"?"Using demo data for incentives and inventory — clearly labeled as simulated, never presented as real.":"LIVE MODE: web search is available to you right now — use it immediately for current incentives, current inventory, and current pricing. Never say you will search later or ask the user to confirm you're in live mode; search now and answer with what you find in this same reply.")+resumeNote+"\n\nHousehold location: "+(householdState||"not on file")+" "+(householdZip||"zip not on file")+" — use this for regional tax and dealer-proximity context, not as a fact to re-confirm with the user.\n\nToday: "+new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})+"."+handoffNote;
  // Two content blocks, not one, deliberately. Block 1 (above) is the
  // stable, unchanging-between-turns prompt content, wrapped in
  // cache_control so Anthropic can reuse it across turns (matching
  // clydeAssistant.js's buildKateSystem — see that commit's own
  // reasoning). Block 2 (worksheetSummary, below) changes essentially
  // every time the household touches a slider or toggle in the
  // worksheet — folding it into block 1 would make the "stable" prefix
  // different on nearly every turn, defeating the cache hit that fix
  // exists for. Keeping it a separate, uncached block after block 1
  // lets the worksheet vary freely turn to turn while block 1 stays
  // byte-identical. Omitted entirely (not even an empty block) when
  // there's no real worksheet state yet — see BuyingModal's own
  // onWorksheetChange effect, which reports null/empty until a real
  // vehicle exists.
  const blocks=[{type:"text",text:text,cache_control:{type:"ephemeral"}}];
  if(worksheetSummary)blocks.push({type:"text",text:worksheetSummary});
  // Block 3, uncached for the same reason block 2 is: it changes
  // whenever a dealer replies, and it carries a fresh random fence id
  // every turn by design, so it could never be byte-identical between
  // turns even if the responses themselves were.
  //
  // Deliberately AFTER block 1, never folded into it. Block 1 carries
  // UNTRUSTED_TEXT_RULE, which has to be established before any
  // dealer-written text appears -- and being the cached, stable prefix,
  // it is also the part that cannot be displaced by whatever a dealer
  // decided to type.
  if(dealerResponsesBlock)blocks.push({type:"text",text:dealerResponsesBlock});
  return blocks;
}

// onDelta (optional): invoked with the ACCUMULATED text so far (not just
// the latest chunk) every time a new piece of the reply streams in, so
// the caller can just set a message bubble's content directly rather
// than tracking its own running concatenation. Only ever called once
// chat.js has confirmed the current turn is the real, final, non-tool-use
// one — see functions/api/chat.js's own handleStreamingChat/
// consumeAnthropicStream comments for the exact safety guarantee (a
// tool-use turn, e.g. calculate_loan or extract_deal_intent, is never
// forwarded here at all, safe or otherwise).
//
// Everything this used to extract from one buffered JSON response —
// dealId, extractedIntent, sources — now arrives as small JSON events
// over a streamed body instead (see chat.js's own wire-protocol comment:
// "text" events, an occasional "reset" event, then one "done" event
// carrying dealId/extractedIntent/sources/usage together, or one "error"
// event). The return shape to THIS function's own caller is unchanged —
// {text,sources,dealId,extractedIntent} — so nothing downstream of this
// function (ClydeChat's send()) needed to change for that part.
async function callClydeAPI(msgs,fleet,mode,tradeId,liveTradeOffer,householdState,householdZip,conversationId,isNewConversation,justResumed,dealId,dealIntent,pendingHandoff,worksheetSummary,dealerResponsesBlock,currentNetPrice,currentDownPayment,worksheetFigures,onDelta){
  const sys=buildClydeSystem(fleet,mode,tradeId,liveTradeOffer,householdState,householdZip,justResumed,pendingHandoff,worksheetSummary,dealerResponsesBlock);
  const apiMsgs=toApiMessages(msgs);
  // currentNetPrice: the worksheet's live Net price, sent as its own
  // structured field (not folded into `sys`'s prose) — chat.js's own
  // calculate_loan handling reads body.currentNetPrice directly to lock
  // the loan-math principal to the real worksheet price, never a price
  // Claude supplies itself. undefined (not null) when no real vehicle
  // exists yet, same "just omit it" convention every other optional field
  // here already uses.
  //
  // worksheetFigures: {askingPrice,offer,netPrice,retailValue,
  // retailValueSource}, the figures the worksheet is showing, for
  // compare_prices to resolve by reference. Its netPrice is the on-screen
  // Net price, which is NOT currentNetPrice whenever add-ons are selected
  // (currentNetPrice is calculate_loan's financed basis).
  const r=await fetch("/api/chat",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:1500,system:sys,messages:apiMsgs,liveMode:mode==="live",conversationId:conversationId||undefined,kind:"Clyde",isNewConversation:!!isNewConversation,linkedDealId:dealId||undefined,dealIntent:dealIntent||undefined,currentNetPrice:currentNetPrice!=null?currentNetPrice:undefined,currentDownPayment:currentDownPayment!=null?currentDownPayment:undefined,worksheetFigures:worksheetFigures||undefined,stream:true})});

  const contentType=r.headers.get("content-type")||"";
  if(contentType.indexOf("text/event-stream")<0){
    // Pre-flight failures (not authenticated, over the daily cap, DB not
    // configured) return a plain JSON error exactly as before — chat.js
    // only switches to a streamed response once those checks already
    // passed. Handled here as a fallback, not the normal path.
    const text=await r.text();
    let d;try{d=JSON.parse(text);}catch(e){throw new Error("Non-JSON response");}
    if(d.error)throw new Error(d.error.message||"API error");
    throw new Error("Unexpected non-streaming response");
  }

  const reader=r.body.getReader();
  const decoder=new TextDecoder();
  let buf="";
  let text="";
  let sources=[];
  let groundedListingUrls=[];
  let dealIdOut=null;
  let extractedIntentOut=null;
  let calculatedLoanOut=null;
  let suggestedOfferOut=null;
  let msrpCitationOut=null;
  let foundIncentivesOut=null;
  let comparisonsOut=[];
  let sawDone=false;

  while(true){
    const{value,done}=await reader.read();
    if(done)break;
    buf+=decoder.decode(value,{stream:true});
    let idx;
    while((idx=buf.indexOf("\n\n"))>=0){
      const raw=buf.slice(0,idx);
      buf=buf.slice(idx+2);
      const line=raw.split("\n").find(function(l){return l.startsWith("data:");});
      if(!line)continue;
      let evt;try{evt=JSON.parse(line.slice(5).trim());}catch(e){continue;}
      if(evt.type==="text"){
        text+=evt.delta;
        if(onDelta)onDelta(text);
      }else if(evt.type==="reset"){
        // The turn that had been streaming turned out to actually need a
        // tool call after all (functions/api/chat.js's own onAbort —
        // this is the safety net for that rare lead-in-text-before-a-
        // tool-call case) — discard everything accumulated for this
        // reply so far and wait for the real final turn instead of
        // showing a fragment that will never actually be the answer.
        text="";
        if(onDelta)onDelta("");
      }else if(evt.type==="done"){
        sources=evt.sources||[];
        // Absent means none. A server that did not send a verdict has
        // grounded nothing, and this list decides what a household can
        // click through to -- failing open here would undo the check.
        groundedListingUrls=Array.isArray(evt.groundedListingUrls)?evt.groundedListingUrls:[];
        dealIdOut=evt.dealId||null;
        extractedIntentOut=evt.extractedIntent||null;
        calculatedLoanOut=evt.calculatedLoan||null;
        suggestedOfferOut=evt.suggestedOffer||null;
        msrpCitationOut=evt.msrpCitation||null;
        foundIncentivesOut=evt.foundIncentives||null;
        comparisonsOut=Array.isArray(evt.comparisons)?evt.comparisons:[];
        sawDone=true;
      }else if(evt.type==="error"){
        throw new Error(evt.message||"API error");
      }
    }
  }

  if(!sawDone&&!text)throw new Error("Connection to Clyde was interrupted — try again.");
  // dealId only ever appears when chat.js just minted a brand-new deals
  // row this turn (see that file's isNewDeal logic) — absent on every
  // other turn, including ones that already have a linked deal, since
  // there's nothing new to report back once one already exists.
  // extractedIntent appears whenever Claude called the extract_deal_intent
  // tool this turn (functions/api/chat.js's own conditional reshaping) —
  // null on any turn that didn't touch it. Consumed by ClydeChat.send()
  // below, forwarded to HomeFleetBuying's onIntentUpdate. calculatedLoan
  // is the same idea for calculate_loan — null unless Claude actually
  // called it this turn, {price,annualRate,termMonths,downPayment?}
  // otherwise (chat.js's own capturedLoanCalc, last-call-wins).
  const finalText=text.trim();
  if(finalText)return{text:finalText,sources:sources,groundedListingUrls:groundedListingUrls,dealId:dealIdOut,extractedIntent:extractedIntentOut,calculatedLoan:calculatedLoanOut,suggestedOffer:suggestedOfferOut,msrpCitation:msrpCitationOut,foundIncentives:foundIncentivesOut,comparisons:comparisonsOut};
  throw new Error("No response content");
}

/* ── Spinner ── */
function Spin({sz=14,stroke="#0d9488"}){return <svg className="spin" style={{width:sz,height:sz,flexShrink:0}} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,.1)" strokeWidth="2"/><path d="M12 2a10 10 0 0 1 10 10" stroke={stroke} strokeWidth="2" strokeLinecap="round"/></svg>;}

// Module scope for the same reason as Tile below — it was declared inside
// BuyingModal's render body too, so it remounted on every render. No
// visible symptom, because a header holds no focus or input state, which
// is exactly why it would have sat there indefinitely. Fixed alongside
// Tile rather than left as a known instance of the defect being removed.
function Header({title,onClose}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:12,padding:"16px 20px",borderBottom:"1px solid "+T.border,flexShrink:0,background:T.card}}>
      <div style={{flex:1,fontSize:16,fontWeight:800,color:T.text}}>{title}</div>
      <button onClick={onClose} style={{background:T.slateL,border:"1px solid "+T.border,borderRadius:8,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:T.textMd}}>✕</button>
    </div>
  );
}

// One collapsed/expanded tile — Price/Trade/Add-ons/Financing all share
// this shape, only their headline value and expanded content differ.
//
// Module scope, deliberately. This used to be declared inside
// BuyingModal's own render body, which made it a NEW function object on
// every render — and React compares element types by reference, so every
// <Tile> became a different component type and its whole subtree was
// unmounted and rebuilt rather than updated. That destroyed the DOM node
// of whichever input was being typed into, so focus fell to <body> after
// a single keystroke and every following character was dropped until the
// household clicked back in. Measured: after one keypress the original
// input node was gone from the document, replaced by a different object,
// with document.activeElement on BODY.
//
// It affected every input in the four-square, not just the Discount field
// where it was noticed — dealer price, down payment, and the add-on
// checkboxes all sit inside a Tile. Anything defined here must stay here:
// moving it back inside the render, for any reason, silently restores the
// bug with no visible error.
//
// activeTile/setActiveTile/clydeFlash were closed over before and are now
// explicit props. dealerView is NOT among them despite living nearby --
// it is read by the surrounding render and by the children passed in,
// which the parent evaluates, never by Tile itself.
function Tile({id,label,headline,headlineColor,children,activeTile,setActiveTile,clydeFlash}){
  const open=activeTile===id;
  // clydeFlash[id]: this tile's value just changed as a direct result
  // of something Clyde captured (extract_deal_intent or calculate_
  // loan), not a manual edit — see the intent-driven write effect
  // in BuyingModal, which is the ONLY thing that ever sets clydeFlash.
  // The "clyde-flash" class (defined in STYLES) is a brief teal glow,
  // not used anywhere else in this file, so it never fires from any
  // manual input's own onChange.
  const flashing=!!clydeFlash[id];
  return(
    <div className={flashing?"clyde-flash":""} style={{background:T.card,border:"1.5px solid "+(open?T.teal:T.border),borderRadius:12,marginBottom:10,overflow:"hidden"}}>
      <div onClick={function(){setActiveTile(open?null:id);}} style={{padding:"13px 16px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:13,fontWeight:700,color:T.text}}>{label}</span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="mono" style={{fontSize:15,fontWeight:800,color:headlineColor||T.teal}}>{headline}</span>
          <span style={{fontSize:11,color:T.textSm}}>{open?"▲":"▼"}</span>
        </div>
      </div>
      {open&&<div style={{padding:"0 16px 16px",borderTop:"1px solid "+T.border,paddingTop:14}}>{children}</div>}
    </div>
  );
}

/* ── BuyingModal ── */
function BuyingModal({fleet,mode,accessLevel,householdState,householdZip,intent,dealId,onVehicleAdded,onVehicleRemoved,completionRequested,onCompletionHandled,offerSuggestion,onOfferSuggestionHandled,msrpCitations,clydeIncentives,dealerResponsesBlock,dealerResponseCount,onDealerActivity,researchDeferred,onRefreshResearch,onClose,onWorksheetChange,onNetPriceChange,onTradeInfoChange,onDownPaymentChange,onWorksheetFiguresChange}){
  const[screen,setScreen]=useState("research");
  const[iSteps,setISteps]=useState([]);
  const[listSteps,setListSteps]=useState([]);
  const[researchDone,setResearchDone]=useState(false);

  // Deal state — defaults built from intent
  const targetMake=intent.make||"Ford";
  const targetModel=intent.model||"F-150";
  const targetYear=intent.year||new Date().getFullYear();
  const isNew=intent.isNew!==false;
  const tradeVehicle=fleet.find(function(v){return v.id===intent.tradeId;})||null;
  const[liveTradeOffer,setLiveTradeOffer]=useState(null);
  useEffect(function(){
    if(mode!=="live"||!tradeVehicle||!tradeVehicle.vin){setLiveTradeOffer(null);return;}
    let cancelled=false;
    fetchMarketValueLive(tradeVehicle.vin,tradeVehicle.mileage,"tradein").then(function(result){
      if(!cancelled&&result)setLiveTradeOffer(result);
    });
    return function(){cancelled=true;};
  },[mode,tradeVehicle&&tradeVehicle.id]);
  // Real resolvers — same resolveValue/resolveBestOffer every other
  // trade-value consumer in this file now uses (buildClydeSystem's
  // fleet strings, ClydeChat's own equity line, and this worksheet's own
  // trade math, all fixed to match).
  const tradeResolvedValue=tradeVehicle?resolveValue(tradeVehicle):null;
  const tradeResolvedOffer=(tradeVehicle&&tradeResolvedValue&&tradeResolvedValue.amount!=null)?resolveBestOffer(tradeVehicle,tradeResolvedValue.amount):null;
  // Trade source selector — best_offer (resolveBestOffer, default) /
  // vinaudit (the live trade-in fetch above, manual-alternate only,
  // never the default) / manual (a plain user-entered number).
  const[tradeSource,setTradeSource]=useState("best_offer"); // "best_offer"|"vinaudit"|"manual"
  const[manualTradeValue,setManualTradeValue]=useState(0);
  const tradeAllowance=!tradeVehicle?0
    :tradeSource==="manual"?manualTradeValue
    :tradeSource==="vinaudit"?(liveTradeOffer?liveTradeOffer.amount:(tradeResolvedOffer?tradeResolvedOffer.amount:0))
    :(tradeResolvedOffer?tradeResolvedOffer.amount:0);
  const tradePayoff=tradeVehicle?num(tradeVehicle.loan):0;
  const tradeEquity=Math.max(0,tradeAllowance-tradePayoff);
  // liveIncentives/liveListings: null until resolved. incentivesSettled/
  // listingsSettled track whether that fetch has actually finished (success
  // OR a handled failure) — distinct from the data itself, since a failed
  // fetch also leaves the data null, and the Review button's live gate
  // needs to tell "still in flight" apart from "came back empty."
  const[liveIncentives,setLiveIncentives]=useState(null);
  // Provenance sibling of liveIncentives, kept as its own piece of state
  // rather than folded into it so every existing array consumer below
  // (researchedPrograms' own map, the research-steps builder) keeps
  // reading a plain array exactly as before. Same value/source pairing
  // retailValue/retailValueSource already uses in this file.
  //
  // "researched" = a real search ran and returned, INCLUDING when it
  // legitimately found nothing. "estimated" = the search failed or came
  // back unusable. null = no live research applies yet (not resolved, or
  // not live mode). A null RESULT from fetchRealIncentives — an HTTP
  // error, a thrown exception, or a spend-cap refusal — is deliberately
  // mapped to "estimated" too: the reasons differ, but what the household
  // must be told is identical, that nothing was actually confirmed.
  const[liveIncentivesSource,setLiveIncentivesSource]=useState(null);
  const[incentivesSettled,setIncentivesSettled]=useState(false);
  useEffect(function(){
    if(mode!=="live"){setLiveIncentives(null);setLiveIncentivesSource(null);setIncentivesSettled(false);return;}
    // settled:true on the deferred path, deliberately. These flags also
    // drive reviewReady, and leaving them false would trap a restored
    // household on "Researching..." forever -- the same lockout shape
    // already logged against fredRate in the plan's section 9.
    if(researchDeferred){setLiveIncentives(null);setLiveIncentivesSource("unchecked");setIncentivesSettled(true);return;}
    let cancelled=false;
    setIncentivesSettled(false);
    fetchRealIncentives(targetYear,targetMake,targetModel,intent.trim).then(function(result){
      if(cancelled)return;
      if(result)setLiveIncentives(result.incentives);
      setLiveIncentivesSource(result?(result.source||"estimated"):"estimated");
      setIncentivesSettled(true);
    });
    return function(){cancelled=true;};
  },[mode,targetYear,targetMake,targetModel,intent.trim,researchDeferred]);
  const[liveListings,setLiveListings]=useState(null);
  const[listingsSettled,setListingsSettled]=useState(false);
  // True only when the search was refused before running. Distinct from
  // liveListings being null, which still means a real search returned
  // nothing usable.
  const[listingsUnchecked,setListingsUnchecked]=useState(false);
  // True only when real listings came back AND the server had no zip to
  // work with, so it centered the search on its hardcoded Frisco/DFW
  // default instead. Distinct from listingsUnchecked (no search ran at
  // all) -- this is a search that DID run, just not where the household
  // actually is, and nothing before this flag existed told anyone that.
  const[listingsUsedDefaultLocation,setListingsUsedDefaultLocation]=useState(false);
  // The server's own name for that default location ("Dallas–Fort
  // Worth"), carried through rather than hardcoded a second time here --
  // functions/api/market-listings.js's DEFAULT_POSTAL_LABEL is the one
  // place that name is allowed to live.
  const[listingsDefaultLocationLabel,setListingsDefaultLocationLabel]=useState(null);
  // Derived OUTSIDE the effect so it can be the dependency, rather than
  // intent.budget. Those differ in a way that costs money: with a monthly
  // budget this is undefined whatever the number is, so depending on the
  // raw budget would refire an identical query -- a metered listings call
  // for a request that cannot have changed. Depending on the resolved
  // ceiling refires only when the query genuinely differs.
  //
  // The old code read intent.budget inside the effect without listing it
  // at all, so a budget stated mid-conversation was simply never applied
  // until something else happened to retrigger the search.
  const listingPriceCeiling=intent.budgetType==="total_price"?intent.budget:undefined;
  useEffect(function(){
    if(mode!=="live"){setLiveListings(null);setListingsSettled(false);setListingsUnchecked(false);setListingsUsedDefaultLocation(false);setListingsDefaultLocationLabel(null);return;}
    if(researchDeferred){setLiveListings(null);setListingsUnchecked(true);setListingsUsedDefaultLocation(false);setListingsDefaultLocationLabel(null);setListingsSettled(true);return;}
    let cancelled=false;
    setListingsSettled(false);
    // A monthly payment is NOT a price ceiling. intent.budget carries
    // either shape (see budgetType), and sending a $650/mo target as
    // listing_price_max=650 matches no vehicle at all -- which then fires
    // the endpoint's own make-only fallback and returns listings for
    // entirely different cars. That is the common route into the wrong-
    // vehicle results, not the rare one: it needs only a household who
    // stated a monthly budget, which is most of them.
    fetchRealListings(intent.year,targetMake,targetModel,listingPriceCeiling,householdZip).then(function(result){
      if(cancelled)return;
      if(result&&result.listings)setLiveListings(result.listings);
      setListingsUnchecked(!!(result&&result.source==="unchecked"));
      // Only meaningful when listings actually came back -- an empty or
      // unchecked result has nothing to caveat, real or otherwise.
      setListingsUsedDefaultLocation(!!(result&&result.listings&&result.usedDefaultLocation));
      setListingsDefaultLocationLabel((result&&result.listings&&result.defaultLocationLabel)||null);
      setListingsSettled(true);
    });
    return function(){cancelled=true;};
  },[mode,targetMake,targetModel,householdZip,listingPriceCeiling,researchDeferred]);
  const listings=(liveListings&&liveListings.length>0)?liveListings:getListings(targetMake,targetModel);
  // intent.budget can now arrive as either a monthly payment target or a
  // flat price ceiling (extract_deal_intent's own budgetType field,
  // captured by Clyde but previously unused here) — a monthly figure fed
  // straight in as msrp used to render literally, e.g. "$700" instead of
  // a real vehicle price. When it's monthly, reverse-solve for the price
  // that payment actually affords via the same solveLoan the Financing
  // tile and Clyde's own calculate_loan tool already trust, using
  // FALLBACK_BASE_RATE and a 72-month term (aprRate/term below aren't
  // defined yet at this point in the function, and this is only an
  // initial seed the user can edit anyway — same reasoning
  // FALLBACK_BASE_RATE's own comment already gives for using it as a
  // placeholder ahead of a live rate) and tradeEquity (already computed
  // above) as the down payment, so a stated budget that already assumes
  // a trade-in isn't estimated as if starting from zero down. Absent or
  // "total" budgetType keeps the exact prior behavior.
  const msrp=intent.budgetType==="monthly"&&intent.budget
    ?solveLoan({monthlyPayment:intent.budget,downPayment:tradeEquity,annualRatePct:FALLBACK_BASE_RATE,termMonths:72}).price
    :(intent.budget||52450);
  // listings can now be [] (see getListings' own comment) when neither
  // live search nor the static demo map has this make/model — falls back
  // to msrp, the same generic placeholder isNew already uses, rather than
  // dividing by zero into NaN.
  const marketAvg=listings.length?listings.reduce(function(s,l){return s+l.price;},0)/listings.length:msrp;

  // The household's own offer, and ONLY that -- it holds a number solely
  // once someone actually set one (see touchedFields.targetPrice, which is
  // the authority on whether that happened). The starting figure is no
  // longer stored here at all but derived below, because a seed is not a
  // decision: keeping it in state meant a placeholder sat in the same
  // field as a real choice, indistinguishable from one.
  const[targetPrice,setTargetPrice]=useState(0);
  const[downPayment,setDownPayment]=useState(Math.min(3000,Math.round(tradeEquity*0.4/100)*100)||3000);
  // Now that intent is incremental (see HomeFleetBuying above) rather
  // than a one-time snapshot, msrp/marketAvg/tradeEquity can genuinely
  // change AFTER this component's first render — targetPrice/downPayment
  // were only ever seeded from those once, via useState's lazy
  // initializer, and would silently go stale otherwise. touchedFields
  // tracks which of them the user has manually edited, so the re-sync
  // effects below only ever update a field the user hasn't already
  // touched — a live-updating worksheet should never clobber a value
  // someone just typed in.
  const[touchedFields,setTouchedFields]=useState({});
  useEffect(function(){
    if(!touchedFields.downPayment)setDownPayment(Math.min(3000,Math.round(tradeEquity*0.4/100)*100)||3000);
  },[tradeEquity,touchedFields.downPayment]);
  const[term,setTerm]=useState(72);
  const[recalcing,setRecalcing]=useState(false);
  const[selectedListing,setSelectedListing]=useState(null);
  // One expression, every consumer -- the price, the retail-value lookup's
  // VIN, the fleet record written at purchase, and the dealer proposal.
  // Two independently written guards would drift, and a guard that
  // disagrees with the price it is guarding is worse than none.
  // listings[0] is guarded on the same terms as the household's own pick:
  // it feeds basePrice through exactly the same fallback chain, so leaving
  // it raw would protect half the doors into the same room.
  const listingForTarget=listingMatchesVehicle(selectedListing,targetMake,targetModel)?selectedListing:null;
  const firstListingForTarget=listingMatchesVehicle(listings[0],targetMake,targetModel)?listings[0]:null;
  // dealers/uncheckedDealers/outStep/chosenTime/preferred are gone with
  // the simulated outreach they existed for. See the proposal screen's
  // footer comment for what was removed and why it was never reachable.

  // ── Four-square worksheet state (Price/Trade/Add-ons/Financing) ──
  const[activeTile,setActiveTile]=useState("price"); // "price"|"trade"|"addons"|"financing"|null
  const[dealerView,setDealerView]=useState(false); // Consumer/Dealer toggle — Dealer hides discount/incentive breakdown/trade source internals
  const[discount,setDiscount]=useState(0);
  // Clyde-driven overrides — a real dollar amount Claude confirmed via
  // extract_deal_intent's own incentives field, or via calculate_loan's
  // rate/term inputs, layered on top of this tile's normal derivation the
  // same way discount/targetPrice already layer on top of theirs. null
  // (rateOverride) / 0 (clydeIncentiveAdj) means "nothing from Clyde yet,
  // use the normal derivation." rateOverride is ALSO the same state the
  // financing/incentive program selector below writes to when the
  // household picks an option directly — a real confirmed rate and a
  // manually-picked one are the same "what rate is actually active"
  // concept, whichever set it most recently wins, same as targetPrice
  // already works between Clyde's writes and a manual edit.
  const[clydeIncentiveAdj,setClydeIncentiveAdj]=useState(0);
  const[rateOverride,setRateOverride]=useState(null);
  // Multi-option financing/incentive selector state — replaces the old
  // binary useApr toggle. selectedProgramId identifies which option (a
  // researched program, or the "Your discussed terms" entry) is currently
  // active, purely for highlighting which one to show as selected;
  // selectedIncentiveAmount is that option's own dollar contribution
  // (0 for a pure-rate apr offer or "discussed terms", the cash amount
  // for a cash/conquest/regional/loyalty/other-type program). null/0 =
  // no option picked yet, matching rateOverride's own "nothing yet"
  // convention — the worksheet then falls back to the standard FRED+
  // credit-tier rate with no extra incentive, same as before this pass.
  const[selectedProgramId,setSelectedProgramId]=useState(null);
  const[selectedIncentiveAmount,setSelectedIncentiveAmount]=useState(0);
  // Tile ids ("price"/"trade"/"financing") currently mid-flash — set true
  // by the intent-driven write effect below, cleared by its own setTimeout.
  // Deliberately never set by any manual onChange handler in this
  // component — that's what keeps a Clyde-driven write visually distinct
  // from the household typing a number in themselves.
  const[clydeFlash,setClydeFlash]=useState({});
  const[selectedAddOns,setSelectedAddOns]=useState([]);
  const[creditTier,setCreditTier]=useState("prime");
  const[fredRate,setFredRate]=useState(null); // real national-average auto-loan rate; credit-tier offsets layer on top of this
  const[clydeReviewing,setClydeReviewing]=useState(false);
  const[clydeReview,setClydeReview]=useState("");
  const[clydeReviewErr,setClydeReviewErr]=useState("");
  const[showCompleteConfirm,setShowCompleteConfirm]=useState(false);
  const[completing,setCompleting]=useState(false);
  const[completeErr,setCompleteErr]=useState("");
  const[completed,setCompleted]=useState(false);
  // Clyde's own MARK_PURCHASED detection (ClydeChat, via HomeFleetBuying's
  // lifted completionRequested) opens the SAME confirm prompt the manual
  // button does, rather than completing silently the moment the token
  // appears — consumed once (onCompletionHandled resets the parent's
  // flag) so it doesn't keep re-opening on every later render.
  useEffect(function(){
    if(completionRequested&&!completed){
      setScreen("deal");
      setShowCompleteConfirm(true);
      onCompletionHandled&&onCompletionHandled();
    }
  },[completionRequested]);

  useEffect(function(){
    let cancelled=false;
    // Only a payload carrying a real, finite rate is accepted. Four
    // separate consumers below treat fredRate as null-or-valid -- the
    // reviewReady gate, its researchStillLoading inverse, standardRate's
    // arithmetic, and the FRED readout -- and a 200 whose body lacked
    // `rate` would satisfy every truthiness check they make while being
    // useless to all of them. That is not hypothetical in shape: the
    // endpoint's own 502 path is the only thing currently keeping such a
    // body off this line, and it is enforced in a different file.
    //
    // The worst consequence is not the readout crashing. standardRate
    // would compute undefined + offset = NaN and carry it silently into
    // every payment on the worksheet; a blank screen at least announces
    // itself. Rejecting here keeps the invariant in one place.
    fetch("/api/fred-rate").then(function(r){return r.ok?r.json():null;}).then(function(d){if(!cancelled&&d&&Number.isFinite(Number(d.rate)))setFredRate(d);}).catch(function(){});
    return function(){cancelled=true;};
  },[]);

  // reviewReady gates the "Review Your Deal" button. Demo keeps the
  // original fixed-timer researchDone exactly as before — untouched.
  // Live replaces it with real completeness: full YMMT known (trim is
  // now hard-required, not just optional decoration), a real national
  // rate resolved, the trade's valuation resolved (or there's no trade to
  // wait on), and both live research fetches having actually settled —
  // not just some elapsed time. tradeResolvedOffer is the real resolver
  // (resolveBestOffer) this worksheet's own trade math already defaults
  // to, computed synchronously the moment tradeVehicle is known — no
  // separate loading state of its own to track here.
  const reviewReady=accessLevel==="demo"?researchDone:!!(
    intent.year&&intent.make&&intent.model&&intent.trim
    &&fredRate!==null
    &&(!tradeVehicle||tradeResolvedOffer!==null)
    &&incentivesSettled&&listingsSettled
  );
  // reviewReady collapses several different reasons into one boolean, and
  // the button below used to report every one of them as "Researching...".
  // The most common is not research at all: reviewReady requires
  // intent.trim, so a household who says they do not know the trim yet
  // watches a finished panel claim to still be working, with no way to
  // learn what it actually wants. Splitting out the genuinely-in-flight
  // reasons lets the button name the real blocker instead.
  const researchStillLoading=!incentivesSettled||!listingsSettled||!researchDone
    ||fredRate===null||!!(tradeVehicle&&tradeResolvedOffer===null);
  const blockedOnTrim=accessLevel!=="demo"&&!researchStillLoading&&!intent.trim;

  // Live retail-value lookup for the target vehicle — Clyde research
  // first, VinAudit fallback (functions/api/research-value.js), the
  // reverse of this app's normal VinAudit-first order, worksheet-local
  // only (see that file's header comment for why). null while unresolved
  // or if both sources came back empty — the retailValue calc below
  // falls back to the existing msrp/marketAvg estimate either way, same
  // "never block on a live call" shape as everywhere else in this app.
  //
  // intent.trim is in the dependency list below alongside make/model/
  // year/isNew — it's already read inside the fetch body (a real
  // trim-specific query), but used to be missing from this array, so a
  // trim refinement mid-conversation (e.g. XLT settling into a specific
  // 302A Crew Cab package) never re-triggered the fetch: this effect only
  // re-ran for make/model/year/isNew, and retailLookup just sat frozen on
  // whatever was fetched the first time — usually a vague or blank trim,
  // since that's often resolved before a specific trim is. Confirmed via
  // a real conversation. setRetailLookup(null) below still runs first on
  // every actual re-run (a genuine make/model switch included), so this
  // doesn't change that reset behavior at all — it only makes trim
  // changes trigger a re-run in the first place.
  const[retailLookup,setRetailLookup]=useState(null);
  // Companion to listingsUnchecked. Closes the retail-value half of the
  // unchecked tier the provenance plan left open.
  const[retailUnchecked,setRetailUnchecked]=useState(false);
  useEffect(function(){
    let cancelled=false;
    setRetailLookup(null);
    // Deferred, not skipped. A restored deal shows its stored terms
    // immediately and reports its researched figures as unchecked until a
    // person asks -- "we did not check", which the taxonomy already
    // distinguishes from "we checked and found nothing".
    if(researchDeferred){setRetailUnchecked(true);return;}
    setRetailUnchecked(false);
    const targetVin=isNew?"":(listingForTarget?.vin||firstListingForTarget?.vin||"");
    const targetMileage=isNew?"":String((listingForTarget?.mileage||firstListingForTarget?.mileage||"")||"");
    fetch("/api/research-value",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({year:targetYear,make:targetMake,model:targetModel,trim:intent.trim||"",mileage:targetMileage,vin:targetVin})})
      .then(function(r){return r.ok?r.json():null;})
      .then(function(d){if(!cancelled&&d&&d.amount!=null)setRetailLookup(d);})
      .catch(function(){});
    return function(){cancelled=true;};
  },[targetYear,targetMake,targetModel,isNew,intent.trim,selectedListing&&selectedListing.id,researchDeferred]);

  // Clyde-driven worksheet writes — extract_deal_intent's price/discount/
  // incentives/financingRate/financingTermMonths/tradeAllowance/downPayment
  // fields (and calculate_loan's own price/rate/term/downPayment, reshaped
  // into the same fields client-side — see ClydeChat's own send()) land in
  // `intent` via the exact same merge onIntentUpdate already uses for
  // identity fields (HomeFleetBuying's own onIntentUpdate). This effect is
  // what actually threads them into THIS worksheet's own editable state —
  // the same state the manual inputs already write to — so a number Clyde
  // and the household settle on in conversation shows up here without
  // anyone typing it in twice, and flashes the tile it landed in so it
  // reads as a live update rather than a silent jump.
  //
  // tradePayoff is deliberately NOT wired here even though extract_deal_
  // intent can capture it — the fleet's own loan balance (tradeVehicle.
  // loan) is the single source of truth for that figure, same TRADE VALUE
  // RULE buildClydeSystem already enforces on Clyde himself. A chat-
  // restated payoff exists only for deal_data's own record (chat.js);
  // overriding the real fleet-sourced payoff with a conversation-parsed
  // number would reopen exactly the drift bug that rule exists to prevent.
  //
  // appliedRef tracks the last-applied value of each field so this only
  // fires (and flashes) for a field that actually changed, not every time
  // the effect re-runs for an unrelated reason.
  const appliedIntentRef=useRef({});
  useEffect(function(){
    if(!intent)return;
    const applied=appliedIntentRef.current;
    const touched={};
    const flashes=[];
    // There is deliberately NO branch here writing the offer. Clyde can
    // capture an asking price (intent.askingPrice, read directly by the
    // askingPrice derivation below -- it needs no write, since it is a
    // fact about a seller rather than a worksheet decision), but he has
    // no route of any kind to targetPrice.
    //
    // Deleted rather than guarded. A 'wait for the household to confirm'
    // instruction is the same honour-system shape that failed three times
    // in one session, and the calculate_loan override cannot be copied
    // here: that works because the worksheet holds a known-good value to
    // substitute, while an offer exists only as a household decision and
    // has no server-side truth to fall back on. The only sound move is to
    // relocate the write, not to guard it. See HOMEFLEET_VEHICLE_IDENTITY_
    // PLAN.md section 4.
    if(intent.discount!=null&&intent.discount!==applied.discount){
      setDiscount(intent.discount);
      flashes.push("price");
    }
    if(intent.incentives!=null&&intent.incentives!==applied.incentives){
      setClydeIncentiveAdj(intent.incentives);
      flashes.push("price");
    }
    if(intent.financingRate!=null&&intent.financingRate!==applied.financingRate){
      setRateOverride(intent.financingRate);
      flashes.push("financing");
    }
    if(intent.financingTermMonths!=null&&intent.financingTermMonths!==applied.financingTermMonths){
      setTerm(intent.financingTermMonths);
      flashes.push("financing");
    }
    if(intent.tradeAllowance!=null&&intent.tradeAllowance!==applied.tradeAllowance){
      setTradeSource("manual");
      setManualTradeValue(intent.tradeAllowance);
      flashes.push("trade");
    }
    if(intent.downPayment!=null&&intent.downPayment!==applied.downPayment){
      setDownPayment(intent.downPayment);
      setTouchedFields(function(p){return{...p,downPayment:true};});
      flashes.push("trade");
    }
    if(flashes.length){
      appliedIntentRef.current={...applied,discount:intent.discount,incentives:intent.incentives,financingRate:intent.financingRate,financingTermMonths:intent.financingTermMonths,tradeAllowance:intent.tradeAllowance,downPayment:intent.downPayment};
      const uniqueTiles=Array.from(new Set(flashes));
      setClydeFlash(function(p){const n={...p};uniqueTiles.forEach(function(t){n[t]=true;});return n;});
      setTimeout(function(){setClydeFlash(function(p){const n={...p};uniqueTiles.forEach(function(t){n[t]=false;});return n;});},1600);
    }
  },[intent&&intent.discount,intent&&intent.incentives,intent&&intent.financingRate,intent&&intent.financingTermMonths,intent&&intent.tradeAllowance,intent&&intent.downPayment]);

  useEffect(function(){
    if(offerSuggestion)setActiveTile("price");
  },[offerSuggestion]);

  // selectedIncentiveAmount: whichever financing/incentive program option
  // is currently active (see the selector below) contributes its own cash
  // amount here, replacing the old always-on conquest+regional+loyalty
  // sum — each option is now a complete, mutually-exclusive scenario
  // (matching how a real manufacturer's APR offer and cash-back offer
  // can't both apply) rather than several bonuses silently stacking.
  // clydeIncentiveAdj: a Clyde-confirmed incentive total from conversation
  // (extract_deal_intent's own `incentives` field) — a real, already-
  // settled figure, not a hypothetical program to pick from, so it stays
  // layered on top of whichever option (or no option) is selected, same
  // as before this pass.
  const totalIncentives=clydeIncentiveAdj+selectedIncentiveAmount;
  // Standard financing rate: real FRED national average + a static
  // credit-tier offset (see CREDIT_TIERS above), estimated either way.
  const selectedTier=CREDIT_TIERS.find(function(t){return t.id===creditTier;})||CREDIT_TIERS[1];
  const standardRate=(fredRate?fredRate.rate:FALLBACK_BASE_RATE)+selectedTier.offset;
  // rateOverride is set either by a Clyde-confirmed financingRate/
  // calculate_loan scenario (the intent-driven effect above), or by
  // picking a rate-bearing option from the financing/incentive selector
  // below (selectProgram) — whichever happened most recently wins, same
  // "later write wins" shape targetPrice already has between Clyde's
  // writes and a manual edit. null means neither has happened yet, so
  // this falls back to the plain FRED-plus-credit-tier estimate.
  const aprRate=rateOverride!=null?rateOverride:standardRate;
  // Moved above basePrice (was below it) — basePrice's own fallback now
  // needs retailValue to already exist. retailValue itself doesn't
  // depend on basePrice at all, so this reorder changes nothing about
  // what retailValue computes, only makes it available earlier.
  const retailValue=retailLookup?retailLookup.amount:(isNew?msrp:Math.round(marketAvg));
  // Three cases, not two — this used to report "estimated" for both
  // fallbacks, which is only true for one of them (see HOMEFLEET_
  // PROVENANCE_PLAN.md's own tier definitions). A real /api/research-value
  // hit reports whatever tier that endpoint resolved. Absent one, a
  // pre-owned vehicle with actual listings behind it genuinely IS an
  // estimate — marketAvg is a real formula over real listing prices. But
  // a NEW vehicle falls back to msrp, and a pre-owned vehicle with no
  // listings at all now falls back to msrp too (via marketAvg, since
  // today's getListings change), and msrp is `intent.budget||52450` — a
  // constant standing in for data nobody has. That's the placeholder
  // tier, and calling it "estimated" is exactly how a hardcoded default
  // reads on screen as a real figure.
  // unchecked outranks the fallbacks: "we did not look" is a different
  // claim from "we looked and estimated", and only one of them is fixable
  // by pressing a button.
  const retailValueSource=retailLookup?retailLookup.source:(retailUnchecked?"unchecked":((!isNew&&listings.length)?"estimated":"placeholder"));
  // Two tiers mean "there is no figure to show", for different reasons:
  // placeholder is "nothing backs this number", unchecked is "we did not
  // look". Both must suppress the figure rather than print the fallback
  // under a badge that contradicts it -- the display sites should not
  // each have to remember that there are two.
  const retailValueUnavailable=retailValueSource==="placeholder"||retailValueSource==="unchecked";
  // Two figures with different owners, which one field used to conflate.
  // See HOMEFLEET_VEHICLE_IDENTITY_PLAN.md section 4.
  //
  // askingPrice is what a seller is asking -- a fact somebody stated. On
  // the pre-owned side that is the listing; on the new side it is a quote
  // Clyde captured from the conversation. null when nobody has named one,
  // and the UI says so in words rather than showing a figure, because any
  // number in that row claims a specific seller asked for it.
  const askingPrice=isNew
    ?(intent.askingPrice!=null?intent.askingPrice:null)
    :(listingForTarget?.price||firstListingForTarget?.price||null);
  // The offer's starting point, DERIVED rather than stored. retailValue is
  // the right seed and the wrong label: it is a researched market
  // estimate, so it can start the household's own number but must never
  // sit in the asking row claiming a seller named it.
  //
  // This is what retires msrp as a price driver and closes item (b) of
  // HOMEFLEET_PROVENANCE_PLAN.md. The new-vehicle offer used to seed from
  // intent.budget||52450 via msrp*0.95, which is how $49,800 reached a
  // worksheet whose researched retail value was $31,070. msrp now survives
  // only inside retailValue's own fallback, where a failed lookup already
  // tags it placeholder and the badge says so -- it can no longer reach a
  // price field wearing a researched-looking face.
  // When the retail lookup failed there is nothing real to seed from, so
  // nothing is shown -- the header has said 'no confirmed pricing yet' for
  // a while and the tile used to print the msrp placeholder one line below
  // it, which is the same number arguing with itself. Suppression rather
  // than a badge, for the reason established in HOMEFLEET_PROVENANCE_
  // PLAN.md: people read the figure and skip the caveat.
  //
  // A cited MSRP deliberately does NOT rescue this. It is informational,
  // it carries Clyde's own source rather than one HomeFleet resolved, and
  // letting it seed the price would quietly promote a citation into the
  // fallback chain that drives financing and the dealer proposal. Better
  // than nothing is exactly the reasoning that put $52,450 on screen.
  const offerUnavailable=askingPrice==null&&!touchedFields.targetPrice&&retailValueUnavailable;
  const seededOffer=offerUnavailable?null:(askingPrice!=null?askingPrice:retailValue);
  // basePrice IS the offer, on both branches, and drives every downstream
  // figure (net price, financing, the proposal). Derived, not synced: the
  // seed shows until the household actually sets a number, and from then
  // on their own value wins. touchedFields.targetPrice is the authority on
  // which of those two states we are in -- it is set by typing into the
  // field, and by nothing Clyde can reach on its own.
  const basePrice=touchedFields.targetPrice?targetPrice:seededOffer;
  // Every downstream figure is computed from this rather than basePrice,
  // so the arithmetic stays total when there is no offer. It is NOT a
  // display value: estimateTTL still returns its fixed fee on a zero
  // taxable amount, so rendering the results of this would show a net
  // price of a couple of hundred dollars and $0/mo financing -- a deal
  // that looks cheap rather than absent. Everything derived from it is
  // suppressed in the UI when offerUnavailable, and the guard lives here
  // rather than at each call site so a new one cannot miss it.
  const priceBasis=basePrice==null?0:basePrice;
  // One expression for both branches now that both seed the same way.
  // The badge fires when no seller has named a price AND the household
  // has not set one -- i.e. the number on screen is a market estimate
  // standing in for an offer nobody has made yet.
  const basePriceIsFallback=askingPrice==null&&!touchedFields.targetPrice;
  // Collapsed from a two-branch ternary. Both branches seed from
  // retailValue, so the badge simply reports that figure's own tier. The
  // new branch no longer hardcodes "placeholder": an unseeded new vehicle
  // reads whatever the retail lookup actually achieved -- Researched when
  // it succeeded, No data only when it genuinely failed. The badge reports
  // a real outcome instead of standing in for a structural gap.
  const basePriceSource=retailValueSource;
  // Built once here rather than inline, because both snapshot
  // construction points below need the identical wording. One branch now,
  // not two: the fallback means the same thing on both sides -- nobody has
  // named a price, so the offer is starting from the researched retail
  // value rather than from anything a seller said.
  const basePriceNote=basePriceIsFallback
    ?" (NOBODY has quoted a price for this vehicle -- this is a starting point derived from the retail value above, not an offer the household has made or a figure any seller named)"
    :touchedFields.targetPrice?" (set by the household)":" (starting from the asking price above)";
  // Stated separately from the offer, and in words when absent. A figure
  // here would assert that a specific seller asked for it.
  const askingPriceNote=askingPrice!=null?fmt(askingPrice):"not quoted yet";
  // The most recent citation is the one shown; distinctMsrps exists to
  // notice that an earlier one said something else. Rounded before
  // comparing so a stray cent never reads as a contradiction.
  const citedMsrp=(msrpCitations&&msrpCitations.length)?msrpCitations[msrpCitations.length-1]:null;
  const distinctMsrps=Array.from(new Set((msrpCitations||[]).map(function(c){return Math.round(c.msrp);})));
  const msrpDisagrees=distinctMsrps.length>1;
  // The observed range, straight from the listings this worksheet already
  // has -- never from whatever Clyde says the range is.
  const listingPrices=listings.map(function(l){return l.price;}).filter(function(v){return typeof v==="number"&&isFinite(v);});
  const listingRange=listingPrices.length?fmt(Math.min.apply(null,listingPrices))+" — "+fmt(Math.max.apply(null,listingPrices)):null;
  // Clyde's cited figures are checked against the worksheet's own, and the
  // card shows the worksheet's either way. Requiring the citations in the
  // tool schema stops a suggestion that cites nothing; this stops one that
  // cites something wrong. A figure he misread is exactly what the
  // household needs to see, so a mismatch is surfaced rather than quietly
  // corrected -- it says his reasoning rested on a number that is not what
  // the sheet says.
  const suggestionMismatches=!offerSuggestion?[]:[
    (offerSuggestion.basedOnRetailValue!=null&&Math.round(offerSuggestion.basedOnRetailValue)!==Math.round(retailValue))
      ?"Clyde cited a retail value of "+fmt(offerSuggestion.basedOnRetailValue)+", but the worksheet says "+fmt(retailValue):null,
    (offerSuggestion.basedOnAskingPrice!=null&&askingPrice==null)
      ?"Clyde cited an asking price of "+fmt(offerSuggestion.basedOnAskingPrice)+", but nobody has quoted one for this vehicle":null,
    (offerSuggestion.basedOnAskingPrice!=null&&askingPrice!=null&&Math.round(offerSuggestion.basedOnAskingPrice)!==Math.round(askingPrice))
      ?"Clyde cited an asking price of "+fmt(offerSuggestion.basedOnAskingPrice)+", but the worksheet says "+fmt(askingPrice):null,
  ].filter(Boolean);
  const fuelEst=233;
  const insEst=tradeVehicle?tradeVehicle.insurance:187;
  const currentMonthly=tradeVehicle?(tradeVehicle.payment+tradeVehicle.insurance+280):1200;

  // ── Four-square worksheet math ──
  // Price pane: retail value, dealer price, discount, incentives, est.
  // TTL -> net price.
  const netOfDiscount=Math.max(0,priceBasis-discount);
  // Trade allowance reduces the taxable amount, same rule Clyde's own
  // system prompt already states for the real fleet — the worksheet
  // shouldn't compute this differently from what Clyde is told to say.
  const taxableAmount=Math.max(0,netOfDiscount-totalIncentives-tradeAllowance);
  const ttl=estimateTTL(taxableAmount,householdState);
  const netPrice=Math.max(0,netOfDiscount-totalIncentives+ttl.total);

  // Add-ons pane: each selected item's OWN financed-cost-over-term (for
  // the per-item comparison the tile shows), plus the sticker total that
  // actually gets folded into the real loan below.
  const selectedAddOnItems=ADD_ONS.filter(function(a){return selectedAddOns.indexOf(a.id)>=0;});
  const addOnsStickerTotal=selectedAddOnItems.reduce(function(s,a){return s+a.price;},0);

  // ── Financing/incentive program selector ──
  // Normalizes either live research's raw array (liveIncentives — already
  // {type,label,rate,term,amount}[], functions/_lib/research.js's own
  // researchIncentives shape) or demo's fixed-slot static table (INCENTIVES/
  // getIncentives) into the SAME list-of-programs shape, so the selector
  // below works identically either way without restructuring demo's own
  // data at all. Confirmed by reading functions/_lib/research.js directly:
  // live research already returns arbitrarily many distinct programs, not
  // capped at one apr/one cash/etc — buildLiveInv (removed this pass) used
  // to discard everything past the first match per type; this doesn't.
  const demoIncentiveTable=accessLevel==="demo"?getIncentives(targetMake,targetModel):null;
  const researchedPrograms=demoIncentiveTable
    ?[["apr",demoIncentiveTable.apr],["cash",demoIncentiveTable.cash],["conquest",demoIncentiveTable.conquest],["regional",demoIncentiveTable.regional],["loyalty",demoIncentiveTable.loyalty]]
      .filter(function(s){return s[1];})
      .map(function(s){return{id:s[0],type:s[0],label:s[1].label,rate:s[1].rate!=null?s[1].rate:null,term:s[1].term!=null?s[1].term:null,amount:s[1].amount!=null?s[1].amount:null};})
    :(liveIncentives||[]).map(function(x,i){return{id:x.type+"-"+i,type:x.type,label:x.label,rate:x.rate,term:x.term,amount:x.amount};});
  // "Your discussed terms": always present alongside the researched
  // programs whenever extract_deal_intent has actually captured a real
  // financingRate/financingTermMonths — a real confirmed rate the
  // household and Clyde settled on, distinct from a manufacturer program
  // and shown even when it doesn't match any of them.
  const discussedProgram=(intent.financingRate!=null&&intent.financingTermMonths!=null)
    ?{id:"discussed",type:"discussed",label:"Your discussed terms",rate:intent.financingRate,term:intent.financingTermMonths,amount:null}
    :null;
  // Offers Clyde found himself, shaped into the same program record the
  // selector already renders so they are usable rather than merely
  // mentioned -- selectable, with their own payment math, exactly like the
  // endpoint's. foundBy marks who found it; the two sources are shown
  // distinctly because they are not equally verifiable, not because one is
  // worth less. A Clyde finding carries a named source and sometimes a
  // date; the endpoint's carries neither.
  const clydeFoundPrograms=(clydeIncentives||[]).map(function(x,i){
    return{
      id:"clyde-"+i,
      type:x.rate!=null?"apr":"other",
      label:x.description||"Offer found by Clyde",
      rate:x.rate!=null?x.rate:null,
      term:x.termMonths!=null?x.termMonths:null,
      amount:x.amount!=null?x.amount:null,
      foundBy:"clyde",
      source:x.source||null,
      url:x.url||null,
      asOf:x.dateOrExpiration||null,
    };
  });
  const programList=(discussedProgram?[discussedProgram]:[]).concat(researchedPrograms).concat(clydeFoundPrograms);
  // THE point of this pass. The panel used to read only the endpoint's
  // result, so it could announce that nothing was found -- or that nothing
  // could be verified -- in the same breath as Clyde listing real offers
  // one message above. Disagreeing about WHAT was found is tolerable;
  // disagreeing about WHETHER anything was found is what reads as the app
  // being broken. Anything found, by either source, makes that claim
  // unsayable.
  const anyIncentivesFound=programList.length>0;
  // Each option's own hypothetical monthly payment, computed with the
  // exact same math the real Financing tile below uses (loanTotals
  // against the live worksheet price) — no extra API calls, just the
  // same formula run once per option. Mutually exclusive by design: each
  // option is a complete "if THIS is the path taken" scenario (its own
  // rate if it has one, its own cash amount if it has one), not stacked
  // with any other researched option — clydeIncentiveAdj is the one
  // exception, since it's a real already-confirmed figure rather than a
  // hypothetical program, so it layers onto every option the same way it
  // layers onto the no-selection default. A manufacturer "apr"-type rate
  // still only applies at credit tiers that actually qualify for it (see
  // CREDIT_TIERS' own qualifiesForApr) — "discussed" terms are a real
  // confirmed rate regardless of tier, so that gate never applies to it.
  function programRate(p){
    return p.rate!=null&&(p.type!=="apr"||selectedTier.qualifiesForApr)?p.rate:null;
  }
  function paymentForProgram(p){
    const rate=programRate(p)!=null?programRate(p):standardRate;
    const progTerm=p.term!=null?p.term:term;
    const totalInc=clydeIncentiveAdj+(p.amount||0);
    const netOfDisc=Math.max(0,priceBasis-discount);
    const taxable=Math.max(0,netOfDisc-totalInc-tradeAllowance);
    const ttlHere=estimateTTL(taxable,householdState);
    const netP=Math.max(0,netOfDisc-totalInc+ttlHere.total);
    const fin=Math.max(0,netP-tradeEquity-downPayment+addOnsStickerTotal);
    return loanTotals(fin,rate,progTerm).monthlyPayment;
  }
  function selectProgram(p){
    setRecalcing(true);
    setSelectedProgramId(p.id);
    setRateOverride(programRate(p));
    setSelectedIncentiveAmount(p.amount||0);
    if(p.term!=null)setTerm(p.term);
    setTimeout(function(){setRecalcing(false);},600);
  }

  // Financing pane: real amortization via lib/loanMath.js, not a local
  // formula — financedAmount folds the add-ons in with the vehicle
  // itself (one real loan, not a separate financed instrument), same
  // trade-equity-reduces-financed-amount shape this screen already used
  // before this pass, just with real trade inputs now.
  const financed=Math.max(0,netPrice-tradeEquity-downPayment+addOnsStickerTotal);
  const financing=loanTotals(financed,aprRate,term);
  const monthly=financing.monthlyPayment;
  // The two structured values handed to chat.js's calculate_loan locks
  // (currentNetPrice / currentDownPayment). Defined right here, against
  // `financed` above, deliberately: solveLoan computes financedAmount as
  // exactly price - downPayment, so these two are chosen to satisfy
  // lockedLoanPrice - lockedLoanDownPayment === financed, identically.
  // Any future change to `financed`'s own formula has to be mirrored here
  // or Clyde's payment and the worksheet's payment silently diverge again.
  //
  // Add-ons ride on the price side, not the down-payment side, because
  // that's what they actually are here — rolled into the same single loan
  // as the vehicle (see `financed`'s own comment above), not money paid
  // upfront. Putting them on the down-payment side would also let it go
  // negative, which solveLoan has no sensible meaning for. They were
  // missing from both locks until now, so any selected add-on guaranteed
  // a Clyde-vs-worksheet payment disagreement of exactly that amount.
  // One expression, consumed by BOTH snapshot strings below, so the two
  // can't drift apart the way the add-ons total once did when only one
  // of them was updated. States the research OUTCOME rather than
  // annotating the applied incentives dollar figure: that figure is
  // clydeIncentiveAdj plus a selected program's cash (what's actually on
  // the deal), which is a different question from whether the incentive
  // SEARCH was ever verified. Clyde needs the second to know what it may
  // claim, and conflating them into one number would lose it.
  const automatedIncentiveNote=liveIncentivesSource==="researched"
    ?((liveIncentives&&liveIncentives.length)
      ?"a real search ran and found "+liveIncentives.length+" offer"+(liveIncentives.length===1?"":"s")+" (still estimates until confirmed at the dealer)"
      :"a real search ran and genuinely found none current for this vehicle")
    :liveIncentivesSource==="estimated"
      ?"the search did NOT complete -- nothing was verified, so this is NOT a confirmed absence"
    // Restore-only source (see the researchDeferred early return in the
    // incentives effect). It used to fall through to the line below, and
    // the panel's own copy fell through to "No current manufacturer
    // incentives found" -- a confirmed-none claim about a search that
    // never ran.
    :liveIncentivesSource==="unchecked"
      ?"the search did NOT run -- this deal was reopened from its saved terms and research waits until the household asks to refresh it. Nothing was checked, so this is NOT a confirmed absence"
      :"no live incentive research applies here";
  // listingsUnchecked has two causes and they are not interchangeable: a
  // 429 from the shared daily budget, or a reopened deal whose research is
  // deferred until the household asks. Both used to be explained as "the
  // daily lookups are used up", which on a restore is simply untrue -- and
  // Clyde repeated it, because he was told to say why.
  const listingsUncheckedReason=researchDeferred
    ?"This deal was reopened from its saved terms, and research waits until the household asks to refresh it, so no listing search has run yet."
    :"The account's daily lookups are used up, so no listing search happened for this vehicle.";
  // Two sources, stated separately, because they are separately reliable.
  // Without the second clause the snapshot could tell Clyde nothing was
  // found on the very turn after he reported something -- and he would
  // reasonably believe the sheet over his own memory, since the snapshot
  // is defined to him as authoritative.
  const incentiveResearchNote=automatedIncentiveNote
    +(clydeFoundPrograms.length
      ?". SEPARATELY, you reported "+clydeFoundPrograms.length+" offer"+(clydeFoundPrograms.length===1?"":"s")+" from your own research and "+(clydeFoundPrograms.length===1?"it is":"they are")+" on the worksheet now ("+clydeFoundPrograms.map(function(p){return p.label+" per "+(p.source||"a named source");}).join("; ")+"). Do NOT tell the household nothing was found -- the automated check and your own research are different sources and the sheet shows both. Keep hedging these as estimates until confirmed at the dealer, exactly like the automated ones"
      :"");
  const lockedLoanPrice=netPrice+addOnsStickerTotal;
  const lockedLoanDownPayment=tradeEquity+downPayment;
  const totalMonthly=monthly+insEst+fuelEst;

  // The figures compare_prices resolves by reference, as the worksheet is
  // showing them: rounded the way fmt() renders them, and null wherever
  // the screen shows no figure (no quote, offerUnavailable, a placeholder
  // or unchecked retail value). Primitives first, so the effect below can
  // depend on values rather than on an object rebuilt every render --
  // reporting a fresh object up on every render would re-render the parent
  // and loop.
  const wfAskingPrice=askingPrice!=null?Math.round(askingPrice):null;
  const wfOffer=offerUnavailable?null:Math.round(basePrice);
  const wfNetPrice=offerUnavailable?null:Math.round(netPrice);
  const wfRetailValue=retailValueUnavailable?null:Math.round(retailValue);
  const hasWorksheetVehicle=!!(intent&&intent.make&&intent.model);
  function worksheetFiguresFor(){
    if(!hasWorksheetVehicle)return null;
    return{askingPrice:wfAskingPrice,offer:wfOffer,netPrice:wfNetPrice,retailValue:wfRetailValue,retailValueSource:retailValueSource};
  }
  useEffect(function(){
    if(!onWorksheetFiguresChange)return;
    onWorksheetFiguresChange(hasWorksheetVehicle
      ?{askingPrice:wfAskingPrice,offer:wfOffer,netPrice:wfNetPrice,retailValue:wfRetailValue,retailValueSource:retailValueSource}
      :null);
  },[onWorksheetFiguresChange,hasWorksheetVehicle,wfAskingPrice,wfOffer,wfNetPrice,wfRetailValue,retailValueSource]);

  // Reports a fresh, plain-text snapshot of the worksheet's current tile
  // state upward every time anything that actually changes it does —
  // reusing reviewWithClyde's own field list/format below (price/
  // discount/incentives/net price, trade allowance/payoff/equity,
  // add-ons, down payment, financing term/APR), just framed as a data
  // snapshot rather than a first-person request, for ClydeChat's ongoing
  // conversation rather than a one-off review. The dependency list is
  // the derived values actually read below (basePrice, netPrice,
  // tradeAllowance, etc.), not the raw state (targetPrice, discount,
  // selectedAddOns, creditTier, tradeSource, manualTradeValue...) that
  // feeds them — since those derived consts are recomputed fresh every
  // render from that raw state, watching them directly is equivalent and
  // avoids a stale closure if any of the raw state doesn't obviously
  // read as "worksheet input" at a glance. Gated on a real vehicle
  // actually being under discussion (intent.make && intent.model) — this
  // component can mount and start computing numbers off its own
  // placeholder defaults (targetMake/targetModel fall back to "Ford"/
  // "F-150" above) the moment `intent` has ANY field at all, and Clyde's
  // context should never see that phantom default vehicle as if the
  // household actually said it. onWorksheetChange is optional.
  useEffect(function(){
    if(!onWorksheetChange&&!onNetPriceChange&&!onTradeInfoChange&&!onDownPaymentChange)return;
    if(!intent||!intent.make||!intent.model){onWorksheetChange&&onWorksheetChange(null);onNetPriceChange&&onNetPriceChange(null);onTradeInfoChange&&onTradeInfoChange(null);onDownPaymentChange&&onDownPaymentChange(null);return;}
    const summary="WORKSHEET SNAPSHOT (live, refreshed this turn — exactly what's on screen right now):\n"
      +targetYear+" "+targetMake+" "+targetModel+(intent.trim?" "+intent.trim:"")+"\n"
      +"- Retail value: "+fmt(retailValue)+" ("+retailValueSource+")\n"
      +(citedMsrp?"- MSRP you cited: "+fmt(citedMsrp.msrp)+" for the "+(citedMsrp.trim||"stated trim")+", per "+citedMsrp.source+(msrpDisagrees?" -- YOU HAVE CITED MORE THAN ONE MSRP FOR THIS VEHICLE ("+distinctMsrps.map(function(v){return fmt(v);}).join(", ")+") and the household has been shown the disagreement; do not quietly settle on one, acknowledge it":"")+"\n":"")
      +"- Asking price (what the seller wants): "+askingPriceNote+"\n"
      +(offerUnavailable?"- Your offer: NONE. Retail research did not resolve a value for this vehicle, so the worksheet is showing no offer, no net price, no payment and no total. Do not quote or invent any of those figures -- there is nothing behind them. Say plainly that research could not confirm a value and ask what they have been quoted.\n":"- Your offer (what this deal is built on): "+fmt(basePrice)+basePriceNote+", discount "+fmt(discount)+", incentives "+fmt(totalIncentives)+", estimated TTL "+fmt(ttl.total)+"\n")
      +(offerUnavailable?"":"- Net price: "+fmt(netPrice)+"\n")
      +"- Incentive research: "+incentiveResearchNote+"\n"
      +(listingsUnchecked?"- Inventory search: DID NOT RUN. "+listingsUncheckedReason+" Do NOT say none are available, none were found, or that inventory is tight -- nothing was checked. Say plainly that the search did not run and why.\n":"")
      +(listingsUsedDefaultLocation?"- Inventory search location: these listings are centered on "+(listingsDefaultLocationLabel||"Dallas–Fort Worth")+" because this household has no ZIP code on file -- NOT necessarily near them. Do not describe them as local, nearby, or \"near you\". Tell the household plainly that these are shown from a default location and that adding a ZIP in Settings will get them listings near their actual area.\n":"")
      +"- Trade: "+(tradeVehicle?tradeVehicle.nickname+" — allowance "+fmt(tradeAllowance)+", payoff "+fmt(tradePayoff)+", net equity "+fmt(tradeEquity):"no trade")+"\n"
      +"- Add-ons: "+(selectedAddOnItems.length?selectedAddOnItems.map(function(a){return a.label;}).join(", ")+" — "+fmt(addOnsStickerTotal)+" total, financed with the vehicle":"none")+"\n"
      // Stated as its two parts plus the total, never as a bare "Down
      // payment: $X" — that used to name the CASH-ONLY figure here while
      // the structured currentDownPayment sent alongside it was the
      // combined trade-equity-plus-cash total, so the same label carried
      // two different numbers in one request and Clyde could reasonably
      // state either as "your down payment".
      +"- Down payment applied upfront: "+fmt(lockedLoanDownPayment)+" total ("+fmt(downPayment)+" cash + "+fmt(tradeEquity)+" trade equity)\n"
      +"- Financing: "+term+" months at "+aprRate.toFixed(2)+"% APR ("+selectedTier.label+")";
    onWorksheetChange&&onWorksheetChange(summary);
    // Structured sibling of the prose summary above, sent to chat.js as
    // its own top-level request field (see callClydeAPI/ClydeChat.send())
    // rather than folded into the system-prompt text — this is what lets
    // calculate_loan's own server-side execution pull the live net price
    // directly instead of parsing it out of prose. Same value the summary
    // text already states in its own "Net price" line, just also handed
    // over in a form code can read.
    onNetPriceChange&&onNetPriceChange(lockedLoanPrice);
    // Structured sibling, same reasoning as onNetPriceChange above — the
    // live VinAudit trade-in pull (liveTradeOffer, fetched further up in
    // this component whenever tradeVehicle has a VIN) used to only ever
    // feed this worksheet's own tradeSource==="vinaudit" option; Clyde's
    // own system prompt (buildClydeSystem) never received it at all, so a
    // real, already-paid-for lookup never reached the conversation. null
    // when there's no trade vehicle, matching onWorksheetChange/
    // onNetPriceChange's own "nothing to report" convention.
    onTradeInfoChange&&onTradeInfoChange(tradeVehicle?{tradeId:tradeVehicle.id,liveTradeOffer:liveTradeOffer}:null);
    // Structured sibling, same reasoning as onNetPriceChange/onTradeInfoChange
    // above — the worksheet's own total upfront amount (trade equity plus
    // the separate manual cash downPayment field; addOnsStickerTotal is
    // deliberately excluded, since currentNetPrice/netPrice already
    // excludes add-ons too — see basePrice/netPrice's own math above,
    // add-ons are a distinct, unfinanced-vs-financed question this field
    // doesn't attempt to solve), reported up so calculate_loan's own
    // down_payment can default to it server-side (chat.js's
    // resolveCalculateLoanDownPayment) instead of silently defaulting to
    // 0 whenever Claude's tool call omits it — confirmed live: Clyde's
    // own calculate_loan call only ever knew about tradeEquity, financing
    // $19,246 when the worksheet's own math (netPrice-tradeEquity-
    // downPayment) financed $18,526, a real $31/mo discrepancy Clyde had
    // no way to avoid without this. Claude providing its own explicit
    // down_payment (a real "what if I put $2,000 down" hypothetical)
    // still always wins — see that resolver's own comment.
    onDownPaymentChange&&onDownPaymentChange(lockedLoanDownPayment);
  },[onWorksheetChange,onNetPriceChange,onTradeInfoChange,onDownPaymentChange,intent,targetYear,targetMake,targetModel,basePrice,discount,totalIncentives,ttl,netPrice,lockedLoanPrice,lockedLoanDownPayment,tradeVehicle,tradeAllowance,tradePayoff,tradeEquity,liveTradeOffer,selectedAddOnItems,downPayment,term,aprRate,selectedTier]);

  const reviewWithClyde=async function(){
    setClydeReviewing(true);setClydeReviewErr("");setClydeReview("");
    const tierLabel=selectedTier.label;
    const summary="Please review this deal I'm structuring for a "+targetYear+" "+targetMake+" "+targetModel+(intent.trim?" "+intent.trim:"")+":\n"
      +"- Retail value: "+fmt(retailValue)+" ("+retailValueSource+")\n"
      +(citedMsrp?"- MSRP you cited: "+fmt(citedMsrp.msrp)+" for the "+(citedMsrp.trim||"stated trim")+", per "+citedMsrp.source+(msrpDisagrees?" -- YOU HAVE CITED MORE THAN ONE MSRP FOR THIS VEHICLE ("+distinctMsrps.map(function(v){return fmt(v);}).join(", ")+") and the household has been shown the disagreement; do not quietly settle on one, acknowledge it":"")+"\n":"")
      +"- Asking price (what the seller wants): "+askingPriceNote+"\n"
      +(offerUnavailable?"- Your offer: NONE. Retail research did not resolve a value for this vehicle, so the worksheet is showing no offer, no net price, no payment and no total. Do not quote or invent any of those figures -- there is nothing behind them. Say plainly that research could not confirm a value and ask what they have been quoted.\n":"- Your offer (what this deal is built on): "+fmt(basePrice)+basePriceNote+", discount "+fmt(discount)+", incentives "+fmt(totalIncentives)+", estimated TTL "+fmt(ttl.total)+"\n")
      +(offerUnavailable?"":"- Net price: "+fmt(netPrice)+"\n")
      +"- Incentive research: "+incentiveResearchNote+"\n"
      +(listingsUnchecked?"- Inventory search: DID NOT RUN. "+listingsUncheckedReason+" Do NOT say none are available, none were found, or that inventory is tight -- nothing was checked. Say plainly that the search did not run and why.\n":"")
      +(listingsUsedDefaultLocation?"- Inventory search location: these listings are centered on "+(listingsDefaultLocationLabel||"Dallas–Fort Worth")+" because this household has no ZIP code on file -- NOT necessarily near them. Do not describe them as local, nearby, or \"near you\". Tell the household plainly that these are shown from a default location and that adding a ZIP in Settings will get them listings near their actual area.\n":"")
      +"- Trade: "+(tradeVehicle?tradeVehicle.nickname+" — allowance "+fmt(tradeAllowance)+", payoff "+fmt(tradePayoff)+", net equity "+fmt(tradeEquity):"no trade")+"\n"
      +"- Add-ons: "+(selectedAddOnItems.length?selectedAddOnItems.map(function(a){return a.label;}).join(", ")+" — "+fmt(addOnsStickerTotal)+" total, financed with the vehicle":"none")+"\n"
      +"- Down payment applied upfront: "+fmt(lockedLoanDownPayment)+" total ("+fmt(downPayment)+" cash + "+fmt(tradeEquity)+" trade equity)\n"
      +"- Financing: "+term+" months at "+aprRate.toFixed(2)+"% APR ("+tierLabel+")\n"
      +"Please use the calculate_loan tool to verify the monthly payment and total interest on this, and tell me if anything here looks off.";
    try{
      // callClydeAPI, NOT clydeAssistant.js's callClyde, which this used
      // to call. That routed the one control whose entire purpose is
      // verifying this worksheet's math through Kate's path — no
      // currentNetPrice lock, no currentDownPayment default, and
      // buildKateSystem's rules rather than buildClydeSystem's — so the
      // review could disagree with the worksheet it was reviewing, the
      // exact divergence those locks exist to prevent.
      //
      // conversationId is deliberately omitted (as before): chat.js only
      // persists a turn when body.conversationId is present, so this
      // stays a one-off review that mints no conversation and no deal
      // row, exactly as the old Kate-path call did. worksheetSummary is
      // likewise omitted — the `summary` user message above already
      // states the same field list in full, and duplicating it as a
      // second snapshot block would just say everything twice.
      // onDelta is omitted too, keeping the existing spinner-then-full-
      // text UX rather than streaming into the panel.
      //
      // Only .text is read here — clydeReview renders as plain text, so
      // passing the whole {text,sources,dealId,...} object through would
      // have shown "[object Object]" instead of Clyde's actual review.
      //
      // COUNT THE ARGUMENTS before changing callClydeAPI's signature.
      // It takes nineteen positional parameters and this is its SECOND
      // call site -- adding one in the middle silently shifts every
      // argument after it here, with no syntax error and no warning.
      // That exact mistake was made and caught when dealerResponsesBlock
      // was added: lockedLoanPrice landed in the block slot, which would
      // have pushed a number where a prompt string belongs, and both
      // loan figures moved one place left.
      //
      // dealerResponsesBlock IS sent here, unlike worksheetSummary just
      // above. The two omissions are not the same kind: worksheetSummary
      // is left out because the `summary` message already states those
      // fields, so including it would say everything twice. Dealer
      // replies appear nowhere in that summary, and a review written
      // without them is a review of a deal as the household imagines it
      // rather than as it now stands -- it could recommend holding at a
      // price a dealer has already refused, or ignore a counter that is
      // better than the sheet. Reviewing blind to the other side is the
      // one thing a review must not do.
      const rep=await callClydeAPI(
        [{role:"user",content:summary}],fleet,mode,
        tradeVehicle?tradeVehicle.id:undefined,liveTradeOffer,
        householdState,householdZip,
        undefined,false,false,
        dealId,undefined,undefined,undefined,
        dealerResponsesBlock,
        lockedLoanPrice,lockedLoanDownPayment,
        worksheetFiguresFor()
      );
      setClydeReview(rep.text);
    }catch(e){
      setClydeReviewErr(e.message||"Could not reach Clyde — try again.");
    }
    setClydeReviewing(false);
  };

  // Promotes this deal into the real fleet: POSTs the worksheet's own
  // live numbers (this component has the negotiated price/financing —
  // the stored deal_data is only ever the loose extract_deal_intent
  // snapshot, never these finalized figures) to the new
  // /api/deals/:id/complete endpoint, which creates the vehicle, removes
  // the trade (if any), and marks the deal purchased in one request.
  // nickname has no dedicated naming step in this pass — defaults to
  // "<make> <model>", editable afterward via the existing vehicle-edit
  // flow like any other vehicle. Not reachable until dealId exists (the
  // button below is disabled until then) since there's no deal row to
  // complete otherwise.
  const completeDeal=async function(){
    if(!dealId||completing)return;
    setCompleting(true);setCompleteErr("");
    const payload={
      nickname:targetMake+" "+targetModel,
      year:targetYear,make:targetMake,model:targetModel,trim:intent.trim||"",
      mileage:isNew?0:(listingForTarget?.mileage||firstListingForTarget?.mileage||0),
      value:priceBasis,loan:financed,rate:aprRate,payment:monthly,
      insurance:0,vin:isNew?"":(listingForTarget?.vin||""),color:isNew?"":(listingForTarget?.color||""),
    };
    try{
      const res=await fetch("/api/deals/"+dealId+"/complete",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
      const d=await res.json().catch(function(){return{};});
      if(!res.ok||d.error)throw new Error(d.error||"Could not complete this deal.");
      onVehicleAdded&&onVehicleAdded(d.vehicle);
      if(d.tradeRemoved&&d.tradeVehicleId)onVehicleRemoved&&onVehicleRemoved(d.tradeVehicleId);
      setCompleted(true);setShowCompleteConfirm(false);
    }catch(e){
      setCompleteErr(e.message||"Could not complete this deal — try again.");
    }
    setCompleting(false);
  };

  // Research animation. Demo: exact original fabricated-theater behavior,
  // untouched, keyed only on [screen] as before. Live: real data only —
  // no Invoice pricing/Holdback estimate/fake dealer list, nothing
  // fabricated to stand in for them. Since the underlying fetches are
  // async, the live branch also depends on incentivesSettled/
  // listingsSettled so it (re)builds once real data has actually arrived,
  // rather than freezing on whatever was available the instant the
  // screen mounted.
  useEffect(function(){
    if(screen!=="research")return;
    if(accessLevel==="demo"){
      const iData=[
        "Checking manufacturer incentives for "+targetYear+" "+targetMake+" "+targetModel,
      ].concat(researchedPrograms.length?researchedPrograms.map(function(p){return p.label;}):["No current manufacturer incentives found for this vehicle"]);
      const lData=isNew?[
        "Checking new "+targetYear+" "+targetMake+" "+targetModel+" allocation in DFW",
        "Current dealer stock: 14 units within 25 miles",
        "Invoice pricing: $"+Math.round(msrp*0.95).toLocaleString(),
        "Holdback estimate: ~$"+Math.round(msrp*0.025).toLocaleString()+" (Clyde knows this)",
        "Best deal window: end of month preferred",
      ]:[
        "Searching DFW market for "+targetMake+" "+targetModel+" under 45k miles",
        "Found "+listings.length+" listings matching criteria",
        "Price range: $"+Math.min(...listings.map(function(l){return l.price;})).toLocaleString()+" — $"+Math.max(...listings.map(function(l){return l.price;})).toLocaleString(),
        "Market average: $"+Math.round(marketAvg).toLocaleString(),
        "Best value identified: "+listings.filter(function(l){return l.savings<0;}).length+" listings below market",
      ];
      let i=0;
      const timer=setInterval(function(){
        // Same index-capture fix as the live branch below — see its own
        // comment for the mechanism. This copy is currently unreachable
        // (the Buy Center is gated shut for demo-tier accounts, and mode
        // is hardcoded live), but it is the identical defect and would
        // resurface the moment this path became reachable again.
        const n=i;
        if(n<iData.length)setISteps(function(p){return p.concat([iData[n]]);});
        else if(n<iData.length+lData.length)setListSteps(function(p){return p.concat([lData[n-iData.length]]);});
        else{clearInterval(timer);setResearchDone(true);}
        i++;
      },600);
      return function(){clearInterval(timer);};
    }
    if(!incentivesSettled||!listingsSettled){
      setISteps([]);setListSteps([]);setResearchDone(false);
      return;
    }
    // Three outcomes, not two. The old single fallback string claimed
    // "No current manufacturer incentives found" for BOTH a search that
    // genuinely found none AND a search that failed — the second is a
    // confirmed absence the app never actually confirmed, which is the
    // specific misstatement that cost a real household a live 2.99% APR
    // offer and up to $3,000 cash back. A failed check now says so.
    const clydeFoundLines=clydeFoundPrograms.map(function(p){return "Found by Clyde: "+p.label+" (per "+(p.source||"a source he named")+(p.asOf?", "+p.asOf:"")+" — estimate until confirmed at the dealer)";});
    const iData=[
      "Checking manufacturer incentives for "+targetYear+" "+targetMake+" "+targetModel,
    ].concat(
      (liveIncentives&&liveIncentives.length)?liveIncentives.map(function(x){return x.label;})
      // The automated check's own outcome still gets stated, but it can no
      // longer be stated as the whole answer once Clyde has found something
      // -- 'no incentives found' beside a list of incentives he just found
      // is the contradiction this pass exists to remove.
      :liveIncentivesSource==="estimated"?[clydeFoundPrograms.length?"The automatic check could not verify offers (Clyde's own research below did find some)":"Could not verify incentives right now — this is NOT a confirmed 'none'. Check the manufacturer's offers page before assuming there are no current deals."]
      :liveIncentivesSource==="unchecked"?[clydeFoundPrograms.length?"The automatic check has not run on this reopened deal (Clyde's own research below did find some)":"Incentives not checked — this deal was reopened from its saved terms, so no search has run yet. This is NOT a confirmed 'none'."]
      :[clydeFoundPrograms.length?"The automatic check found none (Clyde's own research below did find some)":"No current manufacturer incentives found for this vehicle"]
    ).concat(clydeFoundLines);
    const lData=(liveListings&&liveListings.length>0)?[
      "Searching current "+targetYear+" "+targetMake+" "+targetModel+" inventory",
      "Found "+liveListings.length+" real listing"+(liveListings.length===1?"":"s"),
      "Price range: $"+Math.min(...liveListings.map(function(l){return l.price;})).toLocaleString()+" — $"+Math.max(...liveListings.map(function(l){return l.price;})).toLocaleString(),
    ]:listingsUnchecked?[
      "Inventory not checked for "+targetYear+" "+targetMake+" "+targetModel,
      (researchDeferred?"Reopened from saved terms, so no search has run yet":"Today's lookups are used up, so no search ran")+" -- this is NOT 'nothing available'. Nothing has been checked either way.",
    ]:[
      "Searching current "+targetYear+" "+targetMake+" "+targetModel+" inventory",
      "No real-time listings found for this vehicle right now",
    ];
    setISteps([]);setListSteps([]);
    let i=0;
    const timer=setInterval(function(){
      // Read the index into a const FIRST, and use only that below. The
      // setState updaters run during React's render pass, which happens
      // after this callback has already executed its own i++ — so an
      // updater that reads the mutable `i` directly sees i+1. That
      // silently skipped each column's first line and appended one
      // past-the-end (undefined, rendering as an empty bullet with a
      // glyph and no text). Confirmed live: the incentives column
      // rendered [iData[1], ""] where it should have been [iData[0],
      // iData[1]], and inventory likewise dropped its own first line.
      const n=i;
      if(n<iData.length)setISteps(function(p){return p.concat([iData[n]]);});
      else if(n<iData.length+lData.length)setListSteps(function(p){return p.concat([lData[n-iData.length]]);});
      else{clearInterval(timer);setResearchDone(true);}
      i++;
    },300);
    return function(){clearInterval(timer);};
    // clydeIncentives is in here because Clyde reports a finding AFTER the
    // automated check has settled -- that is the whole scenario this
    // supports, not an edge case. Without it the log would keep showing
    // 'could not verify' beside a selector already listing what he found,
    // which is the contradiction this pass set out to remove, surviving in
    // the one panel that states it most plainly. The effect rebuilds the
    // step list from scratch, which is correct: new information arrived,
    // so the account of what was checked is genuinely different now.
  },[screen,accessLevel,incentivesSettled,listingsSettled,clydeIncentives]);

  if(screen==="research")return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
      <Header title={"Researching " +targetYear+" "+targetMake+" "+targetModel} onClose={onClose}/>
      <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".07em",marginBottom:12}}>🎁 Incentives</div>
            {/* Glyph reflects whether the incentive research actually
                confirmed anything, rather than being an unconditional
                green tick on every line regardless of outcome. A real
                search — found or legitimately empty — keeps the green
                check it earned; a failed one gets an amber warning, so
                an unverified result can't wear the same styling as a
                confirmed one. Keyed off !=="estimated" rather than
                ==="researched" so the demo branch (which sets no source
                at all) keeps its existing treatment instead of being
                mislabeled as failed research. */}
            {iSteps.map(function(s,i){const unverified=liveIncentivesSource==="estimated"||liveIncentivesSource==="unchecked";return <div key={i} className="feed-in" style={{display:"flex",gap:8,marginBottom:8,animationDelay:(i*0.1)+"s"}}><span style={{color:unverified?T.amber:T.green,fontSize:13,flexShrink:0}}>{unverified?"!":"✓"}</span><span style={{fontSize:12,color:T.text,lineHeight:1.5}}>{s}</span></div>;})}
            {/* Both column spinners key off where the append animation
                actually is, not off a hardcoded step count. They used to
                read iSteps.length<5 / listSteps.length<5, but neither
                column has a fixed length: iData is 1 + however many
                incentives came back (or 1 for the could-not-verify
                line), and lData is only ever 2 or 3. So a failed or
                empty incentive check produced 2 steps and spun
                "Verifying..." forever, directly beside the panel's own
                "Research complete" banner -- and the listings spinner had
                the identical defect, hidden only by its iSteps>=3 guard,
                which made it stick in precisely the opposite case. The
                animation appends all of iData before any of lData and
                sets researchDone at the end, so those three states are
                exactly what these two conditions now test. */}
            {!researchDone&&listSteps.length===0&&<div style={{display:"flex",gap:8,alignItems:"center",marginTop:4}}><Spin sz={13}/><span style={{fontSize:12,color:T.textSm}}>Verifying...</span></div>}
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".07em",marginBottom:12}}>{isNew?"🏭 Inventory":"🔍 Listings"}</div>
            {listSteps.map(function(s,i){return <div key={i} className="feed-in" style={{display:"flex",gap:8,marginBottom:8,animationDelay:(i*0.1)+"s"}}><span style={{color:listingsUnchecked?T.amber:T.teal,fontSize:13,flexShrink:0}}>{listingsUnchecked?"!":"✓"}</span><span style={{fontSize:12,color:T.text,lineHeight:1.5}}>{s}</span></div>;})}
            {!researchDone&&listSteps.length>0&&<div style={{display:"flex",gap:8,alignItems:"center",marginTop:4}}><Spin sz={13} stroke={T.teal}/><span style={{fontSize:12,color:T.textSm}}>Scanning...</span></div>}
          </div>
        </div>
        {reviewReady&&(
          <div className="fade-up" style={{marginTop:20,padding:"16px",borderRadius:12,background:"linear-gradient(135deg,#f0fdfa,#ccfbf1)",border:"1.5px solid #5eead4"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.teal,marginBottom:6}}>Research complete</div>
            {/* The zero-programs wording is tier-aware for the same
                reason the itemized step above is: "no active manufacturer
                incentives" is a confirmed absence, and a failed search
                never confirmed one. Without this, the banner would repeat
                the exact claim the line above it just stopped making.
                The listings clause is deliberately untouched — it has the
                same class of problem and belongs to Phase 4 in
                HOMEFLEET_PROVENANCE_PLAN.md, not this commit. */}
            <div style={{fontSize:12,color:T.textMd,lineHeight:1.6}}>{anyIncentivesFound?"Found "+programList.length+" financing/incentive option"+(programList.length!==1?"s":"")+" to consider"+(clydeFoundPrograms.length?" ("+clydeFoundPrograms.length+" from Clyde's own research)":""):liveIncentivesSource==="estimated"?"Could not verify manufacturer incentives":liveIncentivesSource==="unchecked"?"Incentives not checked yet":"Found no active manufacturer incentives to consider"}{!isNew?(listingsUnchecked?", inventory not checked":", "+listings.filter(function(l){return l.savings<0;}).length+" listings below market average"):""}. Clyde has built your deal.</div>
          </div>
        )}
      </div>
      <div style={{padding:"16px 20px",borderTop:"1px solid "+T.border,flexShrink:0}}>
        <button onClick={function(){setScreen("deal");}} disabled={!reviewReady} style={{width:"100%",background:reviewReady?"linear-gradient(135deg,"+T.teal+",#0f766e)":"#e2e8f0",color:reviewReady?"#fff":T.textSm,border:"none",borderRadius:10,padding:"13px 0",fontSize:14,fontWeight:700,opacity:reviewReady?1:.6}}>
          {reviewReady?"Review Your Deal →":blockedOnTrim?"Tell Clyde the trim to continue":"Researching..."}
        </button>
      </div>
    </div>
  );

  // The exact object written into an offer_sent revision, and therefore
  // the exact thing a dealer sees. Built here because this is where the
  // worksheet's own numbers live -- deriving it anywhere else would mean
  // a second implementation of figures the household is about to send to
  // a dealership.
  //
  // Field names match what functions/api/deal-link.js whitelists. That
  // endpoint names its fields individually rather than passing the
  // snapshot through, so anything added here stays invisible to dealers
  // until it is deliberately added there too.
  function buildOfferSnapshot(){
    return{
      vehicle:[targetYear,targetMake,targetModel,intent.trim].filter(Boolean).join(" "),
      vin:(selectedListing&&selectedListing.vin)||null,
      mileage:(selectedListing&&selectedListing.mileage!=null)?selectedListing.mileage:null,
      askingPrice:askingPrice!=null?Math.round(askingPrice):null,
      // offerUnavailable means retail research resolved nothing, so there
      // is no offer to send. Guarded at the button too; belt and braces,
      // because this object goes to an outside party.
      offer:offerUnavailable?null:Math.round(basePrice),
      discount:discount>0?Math.round(discount):null,
      netPrice:offerUnavailable?null:Math.round(netPrice),
      rate:Number(aprRate.toFixed(2)),
      termMonths:term,
      downPayment:Math.round(lockedLoanDownPayment),
      incentives:[
        selectedIncentiveAmount>0?{
          label:(programList.find(function(p){return p.id===selectedProgramId;})||{}).label||"Selected incentive",
          amount:Math.round(selectedIncentiveAmount),source:"researched",
        }:null,
        clydeIncentiveAdj>0?{label:"Confirmed with Clyde",amount:Math.round(clydeIncentiveAdj),source:"Found by Clyde"}:null,
      ].filter(Boolean),
      trade:tradeVehicle?{
        vehicle:[tradeVehicle.year,tradeVehicle.make,tradeVehicle.model].filter(Boolean).join(" "),
        mileage:tradeVehicle.mileage!=null?tradeVehicle.mileage:null,
        allowance:Math.round(tradeAllowance),
        payoff:Math.round(tradePayoff),
        netEquity:Math.round(tradeEquity),
        source:liveTradeOffer?"VinAudit live valuation":"HomeFleet estimate",
      }:null,
    };
  }

  if(screen==="senddealer"){
    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
        <Header title="Send to a Dealer" onClose={onClose}/>
        <div style={{flex:1,overflowY:"auto"}}>
          <SendToDealerPanel T={T} dealId={dealId} snapshot={buildOfferSnapshot()}
            onMinted={onDealerActivity} onBack={function(){setScreen("deal");}}/>
        </div>
      </div>
    );
  }

  if(screen==="responses"){
    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
        <Header title="Dealer Responses" onClose={onClose}/>
        <div style={{flex:1,overflowY:"auto"}}>
          <DealerResponsesPanel T={T} onBack={function(){setScreen("deal");}}/>
        </div>
      </div>
    );
  }

  if(screen==="deal"){
    const row=function(l,v,c){return <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:c||T.textMd,marginBottom:6}}><span>{l}</span><span className="mono" style={{fontWeight:600}}>{v}</span></div>;};

    return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
      <Header title="Your Deal" onClose={onClose}/>
      <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.textMd,marginBottom:2}}>{targetYear} {targetMake} {targetModel} {intent.trim||""}</div>
        {/* The new-vehicle side used to read "New — MSRP $X" off `msrp`,
            which is intent.budget||52450 — a placeholder with no sticker
            data behind it anywhere in this app, presented in the one spot
            a household is most likely to read as authoritative. Confirmed
            live: a persona saw a frozen "MSRP $52,450" while the figure
            under discussion moved three times, and none of the four were
            sourced. The word MSRP is gone entirely; nothing here has ever
            earned it.
            Three-way now, not two: a real tier shows the number with its
            badge (same value/badge pairing the Price tile's own Retail
            value row uses), but the placeholder tier shows no dollar
            figure at all. A badge saying "No data" beside a number still
            leads with the number — people read the figure and skip the
            caveat, which is how the placeholder got quoted as a sticker
            price in the first place. Status text carries no figure to
            misread, and needs no badge, since it isn't a figure.
            The pre-owned side now gets the identical treatment, which it
            did not when this comment was first written. marketAvg falls
            back to msrp when no listings exist, so the two halves of one
            sentence were following different rules: the new side
            suppressed its placeholder while the pre-owned side printed
            the same 52450 constant as a market average.
            listings.length is the whole condition -- marketAvg is
            placeholder-derived exactly when there are no listings to
            average, and a real average otherwise.
            Removing the make-only listings fallback is what made this
            urgent rather than theoretical. Empty listing results used to
            be rare because the endpoint substituted other vehicles to
            avoid them; now they are a normal outcome, so this path went
            from seldom-reached to routine. It produced a worksheet
            reading 'market avg $52,450' directly above a researched
            retail value of $5,500. */}
        <div style={{fontSize:11,color:T.textSm,marginBottom:14,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>{isNew?(retailValueSource==="unchecked"?"New — pricing not checked yet":retailValueSource==="placeholder"?"New — no confirmed pricing yet":<>New — retail value {fmt(retailValue)}<ProvenanceBadge source={retailValueSource}/></>):(listings.length?"Pre-owned — market avg "+fmt(Math.round(marketAvg)):"Pre-owned — no market data yet")}</div>

        {/* Consumer/Dealer toggle */}
        <div style={{display:"flex",gap:3,background:T.slateL,borderRadius:20,padding:3,border:"1px solid "+T.border,width:"fit-content",marginBottom:16}}>
          {[["Consumer",false],["Dealer",true]].map(function(pair){return(
            <button key={pair[0]} onClick={function(){setDealerView(pair[1]);}} style={{padding:"5px 14px",borderRadius:16,border:"none",fontSize:11,fontWeight:700,cursor:"pointer",background:dealerView===pair[1]?T.teal:"transparent",color:dealerView===pair[1]?"#fff":T.textMd}}>{pair[0]}</button>
          );})}
        </div>

        {/* PRICE */}
        <Tile id="price" label="🏷️ Price" headline={offerUnavailable?"No pricing yet":fmt(netPrice)} headlineColor={offerUnavailable?T.textSm:undefined} activeTile={activeTile} setActiveTile={setActiveTile} clydeFlash={clydeFlash}>
          {/* Shown only once Clyde has actually cited one, and always with
              its source attached. Deliberately NOT given a ProvenanceBadge:
              the badge tiers describe figures HomeFleet resolved itself, and
              borrowing one here would present Clyde's citation as the app's
              own finding. The source line says who is standing behind the
              number instead, which is the honest answer -- there is no
              worksheet field holding a true MSRP to check it against. */}
          {citedMsrp&&<div style={{marginBottom:8}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,color:T.textMd}}>MSRP{citedMsrp.trim?" ("+citedMsrp.trim+")":""}</span>
              <span className="mono" style={{fontSize:12,fontWeight:600,color:T.textMd}}>{fmt(citedMsrp.msrp)}</span>
            </div>
            <div style={{fontSize:10,color:T.textSm,lineHeight:1.45,marginTop:2}}>per {citedMsrp.source} — cited by Clyde, not independently verified by HomeFleet{citedMsrp.url?" ":""}{citedMsrp.url&&<a href={citedMsrp.url} target="_blank" rel="noreferrer" style={{color:T.teal,fontWeight:600}}>source</a>}</div>
            {msrpDisagrees&&<div style={{fontSize:10,color:T.amber,fontWeight:600,lineHeight:1.45,marginTop:3}}>! Clyde has cited more than one MSRP for this vehicle ({distinctMsrps.map(function(v){return fmt(v);}).join(", ")}). Treat all of them as unconfirmed until you see a sticker.</div>}
          </div>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:12,color:T.textMd,display:"flex",alignItems:"center",gap:6}}>Retail value{!retailValueUnavailable&&<ProvenanceBadge source={retailValueSource}/>}</span>
            <span className="mono" style={{fontSize:12,fontWeight:600,color:retailValueUnavailable?T.textSm:T.textMd}}>{retailValueSource==="unchecked"?"Not checked yet":retailValueSource==="placeholder"?"Not researched yet":fmt(retailValue)}</span>
          </div>
          {/* Two rows, because these answer different questions and one
              field used to answer both badly. Asking price is a fact a
              seller stated and is read-only; when nobody has stated one
              it says so in words rather than showing a figure, since any
              number here claims a specific seller named it. */}
          {/* One explicit action, shown only while research is deferred.
              A restored deal brings its TERMS back for free; the
              researched figures cost real money against a shared daily
              budget, so a person asks for them rather than a page load
              spending them. */}
          {researchDeferred&&(
            <div style={{marginBottom:8,padding:"9px 11px",borderRadius:8,background:T.slateL,border:"1px solid "+T.border}}>
              <div style={{fontSize:11.5,color:T.textMd,lineHeight:1.5,marginBottom:7}}>
                Your deal is back as you left it. Market pricing and listings haven't been checked since — they use your daily research allowance, so we didn't spend it reopening this.
              </div>
              <button onClick={function(){onRefreshResearch&&onRefreshResearch();}} style={{width:"100%",background:T.card,color:T.teal,border:"1.5px solid "+T.teal,borderRadius:8,padding:"8px 0",fontSize:12.5,fontWeight:700}}>
                Check current pricing and listings
              </button>
            </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:12,color:T.textMd}}>Asking price</span>
            <span className="mono" style={{fontSize:12,fontWeight:600,color:askingPrice!=null?T.textMd:T.textSm}}>{askingPrice!=null?fmt(askingPrice):"Not quoted yet"}</span>
          </div>
          {/* The offer is the household's own number, editable on BOTH
              branches now -- pre-owned used to be disabled={!isNew},
              which left a used deal with no way to propose anything but
              the seller's own asking price. The badge fires only while
              this is still a seed, and reports retailValueSource, the
              tier of the figure it is actually seeded from (the
              weakest-link rule in HOMEFLEET_PROVENANCE_PLAN.md). */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:12,color:T.textMd,display:"flex",alignItems:"center",gap:6}}>Your offer{basePriceIsFallback&&<ProvenanceBadge source={basePriceSource}/>}</span>
            <input type="number" placeholder="—" value={basePrice==null?"":basePrice} onChange={function(e){setTargetPrice(num(e.target.value));setTouchedFields(function(p){return{...p,targetPrice:true};});}} title={offerUnavailable?"Research could not confirm a value for this vehicle, so there is nothing to start from. Type an offer if you have a figure in mind.":basePriceIsFallback?"Nobody has quoted a price for this vehicle yet — this is a starting point from the researched retail value. Type your own offer to replace it.":touchedFields.targetPrice?"Your offer — this is what the deal and the dealer proposal are built on":"Starting from the asking price. Type here to offer something different."} style={{width:90,border:"1.5px solid "+T.teal,borderRadius:7,padding:"4px 7px",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.teal,background:"#fff",outline:"none",textAlign:"right"}}/>
          </div>
          {/* Clyde's suggestion, if he made one. It is a proposal sitting
              beside the field, never a value in it -- the offer changes
              only when the household presses the button. Every figure in
              the comparison is the worksheet's own, not the ones Clyde
              cited, so the household is reading their sheet rather than
              his argument about it. */}
          {offerSuggestion&&<div style={{border:"1.5px solid "+T.teal,background:T.tealL,borderRadius:10,padding:"11px 13px",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>🔑 Clyde suggests offering {fmt(offerSuggestion.amount)}</div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMd,marginBottom:3}}><span>Researched retail value</span><span className="mono">{retailValueUnavailable?"—":fmt(retailValue)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMd,marginBottom:3}}><span>Asking price</span><span className="mono">{askingPrice!=null?fmt(askingPrice):"Not quoted yet"}</span></div>
            {listingRange&&<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMd,marginBottom:3}}><span>Listings seen</span><span className="mono">{listingRange}</span></div>}
            {offerSuggestion.rationale&&<div style={{fontSize:11,color:T.textSm,lineHeight:1.5,marginTop:7}}>{offerSuggestion.rationale}</div>}
            {suggestionMismatches.map(function(m,i){return <div key={i} style={{fontSize:11,color:T.amber,lineHeight:1.5,marginTop:7,fontWeight:600}}>! {m}</div>;})}
            <div style={{display:"flex",gap:8,marginTop:10}}>
              <button onClick={function(){setTargetPrice(offerSuggestion.amount);setTouchedFields(function(p){return{...p,targetPrice:true};});onOfferSuggestionHandled&&onOfferSuggestionHandled();}} style={{flex:1,background:T.teal,color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:700,cursor:"pointer"}}>Set my offer to {fmt(offerSuggestion.amount)}</button>
              <button onClick={function(){onOfferSuggestionHandled&&onOfferSuggestionHandled();}} style={{background:"#fff",color:T.textMd,border:"1px solid "+T.border,borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Dismiss</button>
            </div>
          </div>}
          {!dealerView&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:12,color:T.textMd}}>Discount</span>
            <input type="number" value={discount} onChange={function(e){setDiscount(num(e.target.value));}} style={{width:90,border:"1.5px solid "+T.border,borderRadius:7,padding:"4px 7px",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.text,background:"#fff",outline:"none",textAlign:"right"}}/>
          </div>}
          {!offerUnavailable&&!dealerView&&clydeIncentiveAdj>0&&row("✓ Confirmed with Clyde","-"+fmt(clydeIncentiveAdj),T.green)}
          {!offerUnavailable&&!dealerView&&selectedIncentiveAmount>0&&row("✓ "+(programList.find(function(p){return p.id===selectedProgramId;})?.label||"Selected incentive"),"-"+fmt(selectedIncentiveAmount),T.green)}
          {!offerUnavailable&&row("Incentives",dealerView?fmt(totalIncentives):"-"+fmt(totalIncentives),T.green)}
          {!offerUnavailable&&<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
            <span style={{fontSize:12,color:T.textMd}}>Est. TTL</span>
            <ProvenanceBadge source="estimated"/>
          </div>}
          {!offerUnavailable&&row("Tax ("+ttl.rate+"%) + fees",fmt(ttl.total))}
          {!offerUnavailable&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:800,color:T.text,paddingTop:8,borderTop:"1px solid "+T.border,marginTop:4}}><span>Net price</span><span className="mono">{fmt(netPrice)}</span></div>}
          {offerUnavailable&&<div style={{fontSize:11,color:T.textSm,lineHeight:1.5,paddingTop:8,borderTop:"1px solid "+T.border,marginTop:4}}>Research could not confirm a value for this vehicle, so there is no net price, payment or total to show yet. Enter an offer above once you have a figure from a dealer or a listing.</div>}
        </Tile>

        {/* TRADE */}
        {tradeVehicle&&(
        <Tile id="trade" label="🔄 Trade" headline={fmt(tradeEquity)} activeTile={activeTile} setActiveTile={setActiveTile} clydeFlash={clydeFlash}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>{tradeVehicle.year} {tradeVehicle.make} {tradeVehicle.model}</div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:12,color:T.textMd}}>Down payment</span>
            <input type="number" value={downPayment} onChange={function(e){setDownPayment(num(e.target.value));setTouchedFields(function(p){return{...p,downPayment:true};});}} style={{width:80,border:"1.5px solid "+T.teal,borderRadius:7,padding:"4px 7px",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.teal,background:"#fff",outline:"none",textAlign:"right",marginLeft:"auto"}}/>
          </div>
          {!dealerView&&(
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              {[["best_offer","Best Offer"],["vinaudit","VinAudit"],["manual","Manual"]].map(function(opt){return(
                <button key={opt[0]} onClick={function(){setTradeSource(opt[0]);}} style={{flex:1,padding:"6px",borderRadius:7,border:"1.5px solid "+(tradeSource===opt[0]?T.teal:T.border),background:tradeSource===opt[0]?T.tealL:"transparent",color:tradeSource===opt[0]?T.teal:T.textMd,fontSize:10.5,fontWeight:600}}>{opt[1]}</button>
              );})}
            </div>
          )}
          {tradeSource==="manual"&&!dealerView&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <span style={{fontSize:12,color:T.textMd}}>Manual trade value</span>
              <input type="number" value={manualTradeValue} onChange={function(e){setManualTradeValue(num(e.target.value));}} style={{width:90,border:"1.5px solid "+T.teal,borderRadius:7,padding:"4px 7px",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.teal,background:"#fff",outline:"none",textAlign:"right",marginLeft:"auto"}}/>
            </div>
          )}
          {row("Trade allowance"+(dealerView?"":" ("+(tradeSource==="manual"?"manual":tradeSource==="vinaudit"?"VinAudit":"best offer")+")"),fmt(tradeAllowance))}
          {row("Loan payoff","-"+fmt(tradePayoff),T.red)}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:800,color:T.green,paddingTop:8,borderTop:"1px solid "+T.border,marginTop:4}}><span>Net equity</span><span className="mono">+{fmt(tradeEquity)}</span></div>
        </Tile>
        )}

        {/* ADD-ONS */}
        <Tile id="addons" label="➕ Add-ons" headline={selectedAddOnItems.length?"+"+fmt(selectedAddOnItems.reduce(function(s,a){return s+addOnFinancedCost(a.price,aprRate,term).monthlyDelta;},0))+"/mo":"None"} activeTile={activeTile} setActiveTile={setActiveTile} clydeFlash={clydeFlash}>
          {ADD_ONS.map(function(a){
            const checked=selectedAddOns.indexOf(a.id)>=0;
            const cost=addOnFinancedCost(a.price,aprRate,term);
            return(
              <div key={a.id} onClick={function(){setSelectedAddOns(function(p){return checked?p.filter(function(x){return x!==a.id;}):p.concat([a.id]);});}} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 10px",borderRadius:8,border:"1.5px solid "+(checked?T.teal:T.border),background:checked?T.tealL:"transparent",marginBottom:6,cursor:"pointer"}}>
                <div style={{width:18,height:18,borderRadius:5,border:"2px solid "+(checked?T.teal:T.border2),background:checked?T.teal:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{checked&&<span style={{color:"#fff",fontSize:10,fontWeight:800}}>✓</span>}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:T.text}}>{a.label}</div>
                  <div style={{fontSize:10,color:T.textSm}}>{fmt(a.price)} sticker · {fmt(cost.monthlyDelta)}/mo financed over {term}mo</div>
                </div>
              </div>
            );
          })}
        </Tile>

        {/* FINANCING */}
        <Tile id="financing" label="💳 Financing" headline={offerUnavailable?"—":fmt(monthly)+"/mo"} headlineColor={offerUnavailable?T.textSm:undefined} activeTile={activeTile} setActiveTile={setActiveTile} clydeFlash={clydeFlash}>
          {!dealerView&&programList.length>0&&(
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:T.textSm,display:"block",marginBottom:4}}>Financing / incentive options</label>
              {programList.map(function(p){
                const active=selectedProgramId===p.id;
                const pay=paymentForProgram(p);
                const sub=[p.rate!=null?p.rate.toFixed(2)+"% APR":null,p.term!=null?p.term+"mo":null,p.amount!=null?fmt(p.amount)+" cash":null].filter(Boolean).join(" · ");
                return(
                  <div key={p.id} onClick={function(){selectProgram(p);}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"9px 10px",borderRadius:8,border:"1.5px solid "+(active?T.teal:T.border),background:active?T.tealL:"transparent",marginBottom:6,cursor:"pointer"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:600,color:T.text}}>{p.label}{active&&" ✓"}</div>
                      {sub&&<div style={{fontSize:10,color:T.textSm}}>{sub}</div>}
                      {p.foundBy==="clyde"&&<div style={{fontSize:10,color:T.textSm,lineHeight:1.45,marginTop:2}}>Found by Clyde — per {p.source||"a source he named"}{p.asOf?", "+p.asOf:""}. Estimate until confirmed at the dealer.{p.url?" ":""}{p.url&&<a href={p.url} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();}} style={{color:T.teal,fontWeight:600}}>source</a>}</div>}
                    </div>
                    <div className="mono" style={{fontSize:13,fontWeight:700,color:active?T.teal:T.textMd,flexShrink:0}}>{fmt(pay)}/mo</div>
                  </div>
                );
              })}
              <div style={{fontSize:10,color:T.textSm,marginTop:2}}>Pick one to use it for this deal — none selected uses the standard estimated rate below.</div>
            </div>
          )}
          {!dealerView&&(
            <div style={{marginBottom:8}}>
              <label style={{fontSize:11,color:T.textSm,display:"block",marginBottom:4}}>Credit tier</label>
              <select value={creditTier} onChange={function(e){setCreditTier(e.target.value);}} style={{width:"100%",border:"1.5px solid "+T.border,borderRadius:7,padding:"6px 8px",fontSize:12,fontWeight:600,color:T.text,background:"#fff",outline:"none"}}>
                {CREDIT_TIERS.map(function(t){return <option key={t.id} value={t.id}>{t.label}</option>;})}
              </select>
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
            <span style={{fontSize:11,color:T.textSm}}>Est. rate {aprRate.toFixed(2)}%</span>
            <ProvenanceBadge source="estimated"/>
            {fredRate&&fredRate.rate!=null&&<span style={{fontSize:10,color:T.textSm}}>· FRED national avg {fredRate.rate.toFixed(2)}% as of {fredRate.asOf}</span>}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{fontSize:12,color:T.textMd}}>Term</span>
            <select value={term} onChange={function(e){setTerm(num(e.target.value));}} style={{border:"1.5px solid "+T.teal,borderRadius:7,padding:"4px 8px",fontSize:12,fontWeight:600,color:T.teal,background:"#fff",outline:"none"}}>
              {[48,60,72,84].map(function(t){return <option key={t} value={t}>{t} months</option>;})}
            </select>
          </div>
          {row("Amount financed",fmt(financed))}
          <div className={recalcing?"recalc":""} style={{textAlign:"center",padding:"10px 0"}}>
            <div style={{fontSize:28,fontWeight:800,color:T.teal}} className="mono">{fmt(monthly)}<span style={{fontSize:13,color:T.textMd,fontWeight:400}}>/mo</span></div>
          </div>
          {row("Total interest",fmt(financing.totalInterest))}
          {row("Total cost",fmt(financing.totalCost))}
        </Tile>

        {/* TCO */}
        {!offerUnavailable&&<div style={{background:T.slateL,borderRadius:12,padding:"14px 16px",marginBottom:16,marginTop:14}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Total Monthly Cost of Ownership</div>
          {[{l:"Loan payment",v:monthly},{l:"Est. insurance",v:insEst},{l:"Est. fuel (avg)",v:fuelEst}].map(function(r){return <div key={r.l} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textMd,marginBottom:4}}><span>{r.l}</span><span className="mono">{fmt(r.v)}</span></div>;})}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,color:T.text,paddingTop:8,borderTop:"1px solid "+T.border,marginTop:4}}>
            <span>Total monthly</span><span className="mono">{fmt(totalMonthly)}</span>
          </div>
          {tradeVehicle&&<div style={{marginTop:10,padding:"10px 12px",borderRadius:8,background:totalMonthly<currentMonthly?T.greenL:T.amberL,fontSize:12,fontWeight:600,color:totalMonthly<currentMonthly?T.green:T.amber}}>
            {totalMonthly<currentMonthly?"✓ "+(fmt(currentMonthly-totalMonthly))+"/mo better than your current "+tradeVehicle.nickname:"⚠ "+fmt(totalMonthly-currentMonthly)+"/mo more than your current "+tradeVehicle.nickname}
          </div>}
        </div>}

        {/* Have Clyde review this deal */}
        <button onClick={reviewWithClyde} disabled={clydeReviewing} style={{width:"100%",background:T.card,color:T.teal,border:"1.5px solid "+T.teal,borderRadius:10,padding:"11px 0",fontSize:13,fontWeight:700,marginBottom:clydeReview||clydeReviewErr?10:0}}>
          {clydeReviewing?"Asking Clyde...":"🔑 Have Clyde review this deal"}
        </button>
        {clydeReviewErr&&<div style={{fontSize:12,color:T.red,background:T.redL,borderRadius:8,padding:"9px 12px",marginBottom:10}}>{clydeReviewErr}</div>}
        {clydeReview&&<div style={{fontSize:12,color:T.text,background:T.tealL,border:"1px solid #99f6e4",borderRadius:10,padding:"11px 13px",lineHeight:1.6,whiteSpace:"pre-wrap",marginBottom:10}}>{clydeReview}</div>}

        {/* Mark as Purchased — manual trigger, or opened by Clyde's own
            MARK_PURCHASED detection via the completionRequested effect
            above. Deliberately reachable straight from this screen rather
            than gated behind the outreach/proposal pipeline — that
            pipeline needs a real dealer list to proceed past its first
            step, and live accounts get no fabricated one (Stage 3), so
            it's currently a dead end there. */}
        {completed?(
          <div className="fade-up" style={{padding:"14px 16px",borderRadius:12,background:"linear-gradient(135deg,#f0fdfa,#ccfbf1)",border:"1.5px solid #5eead4",fontSize:13,fontWeight:700,color:T.teal,textAlign:"center"}}>
            🎉 Added to your fleet — {targetYear} {targetMake} {targetModel} is now in your garage.
          </div>
        ):showCompleteConfirm?(
          <div style={{padding:"14px 16px",borderRadius:12,background:T.amberL,border:"1.5px solid #fcd34d"}}>
            <div style={{fontSize:12,color:T.text,lineHeight:1.6,marginBottom:10}}>
              Mark this deal as purchased? This adds the {targetYear} {targetMake} {targetModel}{intent.trim?" "+intent.trim:""} to your fleet{tradeVehicle?", and removes your "+tradeVehicle.nickname+" (traded in)":""}.
            </div>
            {completeErr&&<div style={{fontSize:12,color:T.red,background:T.redL,borderRadius:8,padding:"8px 11px",marginBottom:10}}>{completeErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={completeDeal} disabled={completing} style={{flex:1,background:T.amber,color:"#fff",border:"none",borderRadius:9,padding:"9px 0",fontSize:12,fontWeight:700}}>
                {completing?"Completing...":"Confirm — Mark Purchased"}
              </button>
              <button onClick={function(){setShowCompleteConfirm(false);setCompleteErr("");}} disabled={completing} style={{flex:1,background:"#fff",color:T.textMd,border:"1px solid "+T.border,borderRadius:9,padding:"9px 0",fontSize:12,fontWeight:700}}>
                Cancel
              </button>
            </div>
          </div>
        ):(
          <button onClick={function(){setShowCompleteConfirm(true);}} disabled={!dealId} title={dealId?"":"Keep talking with Clyde about this deal first — nothing to mark purchased yet"} style={{width:"100%",background:dealId?"#fff":T.slateL,color:dealId?T.text:T.textSm,border:"1.5px solid "+(dealId?T.border:T.border),borderRadius:10,padding:"11px 0",fontSize:13,fontWeight:700,opacity:dealId?1:.65}}>
            ✓ Mark as Purchased
          </button>
        )}
      </div>
      <div style={{padding:"16px 20px",borderTop:"1px solid "+T.border,flexShrink:0}}>
        {/* listings.length===0: no specific listing exists for this
            vehicle (see getListings' own comment) — nothing for the
            Listings screen to show, so this goes straight to Build
            Proposal off the researched retailValue instead, same as the
            isNew path already does. */}
        {/* Blocked with no offer: this button's other path builds a
            document that goes to dealers, and with nothing behind the
            price it would send a figure derived from zero. */}
        {/* The real dealer flow. "Build Proposal" below opens the offer
            document, whose own footer now routes here too -- the simulated
            broadcast that used to sit behind it is gone. */}
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <button onClick={function(){if(!offerUnavailable&&dealId)setScreen("senddealer");}} disabled={offerUnavailable||!dealId}
            title={dealId?(offerUnavailable?"Enter an offer first":""):"Talk to Clyde about this deal first"}
            style={{flex:1,background:T.card,color:(offerUnavailable||!dealId)?T.textSm:T.teal,border:"1.5px solid "+((offerUnavailable||!dealId)?T.border:T.teal),borderRadius:10,padding:"11px 0",fontSize:13,fontWeight:700,opacity:(offerUnavailable||!dealId)?.6:1}}>
            Send to a dealer
          </button>
          <button onClick={function(){setScreen("responses");}}
            style={{flex:1,background:dealerResponseCount>0?T.tealL:T.card,color:dealerResponseCount>0?T.teal:T.textMd,border:"1.5px solid "+(dealerResponseCount>0?T.teal:T.border),borderRadius:10,padding:"11px 0",fontSize:13,fontWeight:700}}>
            {dealerResponseCount>0?"Responses ("+dealerResponseCount+")":"Responses"}
          </button>
        </div>
        <button onClick={function(){if(!offerUnavailable)setScreen(isNew||listings.length===0?"proposal":"listings");}} disabled={offerUnavailable} style={{width:"100%",background:offerUnavailable?"#e2e8f0":"linear-gradient(135deg,"+T.teal+",#0f766e)",color:offerUnavailable?T.textSm:"#fff",border:"none",borderRadius:10,padding:"13px 0",fontSize:14,fontWeight:700,opacity:offerUnavailable?.6:1}}>
          {offerUnavailable?"Enter an offer to continue":isNew||listings.length===0?"Build Proposal →":"View Listings →"}
        </button>
      </div>
    </div>
    );
  }

  if(screen==="listings")return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
      <Header title="Best Matches" onClose={onClose}/>
      <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
        {/* Only true when real listings came back and the search had no
            zip to work with -- see listingsUsedDefaultLocation's own
            comment above. The line names the actual fallback city rather
            than saying "elsewhere" or "unknown", because a household
            deciding whether to click "Make Offer" on one of these needs
            to know it's genuinely hundreds of miles off, not just vaguely
            uncertain. */}
        {listingsUsedDefaultLocation&&(
          <div style={{marginBottom:12,padding:"9px 11px",borderRadius:8,background:T.amberL,border:"1px solid "+T.amber}}>
            <div style={{fontSize:11.5,color:T.amber,fontWeight:700,lineHeight:1.5}}>
              Showing {listingsDefaultLocationLabel||"Dallas–Fort Worth"} results — add your ZIP in Settings for listings near you.
            </div>
          </div>
        )}
        <div style={{fontSize:12,color:T.textSm,marginBottom:14}}>Market avg: {fmt(Math.round(marketAvg))} · {listings.length} listing{listings.length!==1?"s":""} match your criteria</div>
        {/* .filter drops any slot with no real listing behind it — this
            screen used to assume exactly 3 always exist (true back when
            getListings() always returned a 3-item array, even a wrong
            vehicle's); listings.length can now be 0-3 (see getListings'
            own comment), so a slot with no `listing` is skipped rather
            than crashing on l.id below. The "View Listings" button
            already routes straight to Build Proposal instead when
            listings.length===0, so this empty-map case is a defensive
            fallback, not the normal path there. */}
        {[{listing:listings[0],label:"🔑 Clyde's Pick",lc:T.teal,lb:T.tealL},{listing:listings[1],label:"💰 Best Value",lc:T.green,lb:T.greenL},{listing:listings[2],label:"📍 Make an Offer",lc:T.blue,lb:T.blueL}].filter(function(item){return!!item.listing;}).map(function(item,idx){
          const l=item.listing;
          return(
            <div key={l.id} className="fade-up" style={{borderRadius:12,border:"1.5px solid "+(selectedListing?.id===l.id?T.teal:T.border),background:selectedListing?.id===l.id?T.tealL:T.card,marginBottom:10,overflow:"hidden",animationDelay:(idx*0.08)+"s"}}>
              <div style={{padding:"13px 16px"}}>
                <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:5,color:item.lc,background:item.lb}}>{item.label}</span>
                  {l.partner&&<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,color:T.teal,background:T.tealL,border:"1px solid #99f6e4"}}>✓ Verified Partner</span>}
                  {l.cpo&&<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,color:T.purple,background:T.purpleL}}>CPO</span>}
                  {mode==="live"&&<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,color:T.green,background:T.greenL}}>{l.url?"Live listing":"Live (est.)"}</span>}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:2}}>{l.year} {l.make} {l.model}</div>
                    <div style={{fontSize:12,color:T.textSm,marginBottom:4}}>{l.trim} · {l.mileage.toLocaleString()} mi · {l.color}</div>
                    {l.dist&&<div style={{fontSize:11,color:T.textSm}}>📍 {l.dealer} · {l.dist} mi</div>}
                    {!l.dist&&<div style={{fontSize:11,color:T.textSm}}>👤 {l.dealer}</div>}
                    {l.url&&<a href={l.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:T.teal,fontWeight:600,display:"inline-block",marginTop:4}}>View listing →</a>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div className="mono" style={{fontSize:18,fontWeight:800,color:T.text}}>{fmt(l.price)}</div>
                    {l.savings!=null&&<div style={{fontSize:11,fontWeight:600,color:l.savings<0?T.green:T.red,marginTop:2}}>{l.savings<0?fmt(Math.abs(l.savings))+" below market":fmt(l.savings)+" above market"}</div>}
                  </div>
                </div>
                <div style={{marginTop:8,padding:"8px 10px",borderRadius:7,background:T.slateL,fontSize:11,color:T.textMd,lineHeight:1.5}}>
                  🔑 {l.note}
                </div>
              </div>
              <div style={{padding:"10px 16px",borderTop:"1px solid "+T.border}}>
                <button onClick={function(){setSelectedListing(l);setScreen("proposal");}} style={{width:"100%",background:"linear-gradient(135deg,"+T.teal+",#0f766e)",color:"#fff",border:"none",borderRadius:9,padding:"10px 0",fontSize:13,fontWeight:700}}>Make Offer on This Vehicle →</button>
              </div>
            </div>
          );
        })}
      </div>
      {/* This screen had no back control at all -- only the per-listing
          forward action and the header's close, which exits the modal.
          Same footer treatment its sibling screens already have. */}
      <div style={{padding:"16px 20px",borderTop:"1px solid "+T.border,flexShrink:0}}>
        <button onClick={function(){setScreen("deal");}} style={{width:"100%",background:T.card,color:T.teal,border:"1.5px solid "+T.teal,borderRadius:10,padding:"12px 0",fontSize:13,fontWeight:700}}>
          Back to the worksheet
        </button>
      </div>
    </div>
  );

  if(screen==="proposal"){
    // Guarded at the destination rather than at each button that leads
    // here. fmt(null) renders as $0 rather than failing, so an unguarded
    // route would put a $0 offer in front of a dealer -- silently, and
    // looking entirely deliberate. The button on the deal screen is
    // already disabled in this state and the listings route is not
    // reachable while askingPrice is null, so this should be dead code;
    // it exists because 'should be unreachable' is not what belongs
    // between a null and an outward-facing document.
    if(offerUnavailable){
      return(
        <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
          <Header title="Proposal" onClose={onClose}/>
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",textAlign:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>No offer to send yet</div>
            <div style={{fontSize:12,color:T.textSm,lineHeight:1.6,maxWidth:320}}>Research could not confirm a value for this vehicle, so there is no offer price to put in front of a dealer. Go back and enter an offer once you have a figure from a dealer or a listing.</div>
          </div>
          <div style={{padding:"16px 20px",borderTop:"1px solid "+T.border,flexShrink:0}}>
            <button onClick={function(){setScreen("deal");}} style={{width:"100%",background:T.card,color:T.teal,border:"1.5px solid "+T.teal,borderRadius:10,padding:"12px 0",fontSize:13,fontWeight:700}}>Back to the worksheet</button>
          </div>
        </div>
      );
    }
    // (selectedListing||listings[0]) used to be trusted as always truthy
    // here — true only while getListings() guaranteed a non-empty array
    // (even a wrong vehicle's, pre-fix). Now that listings can be []
    // (see getListings' own comment), this falls back to a synthetic
    // stand-in built from the same target identity/basePrice the
    // worksheet itself already shows, rather than crashing on
    // undefined.year.
    //
    // Both sources are now identity-guarded, so a listing belonging to
    // some other vehicle takes that same synthetic path rather than
    // naming a car nobody is buying. The VIN line is suppressed outright
    // unless the household's own selected listing passes the guard: a VIN
    // is the strongest identity claim in this document, it is what a
    // dealer will act on, and an unverified one must never appear. Not
    // silently corrected to a different VIN either -- if this fires at
    // all, Phase 1 has failed upstream, and a proposal quietly rewriting
    // itself would hide that.
    //
    // The offer price is now the household's own figure rather than
    // listingBasis.price*0.97 -- a hardcoded 3 percent off the asking
    // price, applied to a document that goes to dealers, chosen by
    // nobody. It ignored the worksheet entirely, so a household who set
    // an offer watched a different number get sent. listingBasis still
    // supplies the vehicle's identity and mileage, which is all it was
    // ever a reliable source for.
    const listingBasis=listingForTarget||firstListingForTarget||{year:targetYear,make:targetMake,model:targetModel,mileage:0,price:priceBasis};
    // Claims this document used to make that nothing in this system
    // supports, all removed:
    //   "Financing: Pre-qualified, ready to proceed" -- there is no
    //     lender integration, no credit check and no pre-qualification
    //     anywhere in HomeFleet. A false statement about a buyer's
    //     finances, sent to the party negotiating against them.
    //   "HomeFleet verified buyer" -- signup collects a self-reported
    //     name, phone and zip, and admin approval is one UPDATE of
    //     access_level. Nothing verifies a person, an identity or an
    //     ability to pay, which is what a dealer reads that as.
    //   "Ready to close within 7 days" / "Close within 5 business days"
    //     / "Inspection: Requested prior to close" -- commitments the
    //     household never made, put in their mouth.
    //   "expires in 72 hours" / "Offer valid 48 hours" -- two invented
    //     deadlines that contradicted each other AND the real link,
    //     which lives one week (LINK_TTL_MS.offer in
    //     functions/_lib/dealLinks.js -- change one, change this text).
    //   "Target payment" was fmt(monthly+20) -- the worksheet's real
    //     payment plus an unexplained $20. Small, but it meant the
    //     document quoted a figure the household never chose and the
    //     worksheet never showed. It is now the computed payment itself.
    //     Audited at the same time: that padding was the ONLY arithmetic
    //     adjustment anywhere in this document; every other value is
    //     passed through as the worksheet computed it.
    // The household's own location, or nothing at all. This line used to
    // read "in the DFW market" for every household in the country --
    // invented, and invented in a document they may hand to a dealer.
    const locale=householdZip?" near "+householdZip:(householdState?" in "+householdState:"");
    const proposal=isNew
      ?"Purchase offer from a HomeFleet household"+locale+".\n\nThis offer comes from the buyer. HomeFleet is the tool they used to put it together and does not negotiate, submit, or agree to anything on their behalf.\n\nProposed terms — nothing here is agreed until you respond:\n  Vehicle: "+targetYear+" "+targetMake+" "+targetModel+(intent.trim?" "+intent.trim:"")+"\n  Target price: "+fmt(basePrice)+"\n  Trade equity: "+fmt(tradeEquity)+" ("+(tradeVehicle?tradeVehicle.year+" "+tradeVehicle.make+" "+tradeVehicle.model+(tradeAllowance?", trade-in allowance "+fmt(tradeAllowance)+" ("+(tradeSource==="manual"?"manual entry":tradeSource==="vinaudit"?"VinAudit market value":"best matched offer")+")":""):"no trade")+")\n  Incentive path: "+(programList.find(function(p){return p.id===selectedProgramId;})?.label||"Standard financing, no incentive selected")+"\n  Down payment: "+fmt(downPayment)+"\n  Target payment: ≤"+fmt(monthly)+"/mo @ "+term+" months\n  Timing and inspection: open — the buyer will agree those with you directly.\n\nIf the buyer sends a HomeFleet link with this, it stays open for one week from the day they generate it and can be answered once; the exact date is shown on the page itself. Please respond with availability and any additional dealer incentives."
      :"Purchase offer from a HomeFleet household"+locale+".\n\nThis offer comes from the buyer. HomeFleet is the tool they used to put it together and does not negotiate, submit, or agree to anything on their behalf.\n\nProposed terms — nothing here is agreed until you respond:\n\nVehicle: "+listingBasis.year+" "+listingBasis.make+" "+listingBasis.model+", "+listingBasis.mileage.toLocaleString()+" miles\n"+(listingForTarget?.vin?"VIN: "+listingForTarget.vin+"\n":"")+"Offer price: "+fmt(basePrice)+(askingPrice!=null?"\nAgainst asking price: "+fmt(askingPrice):"")+"\nTrade vehicle: "+(tradeVehicle?tradeVehicle.year+" "+tradeVehicle.make+" "+tradeVehicle.model+" — net equity "+fmt(tradeEquity):"No trade")+"\nTiming and inspection: open — the buyer will agree those with you directly.\n\nIf the buyer sends a HomeFleet link with this, it stays open for one week from the day they generate it and can be answered once; the exact date is shown on the page itself. Please respond with availability and any additional dealer incentives.";

    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.card}}>
        <Header title="Proposal" onClose={onClose}/>
        <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
          <div style={{background:T.slateL,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
            <pre style={{fontSize:12,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{proposal}</pre>
          </div>
        </div>
        <div style={{padding:"16px 20px",borderTop:"1px solid "+T.border,flexShrink:0}}>
          {/* Points at the real flow. This used to read "Send Proposal
              to N Dealers" and open a simulated broadcast -- fake progress
              steps, a fabricated dealer reply and invented appointment
              times. It was unreachable (dealers was always empty outside
              a demo tier that cannot open this screen at all), which is
              why nobody ever saw it, and it is gone rather than left to
              be re-enabled by one line. */}
          {/* Two controls, not one keyed on dealId. The single button this
              replaced hid its back label precisely when a real deal existed
              -- the working path had no way back while the offerUnavailable
              branch above did. The only other control on this screen is the
              header's close, which exits the whole modal, so a household
              here had one button and pressing it discarded the sheet. */}
          {dealId&&(
            <button onClick={function(){setScreen("senddealer");}} style={{width:"100%",background:"linear-gradient(135deg,"+T.teal+",#0f766e)",color:"#fff",border:"none",borderRadius:10,padding:"13px 0",fontSize:14,fontWeight:700,marginBottom:8}}>
              Send this to a dealer →
            </button>
          )}
          <button onClick={function(){setScreen("deal");}} style={{width:"100%",background:T.card,color:T.teal,border:"1.5px solid "+T.teal,borderRadius:10,padding:"12px 0",fontSize:13,fontWeight:700}}>
            Back to the worksheet
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/* ── Clyde chat ── */
/* Extracts structured LISTING: {...} lines Clyde emits when it finds real inventory via
   web search, so they can render as real clickable cards instead of plain text URLs.
   Any line that fails to parse or has no url is silently dropped from the card list
   (it stays visible as plain text, which is the safe fallback). */
// groundedListingUrls is the server's verdict (functions/api/chat.js,
// groundListingUrls): the url strings, verbatim, of LISTING lines whose
// page actually appeared in that turn's search results. A card renders
// only for those. The line pattern below MUST match the server's -- if
// they disagree about what a LISTING line is, one side is checking lines
// the other never renders.
//
// An ungrounded listing is dropped as a CARD and its raw JSON line is
// still stripped from the prose, so the household sees Clyde's description
// without a link to a page nobody verified. A message with no verdict at
// all -- a thread restored from storage, or anything predating this check
// -- renders no cards: those listings were never grounded, and inventory
// that old is likely gone anyway.
function parseListings(content,groundedListingUrls){
  const lines=content.split("\n");
  const listings=[];
  const textLines=[];
  const grounded=Array.isArray(groundedListingUrls)?groundedListingUrls:[];
  lines.forEach(function(line){
    const m=line.match(/^\s*LISTING:\s*(\{.*\})\s*$/);
    if(m){
      try{
        const obj=JSON.parse(m[1]);
        if(obj&&obj.url&&/^https?:\/\//.test(obj.url)){
          if(grounded.indexOf(String(obj.url))>=0)listings.push(obj);
        }
        else textLines.push(line);
      }catch{textLines.push(line);}
    }else textLines.push(line);
  });
  return{text:textLines.join("\n").trim(),listings:listings};
}
// compare_prices results, shown beside the prose that uses them. Every
// figure, the difference and the direction are the server's, rendered as
// given: nothing here subtracts, rounds or decides which way round two
// numbers go, so the arithmetic exists in one place (chat.js's
// runComparePrices). An entry missing any part is skipped rather than
// partly shown. Neutral styling on purpose -- "higher" is not good or bad
// until you know which side the household is on.
const COMPARE_DIRECTIONS={higher:"higher than",lower:"lower than",equal:"the same as"};
function isShownComparison(c){
  return !!(c&&c.a&&c.b&&typeof c.a.value==="number"&&Number.isFinite(c.a.value)&&typeof c.b.value==="number"&&Number.isFinite(c.b.value)
    &&typeof c.difference==="number"&&Number.isFinite(c.difference)&&COMPARE_DIRECTIONS[c.direction]
    &&typeof c.a.label==="string"&&typeof c.b.label==="string");
}
function ComparisonCards({comparisons}){
  const shown=Array.isArray(comparisons)?comparisons.filter(isShownComparison):[];
  if(!shown.length)return null;
  const usd=function(n){return "$"+n.toLocaleString("en-US",{maximumFractionDigits:2});};
  return(
    <div style={{marginTop:10,paddingTop:8,borderTop:"1px solid "+CT.border}}>
      <div style={{fontSize:10,color:CT.muted,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>Checked comparison{shown.length===1?"":"s"}</div>
      {shown.map(function(c,ci){return(
        <div key={ci} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:8,padding:"8px 10px",marginBottom:6,fontSize:12,lineHeight:1.5,whiteSpace:"normal"}}>
          <div><span className="mono" style={{fontWeight:700}}>{usd(c.a.value)}</span> <span style={{color:CT.muted}}>{c.a.label}</span></div>
          <div style={{color:CT.sky,fontWeight:600}}>{c.direction==="equal"?"is the same as":"is "+usd(c.difference)+" "+COMPARE_DIRECTIONS[c.direction]}</div>
          <div><span className="mono" style={{fontWeight:700}}>{usd(c.b.value)}</span> <span style={{color:CT.muted}}>{c.b.label}</span></div>
        </div>
      );})}
    </div>
  );
}

function ListingCard({l}){
  return(
    <a href={l.url} target="_blank" rel="noopener noreferrer" style={{display:"block",background:"rgba(255,255,255,0.06)",border:"1px solid "+CT.border,borderRadius:10,padding:"10px 12px",marginTop:8,textDecoration:"none",color:"#fff"}}>
      <div style={{fontSize:13,fontWeight:700}}>{[l.year,l.make,l.model,l.trim].filter(Boolean).join(" ")}</div>
      <div style={{fontSize:12,color:CT.muted,marginTop:2}}>{l.price?"$"+Number(l.price).toLocaleString():""}{l.mileage?" · "+Number(l.mileage).toLocaleString()+" mi":""}{l.dealer?" · "+l.dealer:""}</div>
      <div style={{fontSize:11,color:CT.sky,marginTop:4}}>View listing →</div>
    </a>
  );
}
function ClydeChat({fleet,mode,onIntentUpdate,onOfferSuggested,onMsrpCited,onIncentivesFound,householdState,householdZip,dealId,onDealIdChange,onIntentClear,onMarkPurchasedRequested,onCollapse,pendingHandoff,resumableConversation,worksheetSummary,dealerResponsesBlock,currentNetPrice,tradeId,liveTradeOffer,currentDownPayment,worksheetFigures}){
  const[msgs,setMsgs]=useState(function(){return[clydeGreetingMessage()];});
  const[inp,setInp]=useState("");
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  // Mirrors ClydePanel.jsx's own conversation-persistence wiring.
  // conversationId: client-generated (uid(), already defined at module
  // scope in this file) once, on the first send() of this chat instance,
  // then reused for every later turn. justResumed: transient, one-turn
  // hint — always false in this pass (nothing loads a past thread back
  // into msgs yet, that's Pass 2's History UI), wired for the same
  // reason ClydePanel.jsx's own flag is. dealId is now a controlled prop,
  // not local state — HomeFleetBuying (the parent) owns it, the same way
  // it already owns intent, since BuyingModal also needs to know it
  // (completeDeal posts to /api/deals/:id/complete) and two independent
  // copies of "the linked deal's id" would risk drifting out of sync.
  // Still null until Clyde's own extract_deal_intent tool call has
  // captured something AND at least one more turn has been sent after
  // that (chat.js resolves/creates the deal id synchronously per
  // request, so the exact turn that first captures real intent doesn't
  // yet know its own id — only the turn after does). No local intent
  // state anymore either — HomeFleetBuying owns the merged, incremental
  // intent now; this component just forwards whatever a turn's response
  // captured.
  const[conversationId,setConversationId]=useState(null);
  const[justResumed,setJustResumed]=useState(false);
  // Guided-intake wizard state (see WIZARD_STEPS above — trim only now).
  // wizardStep indexes into WIZARD_STEPS; wizardStep>=WIZARD_STEPS.length
  // means done, and once there the wizard UI never renders again for this
  // conversation instance. wizardAnswers is a local mirror of what's been
  // captured so far, kept independent of the parent's own intent prop
  // (which this component doesn't read) purely so the trim step's price-
  // estimate box has immediate access to budget/downPayment/YMM the
  // instant they're relevant.
  //
  // ALWAYS starts "done" now, whether or not a handoff is pending — trim
  // presupposes a known make/model, which Clyde only ever has after a
  // confirmed handoff (or organically, later in free-form conversation);
  // there's no sensible moment to auto-ask it on mount otherwise, unlike
  // when this wizard still owned YMM itself. Once HANDOFF_CONFIRMED fires
  // (see send() below), wizardStep is set back to the one remaining step
  // (trim) specifically. wizardAnswers is pre-seeded from pendingHandoff
  // regardless, since the price-estimate box needs it the moment trim
  // activates.
  const[wizardStep,setWizardStep]=useState(function(){return WIZARD_STEPS.length;});

  // Load a stored thread back into msgs. GET /api/conversations/[id]
  // already returned {conversation, messages}, user_id-scoped, and had
  // zero callers -- the "Resume" button navigated and restored nothing,
  // and setJustResumed(true) appeared nowhere in either panel, so the
  // resumeNote both system prompts carry could never fire. This is the
  // missing half, not new plumbing.
  const resumeApplied=useRef(false);
  useEffect(function(){
    if(resumeApplied.current||!resumableConversation||!resumableConversation.id)return;
    // Never over a conversation already in progress. msgs holds only the
    // greeting until someone speaks, which is what makes this safe to do
    // on mount rather than behind a prompt.
    if(conversationId||msgs.some(function(m){return (m.kind||"chat")!=="greeting";}))return;
    resumeApplied.current=true;
    let cancelled=false;
    fetch("/api/conversations/"+encodeURIComponent(resumableConversation.id))
      .then(function(r){return r.ok?r.json():null;})
      .then(function(d){
        if(cancelled||!d||!Array.isArray(d.messages)||!d.messages.length)return;
        // Every restored row is tagged "chat", deliberately. Stored rows
        // carry only role and content, and the two kinds that mean
        // something to this app would both misfire: "greeting" is dropped
        // by toApiMessages' filter, so a real turn would vanish from the
        // model's view of the thread, and "wizard-question" would hand a
        // finished wizard step back to the live renderer.
        const restored=d.messages
          .filter(function(m){return m&&(m.role==="user"||m.role==="assistant")&&m.content;})
          // content stays verbatim for the household; apiContent is what
          // Clyde reads, with every dollar figure and rate replaced by a
          // dated marker. Both roles are redacted, not just Clyde's own:
          // the superseded $48,200 that reached a household live was
          // typed by the household in the thread's first turn.
          .map(function(m){return{role:m.role,kind:"chat",content:m.content,apiContent:redactStaleFigures(m.content,m.createdAt)};});
        if(!restored.length)return;
        // The greeting stays at the top, so order still comes from msgs
        // alone -- the invariant HOMEFLEET_WIZARD_MESSAGING_PLAN.md
        // exists to protect. It is filtered out of the API view anyway,
        // and toApiMessages strips any remaining leading assistant turns,
        // so a thread that opens with Clyde still satisfies the
        // first-message-must-be-user rule.
        setMsgs(function(p){return [p[0]].concat(restored);});
        setConversationId(resumableConversation.id);
        setWizardStep(WIZARD_STEPS.length);
        // Consumed on the next turn by both system prompts: a figure
        // recalled from a stored thread is not current until re-verified.
        setJustResumed(true);
      })
      .catch(function(){});
    return function(){cancelled=true;};
  },[resumableConversation,conversationId,msgs]);
  const[wizardAnswers,setWizardAnswers]=useState(function(){return pendingHandoff||{};});
  const wizardDone=wizardStep>=WIZARD_STEPS.length;
  const[trimDigDeeper,setTrimDigDeeper]=useState(null); // {amount,low,high}|null
  const[trimDigging,setTrimDigging]=useState(false);
  // Hard confirmation gate: while a real handoff is pending, extract_deal_
  // intent captures are buffered locally (bufferedIntent) instead of
  // being forwarded to the parent's onIntentUpdate — that's what actually
  // stops BuyingModal (and its real fetches) from mounting before the
  // household has confirmed Clyde's recap. handoffConfirmed starts true
  // when there's nothing to confirm (no pendingHandoff — e.g. the direct
  // "Shop" nav path or the wizard's own quick-start), so this gate is a
  // no-op for every pre-existing flow and only actually holds anything
  // back when a real handoff occurred.
  const[handoffConfirmed,setHandoffConfirmed]=useState(!pendingHandoff);
  const[bufferedIntent,setBufferedIntent]=useState(null);
  // "New chat" used to reset silently, straight to a blank transcript,
  // with no way to say whether the household meant to keep discussing
  // the SAME vehicle/deal or start over on a different one — dealId/
  // conversationId/the parent's intent all stayed exactly as they were
  // either way, so a genuinely new vehicle conversation could end up
  // merging its captures onto the previous vehicle's stale deal/intent.
  // This gates the reset behind an explicit choice instead of guessing.
  const[showNewChatPrompt,setShowNewChatPrompt]=useState(false);
  const msgsRef=useRef();const inpRef=useRef();

  // Direct scrollTop assignment on the messages list's OWN scrollable
  // container, not Element.scrollIntoView() on a marker inside it.
  // scrollIntoView walks up through every scrollable ancestor trying to
  // bring its target fully into view — harmless back when this pane sat
  // inside a fixed-height, isolated grid cell (nothing above it to
  // cascade to), but since HomeFleetBuying's stacked layout put this
  // component in normal page flow, it started dragging the whole page
  // down to the bottom of this list on mount and after every message —
  // exactly the two scroll bugs reported. Setting scrollTop on the known
  // container directly can only ever move that one element, never any
  // ancestor, regardless of surrounding layout.
  useEffect(function(){if(msgsRef.current)msgsRef.current.scrollTop=msgsRef.current.scrollHeight;},[msgs,busy]);

  // hidden: used only by the auto-kickoff effect below (a real turn, so
  // Clyde's own recap+confirm reply is a genuine model response — just
  // without a visible synthetic "user" bubble prompting it, so Clyde's
  // recap reads as if it's the first thing said in this conversation).
  const send=async function(text,hidden){
    const t=(text||inp).trim();if(!t||busy)return;
    // The one remaining wizard step ("trim") is Claude-routed, not
    // client-parsed — it falls straight through to the real API call
    // below every time, with its own advance-or-stay logic after the
    // response (see the extractedIntent handling further down).
    setInp("");setErr("");
    const next=msgs.concat([{role:"user",content:t}]);
    if(!hidden)setMsgs(next);
    setBusy(true);
    const isNewConversation=!conversationId;
    const cid=conversationId||uid();
    if(isNewConversation)setConversationId(cid);
    // Streaming placeholder — appended immediately so callClydeAPI's
    // onDelta callback below has a real message to update live, in the
    // same spot the single post-await setMsgs(...) used to append the
    // finished reply. streaming:true (and content:"") tells the render
    // below to show the existing typing-dots indicator instead of an
    // empty bubble until the first chunk actually arrives, and to skip
    // parseListings/token-stripping on a string that's still incomplete
    // — both of those still run exactly as before, just once, at
    // stream-end, when this placeholder is replaced with the real
    // finished message below.
    const streamId=uid();
    setMsgs(function(p){return p.concat([{role:"assistant",content:"",streaming:true,_streamId:streamId}]);});
    try{
      // dealIntent (the trailing arg) is no longer sent — chat.js's own
      // isNewDeal trigger reads Claude's own extract_deal_intent tool
      // call server-side now, not a client-guessed object (see that
      // file's own comment on this exact change). callClydeAPI's own
      // dealIntent parameter stays in its signature for now (harmless,
      // simply always undefined from this one call site). pendingHandoff
      // is sent every turn (harmless once Clyde's own handoffNote has
      // already done its job — buildClydeSystem only actually injects
      // anything when it's genuinely present) so a page reload mid-recap
      // doesn't lose the instruction.
      const{text:rawRep,sources,groundedListingUrls,dealId:newDealId,extractedIntent,calculatedLoan,suggestedOffer,msrpCitation,foundIncentives,comparisons}=await callClydeAPI(next,fleet,mode,tradeId,liveTradeOffer,householdState,householdZip,cid,isNewConversation,justResumed,dealId,undefined,pendingHandoff,worksheetSummary,dealerResponsesBlock,currentNetPrice,currentDownPayment,worksheetFigures,function(liveText){
        setMsgs(function(p){return p.map(function(m){return m._streamId===streamId?{...m,content:liveText}:m;});});
      });
      if(justResumed)setJustResumed(false); // one-turn-only hint, consumed
      if(newDealId&&newDealId!==dealId)onDealIdChange&&onDealIdChange(newDealId);
      // Forwarded, never applied here. This is a suggestion the worksheet
      // will render beside the offer field for the household to accept or
      // dismiss -- the same shape as MARK_PURCHASED, where Clyde offers
      // the action and a person completes it. Nothing on this path writes
      // the offer, and nothing should be added that does.
      if(suggestedOffer&&onOfferSuggested)onOfferSuggested(suggestedOffer);
      if(msrpCitation&&onMsrpCited)onMsrpCited(msrpCitation);
      if(foundIncentives&&foundIncentives.length&&onIncentivesFound)onIncentivesFound(foundIncentives);
      if(extractedIntent){
        // Hard confirmation gate: before the household has confirmed
        // Clyde's recap, extract_deal_intent captures are buffered
        // locally rather than forwarded to the parent's onIntentUpdate —
        // onIntentUpdate populating HomeFleetBuying's intent is exactly
        // what mounts BuyingModal and fires its real fetches, so nothing
        // may reach it yet. Once HANDOFF_CONFIRMED arrives (below), the
        // buffer (plus pendingHandoff itself) is flushed in one merge.
        if(handoffConfirmed){
          onIntentUpdate&&onIntentUpdate(extractedIntent);
        }else{
          setBufferedIntent(function(p){return{...(p||{}),...extractedIntent};});
        }
        setWizardAnswers(function(p){return{...p,...extractedIntent};});
      }
      if(calculatedLoan){
        // calculate_loan's own inputs/result, reshaped into the same
        // intent-partial shape onIntentUpdate already merges everything
        // else through — this is what makes every "what if" Clyde runs
        // update the worksheet live, not just a confirmed extract_deal_
        // intent capture. Same hard confirmation gate as above (a
        // calculate_loan call during Clyde's own pre-confirmation recap
        // is unlikely but not impossible, and nothing should reach the
        // worksheet before that gate opens either way). downPayment is
        // only included when chat.js's own capturedLoanCalc actually
        // carried one — see that file's own comment for why a default-0
        // down payment is never forwarded.
        //
        // calculatedLoan.price is deliberately NOT forwarded here, and
        // that now holds for a second, stronger reason. It was already
        // wrong on its own terms: Clyde naturally plugs the NET price
        // (already after discount — 'on the $28,860 net price...') into
        // calculate_loan, since that is the actual loan principal, so
        // routing it into the pre-discount price field double-applied the
        // discount. But there is no longer any price channel to route it
        // into at all -- no tool writes the household's offer, so this
        // must never become one by the back door. calculate_loan is for
        // exploring math, not for asserting what the household will pay.
        const loanPartial={financingRate:calculatedLoan.annualRate,financingTermMonths:calculatedLoan.termMonths};
        if(calculatedLoan.downPayment!=null)loanPartial.downPayment=calculatedLoan.downPayment;
        if(handoffConfirmed){
          onIntentUpdate&&onIntentUpdate(loanPartial);
        }else{
          setBufferedIntent(function(p){return{...(p||{}),...loanPartial};});
        }
      }
      // The "trim" wizard step is routed through this real Claude turn
      // since it needs genuine trim understanding, not just a number —
      // extract_deal_intent already does this reliably. Auto-advance
      // only when THIS turn's own extraction actually captured trim; if
      // not, stay on the same step — Clyde's own reply (already prompted
      // to ask a follow-up when a request is ambiguous) naturally
      // re-asks, no separate retry logic needed here.
      if(!wizardDone){
        const step=WIZARD_STEPS[wizardStep];
        if(step&&step.key==="trim"&&extractedIntent&&extractedIntent.trim){
          setWizardStep(function(s){return s+1;});
        }
      }
      // Same sentinel-token pattern as ClydePanel.jsx's OPEN_SCHEDULER/
      // OPEN_BUYING — strip the token out of the displayed text, forward
      // the request up to HomeFleetBuying (the parent), which relays it
      // into BuyingModal's own confirm UI rather than completing the
      // deal silently the moment the token appears.
      let rep=rawRep;
      // The hard gate itself: only once Clyde has recapped pendingHandoff
      // and the household has explicitly confirmed it does this actually
      // flush anything to the parent's intent — pendingHandoff (the
      // original handoff) plus whatever accumulated in bufferedIntent
      // while waiting, merged in one shot so BuyingModal mounts with the
      // full confirmed picture, never a partial one. Also moves the
      // wizard from fully "done" to specifically the trim step — Clyde's
      // own remaining deep-dive, not Kate's territory.
      let wizardJustStarted=false;
      if(rep.indexOf("HANDOFF_CONFIRMED")>=0){
        rep=rep.replace(/\s*HANDOFF_CONFIRMED\s*/g," ").trim()||"Great — let's get started.";
        setHandoffConfirmed(true);
        onIntentUpdate&&onIntentUpdate({...(pendingHandoff||{}),...(bufferedIntent||{})});
        setBufferedIntent(null);
        setWizardStep(WIZARD_STEPS.length-1);
        wizardJustStarted=true;
      }
      if(rep.indexOf("MARK_PURCHASED")>=0){
        rep=rep.replace(/\s*MARK_PURCHASED\s*/g," ").trim()||"Let's get this marked as purchased.";
        onMarkPurchasedRequested&&onMarkPurchasedRequested();
      }
      // Replaces the streaming placeholder in place (matched by
      // _streamId) rather than appending a new message — p here (not the
      // closed-over msgs) is already correct for the hidden case too,
      // same as before: setMsgs(next) above was skipped when hidden, so
      // p already reflects "no user bubble was ever added."
      setMsgs(function(p){
        const replaced=p.map(function(m){return m._streamId===streamId?{role:"assistant",content:rep,sources:sources,groundedListingUrls:groundedListingUrls,comparisons:comparisons}:m;});
        // Appended in the same update as the reply it follows, so the
        // question can never land above the message that introduced it.
        return wizardJustStarted?replaced.concat([clydeWizardQuestion(WIZARD_STEPS[WIZARD_STEPS.length-1])]):replaced;
      });
    }catch(e){
      setErr(e.message||"Connection error — try again.");
      setMsgs(function(p){return p.map(function(m){return m._streamId===streamId?{role:"assistant",content:"I had trouble with that. Please try again."}:m;});});
    }
    setBusy(false);
  };

  // Auto-kickoff: when a real handoff exists, Clyde's own recap+confirm
  // reply should be the first thing visible in this conversation — fires
  // one hidden turn to get that real model reply, rather than waiting on
  // the household to say something first. sendRef always points at the
  // latest send closure without needing it in this effect's own deps
  // (send is recreated every render, so listing it directly would either
  // refire this on every render or need a lint suppression — a ref sidesteps
  // both cleanly, a standard pattern, not a suppressed warning).
  const sendRef=useRef(send);
  sendRef.current=send;
  const kickedOffRef=useRef(false);
  useEffect(function(){
    if(pendingHandoff&&!kickedOffRef.current){
      kickedOffRef.current=true;
      sendRef.current("Kate just handed this off to you.",true);
    }
  },[pendingHandoff]);

  // freshVehicle=false ("Continue this deal"): exactly the prior silent-
  // reset behavior — clears the transcript/wizard state only, leaves
  // dealId/conversationId/the parent's intent untouched, so a follow-up
  // turn keeps merging onto the same deal. freshVehicle=true ("New
  // vehicle"): the same transcript/wizard reset, PLUS clears dealId (via
  // onDealIdChange, the same prop BuyingModal's own completeDeal path
  // already uses to learn the linked deal), conversationId (local to
  // this component, so chat.js mints a genuinely new conversations row
  // next turn instead of appending to the old one), and the parent's
  // intent/worksheetSummary (via the new onIntentClear prop) — so the
  // worksheet actually unmounts/goes blank rather than continuing to show
  // the previous vehicle while a new one is discussed.
  function resetChat(freshVehicle){
    setShowNewChatPrompt(false);
    setMsgs([clydeGreetingMessage()]);setErr("");setWizardStep(WIZARD_STEPS.length);setWizardAnswers(pendingHandoff||{});setTrimDigDeeper(null);setHandoffConfirmed(!pendingHandoff);setBufferedIntent(null);
    if(freshVehicle){
      setConversationId(null);
      onDealIdChange&&onDealIdChange(null);
      onIntentClear&&onIntentClear();
    }
    // Effect deps don't change on a reset (pendingHandoff itself is the
    // same object), so the auto-kickoff effect won't refire on its own —
    // re-triggering it directly here is what actually gets Clyde's recap
    // to run again after a reset. Deferred one tick so the resets above
    // have actually applied first — calling sendRef.current synchronously
    // here would still run against the PRE-reset send() closure
    // (wizardDone/handoffConfirmed as they were a moment ago), since React
    // batches the setState calls above rather than applying them
    // immediately.
    if(pendingHandoff)setTimeout(function(){sendRef.current("Kate just handed this off to you.",true);},0);
  }


  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:"linear-gradient(180deg,"+CT.navy+" 0%,"+CT.navyMid+" 100%)"}}>
      {/* Header */}
      <div style={{padding:"16px 18px",borderBottom:"1px solid "+CT.border,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <div style={{width:44,height:44,borderRadius:"50%",background:"linear-gradient(135deg,"+CT.sky+","+CT.navyMid+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:"0 0 0 2px "+CT.navyLt+",0 0 16px rgba(74,158,204,0.3)"}}>🔑</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:800,fontSize:17,color:"#fff"}}>Clyde</div>
          <div style={{fontSize:11,color:CT.sky,marginTop:1,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
            Vehicle Purchase Advisor
            <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:mode==="live"?"rgba(22,163,74,0.2)":"rgba(180,83,9,0.2)",color:mode==="live"?"#4ade80":"#fcd34d",border:"1px solid "+(mode==="live"?"rgba(22,163,74,0.3)":"rgba(180,83,9,0.3)")}}>
              {mode==="live"?"LIVE":"DEMO"}
            </span>
          </div>
        </div>
        {msgs.length>0&&(showNewChatPrompt?(
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <button onClick={function(){resetChat(true);}} title="Clears this deal's linked worksheet and starts completely fresh" style={{background:CT.sky,color:CT.navy,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>New vehicle</button>
            <button onClick={function(){resetChat(false);}} title="Clears the transcript only — keeps this deal's worksheet and history" style={{background:"none",border:"1px solid "+CT.border,color:CT.offWhite,borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>Continue deal</button>
            <button onClick={function(){setShowNewChatPrompt(false);}} title="Cancel" style={{background:"none",border:"none",color:CT.muted,fontSize:14,cursor:"pointer",padding:"2px 4px",lineHeight:1}}>✕</button>
          </div>
        ):(
          <button onClick={function(){setShowNewChatPrompt(true);}} style={{background:"none",border:"none",color:CT.muted,fontSize:12,cursor:"pointer",fontWeight:600}}>New chat</button>
        ))}
        {onCollapse&&<button onClick={onCollapse} title="Collapse — the worksheet keeps its own state, nothing here is lost" style={{background:"none",border:"none",color:CT.muted,fontSize:12,cursor:"pointer",fontWeight:600}}>▲ Collapse</button>}
      </div>

      {/* Fleet strip */}
      <div style={{padding:"8px 16px",borderBottom:"1px solid "+CT.border,display:"flex",gap:12,flexShrink:0}}>
        {fleet.map(function(v){const eq=Math.max(0,Math.round(num(resolveValue(v).amount)-num(v.loan)));return(
          <div key={v.id} style={{flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14}}>🚗</span>
            <div><div style={{fontSize:11,fontWeight:700,color:CT.offWhite,whiteSpace:"nowrap"}}>{v.nickname}</div><div style={{fontSize:10,color:eq>0?CT.sky:CT.muted,fontWeight:600}}>${eq.toLocaleString()}</div></div>
          </div>
        );})}
      </div>

      {/* Messages */}
      <div ref={msgsRef} style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column"}}>
        {/* Guided-intake wizard — now just the one "trim" step. Renders
            the prompt as an assistant-style bubble, plus (once YMM is
            known) a rough MSRP-percentage price estimate and a live
            "dig deeper" research option. Answered through the normal
            composer below via the real Claude turn in send() — no
            client-side interception needed for a single Claude-routed
            step. */}
        {msgs.map(function(m,i){
          const isA=m.role==="assistant";
          // Streaming placeholder with nothing in it yet — the busy
          // typing-dots block below already covers this exact gap, same
          // as it always has for the time between send() and the first
          // content arriving; skip rendering an empty bubble on top of it.
          if(isA&&m.streaming&&!m.content)return null;
          // Hold back parseListings/token-stripping on a string that's
          // still incomplete — render the accumulating raw text live
          // while m.streaming is true, then run the normal, unchanged
          // parsing/stripping once this placeholder is replaced with the
          // finished message at stream-end (send()'s own final setMsgs).
          const parsed=isA&&!m.streaming?parseListings(m.content,m.groundedListingUrls):null;
          return(
            <div key={i} style={{display:"flex",justifyContent:isA?"flex-start":"flex-end",marginBottom:10,gap:8,alignItems:"flex-end"}}>
              {isA&&<div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,"+CT.sky+","+CT.navyMid+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>🔑</div>}
              <div style={{maxWidth:"80%",background:isA?"linear-gradient(135deg,"+CT.navyMid+","+CT.navyLt+")":"linear-gradient(135deg,"+CT.sky+",#5A7298)",borderRadius:isA?"4px 14px 14px 14px":"14px 4px 14px 14px",padding:"10px 13px",fontSize:13,lineHeight:1.6,color:"#fff",whiteSpace:"pre-wrap",border:isA?"1px solid "+CT.border:"none"}}>
                {isA?(m.streaming?m.content:parsed.text):m.content}
                {isA&&!m.streaming&&parsed.listings.map(function(l,li){return <ListingCard key={li} l={l}/>;})}
                {isA&&!m.streaming&&<ComparisonCards comparisons={m.comparisons}/>}
                {isA&&!m.streaming&&m.sources&&m.sources.length>0&&(
                  <div style={{marginTop:10,paddingTop:8,borderTop:"1px solid "+CT.border}}>
                    <div style={{fontSize:10,color:CT.muted,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>Sources found</div>
                    {m.sources.map(function(s,si){return(
                      <a key={si} href={s.url} target="_blank" rel="noopener noreferrer" style={{display:"block",fontSize:11,color:CT.sky,textDecoration:"none",marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.title} →</a>
                    );})}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {/* The trim-estimate panel renders BELOW the conversation. It is
            live, state-dependent UI (the dig-deeper button reads current
            input and research state), not a message -- so unlike the
            wizard QUESTION, which now lives in msgs, it cannot simply be
            appended. Anything not a message that sits above msgs.map
            appears before Clyde has said anything. */}
        {!wizardDone&&(function(){
          const step=WIZARD_STEPS[wizardStep];
          const canShowTrimEstimate=step.key==="trim"&&wizardAnswers.make&&wizardAnswers.model;
          const estBase=canShowTrimEstimate?(wizardAnswers.budgetType==="monthly"&&wizardAnswers.budget?solveLoan({monthlyPayment:wizardAnswers.budget,downPayment:wizardAnswers.downPayment||0,annualRatePct:FALLBACK_BASE_RATE,termMonths:72}).price:wizardAnswers.budget):null;
          return(
            <div style={{marginBottom:12}}>
              {canShowTrimEstimate&&(
                <div style={{marginLeft:36,marginTop:4,padding:"11px 13px",background:"rgba(255,255,255,0.04)",border:"1px solid "+CT.border,borderRadius:10,maxWidth:"80%"}}>
                  {!estBase?(
                    <div style={{fontSize:11,color:CT.muted}}>Rough trim pricing will show here once we know your budget.</div>
                  ):(
                    <div>
                      <div style={{fontSize:10,color:CT.muted,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Rough estimate from your budget — not model-specific</div>
                      {[["Base trim",0.92],["Mid trim",1.0],["Top trim",1.12]].map(function(t){return(
                        <div key={t[0]} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#fff",marginBottom:4}}><span>{t[0]}</span><span className="mono">${Math.round(estBase*t[1]).toLocaleString()}</span></div>
                      );})}
                      {mode==="live"&&(
                        <button onClick={async function(){
                          setTrimDigging(true);setTrimDigDeeper(null);
                          const res=await fetchRealTrimPrice(wizardAnswers.year,wizardAnswers.make,wizardAnswers.model,inp.trim()||wizardAnswers.trim||"");
                          setTrimDigDeeper(res);setTrimDigging(false);
                        }} disabled={trimDigging} style={{marginTop:6,width:"100%",background:"none",border:"1px solid "+CT.sky,borderRadius:8,padding:"7px 0",color:CT.sky,fontSize:11,fontWeight:700,cursor:trimDigging?"default":"pointer"}}>
                          {trimDigging?"Searching...":"🔍 Dig deeper — real pricing for a specific trim"}
                        </button>
                      )}
                      {trimDigDeeper&&trimDigDeeper.amount!=null&&(
                        <div style={{marginTop:8,fontSize:12,color:"#fff"}}>
                          Real estimate: ${trimDigDeeper.amount.toLocaleString()}{trimDigDeeper.low!=null&&trimDigDeeper.high!=null?" (range $"+trimDigDeeper.low.toLocaleString()+"–$"+trimDigDeeper.high.toLocaleString()+")":""}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {busy&&!msgs.some(function(m){return m.streaming&&m.content;})&&(
          <div style={{display:"flex",alignItems:"flex-end",gap:8,marginBottom:10}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,"+CT.sky+","+CT.navyMid+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>🔑</div>
            <div style={{background:"linear-gradient(135deg,"+CT.navyMid+","+CT.navyLt+")",borderRadius:"4px 14px 14px 14px",border:"1px solid "+CT.border,padding:"11px 14px",display:"flex",gap:5}}>
              {[0,1,2].map(function(i){return <div key={i} style={{width:7,height:7,borderRadius:"50%",background:CT.sky,animation:"clydeTyping 1.2s ease-in-out "+(i*0.2)+"s infinite"}}/>;})}</div>
          </div>
        )}
        {err&&<div style={{fontSize:12,color:"#f87171",background:"rgba(239,68,68,0.1)",borderRadius:8,padding:"8px 12px",marginBottom:8}}>{err}</div>}
      </div>

      {/* Input */}
      <div style={{padding:"10px 14px 16px",borderTop:"1px solid "+CT.border,flexShrink:0}}>
        <div style={{display:"flex",gap:8,background:"rgba(255,255,255,0.05)",border:"1px solid "+CT.border,borderRadius:12,padding:"5px 5px 5px 12px",alignItems:"flex-end"}}>
          <textarea ref={inpRef} className="ci2" value={inp} onChange={function(e){setInp(e.target.value);}} onKeyDown={function(e){if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Tell Clyde what you're looking for..." rows={1} style={{flex:1,background:"none",border:"none",color:"#fff",fontSize:13,resize:"none",fontFamily:"inherit",lineHeight:1.5,maxHeight:80,overflowY:"auto",padding:"5px 0",caretColor:CT.sky}} onInput={function(e){e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,80)+"px";}}/>
          <button onClick={function(){send();}} disabled={!inp.trim()||busy} style={{background:inp.trim()&&!busy?CT.sky:CT.slate,border:"none",borderRadius:9,width:34,height:34,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>➤</button>
        </div>
        <div style={{fontSize:10,color:CT.slate,textAlign:"center",marginTop:6}}>Clyde uses AI · Estimates only · Verify all figures with dealer</div>
      </div>
    </div>
  );
}

/* ── Root ── */
// Wired into HomeFleetApp.jsx as of this pass — reached via the header's
// "Shop" link or Clyde's OPEN_BUYING handoff, both of which already
// gate out demo accounts before this ever mounts (see HomeFleetApp.jsx),
// so `mode` below is always "live": there's no reachable path into this
// component for an account this flow would need to show simulated data
// to. vehicles/accessLevel/householdState/householdZip all arrive as
// props now, already resolved by HomeFleetApp.jsx's own /api/fleet+
// /api/me fetch — this component no longer fetches its own copy of the
// same data (it used to, from when this file was still a standalone,
// unwired page reachable only by hand-navigating a same-origin URL).
// Own header/logo/mode-toggle/vehicle-count chrome removed too — the
// HomeFleetApp shell's own sticky header (logo, portfolio totals) is
// already visible above whatever this renders, same as AdminDashboard.jsx
// and AccountSettings.jsx, which never had their own header chrome either.
// Claude's extract_deal_intent tool only ever sees vehicle nicknames (it
// has no way to know internal ids — see functions/api/chat.js's own
// comment on this same gap, deliberately left for the client side to
// resolve). Shared by both the initial pendingHandoff (may itself carry
// an unresolved nickname, since ClydePanel.jsx's own extraction uses the
// identical tool/schema) and every later incremental merge.
function withResolvedTrade(obj,vehicles){
  if(!obj||!obj.tradeVehicleNickname)return obj;
  const match=vehicles.find(function(v){return v.nickname.toLowerCase()===obj.tradeVehicleNickname.toLowerCase();});
  return match?{...obj,tradeId:match.id}:obj;
}

// Which intent fields say WHICH VEHICLE this is, versus what deal is being
// built on it. See HOMEFLEET_VEHICLE_IDENTITY_PLAN.md -- nothing separated
// these before, so deal terms silently followed the household onto a
// different car.
//
// isNew is identity, not a filter: new and pre-owned resolve their price
// from entirely different sources, so switching is a genuine vehicle
// change rather than a preference tweak.
// Defense in depth for listings, a level below identityChanged. Phase 1's
// remount should make a listing from another vehicle impossible; this is
// for when it happens anyway, because the failure mode is a purchase offer
// carrying someone else's VIN.
//
// Normalized rather than strict: real listing data differs from intent in
// ways that are not real differences. The live search returns 'FIAT' where
// intent says 'Fiat', and CR-V / CRV both occur. A strict compare would
// reject valid listings, which is the worst way for a guard to fail --
// it looks exactly like the guard working.
function normVehicleWord(v){return String(v==null?"":v).toLowerCase().replace(/[^a-z0-9]/g,"");}
// Prefix either way, not equality. Sub-model and trim words ride along in
// these fields constantly ('500' vs '500X', 'CR-V' vs 'CR-V Hybrid',
// 'Mercedes' vs 'Mercedes-Benz'), and treating those as a different
// vehicle would fire on ordinary search noise instead of on the stale
// cross-vehicle state this is here to catch.
function vehicleWordMatches(a,b){
  const x=normVehicleWord(a),y=normVehicleWord(b);
  if(!x||!y)return false;
  return x.indexOf(y)===0||y.indexOf(x)===0;
}
function listingMatchesVehicle(l,make,model){
  return !!l&&vehicleWordMatches(l.make,make)&&vehicleWordMatches(l.model,model);
}

const IDENTITY_FIELDS=["year","make","model","trim","isNew"];
const DEAL_FIELDS=["askingPrice","discount","incentives","financingRate","financingTermMonths","tradeAllowance","downPayment"];
// BOTH sides must be present for a change to count. A field going absent
// to present is REFINEMENT, not a change: Clyde routinely captures
// make/model on one turn and trim two turns later, and treating that as a
// new vehicle would wipe a deal being built on the car he is still
// describing. 'EX' -> 'Touring' is a real change; null -> 'EX' is not.
// Deliberately not a deep or normalized compare -- extract_deal_intent
// emits these as plain strings/numbers, and a case-insensitive match here
// would only paper over an upstream inconsistency worth seeing.
function identityChanged(prev,partial){
  if(!prev||!partial)return false;
  return IDENTITY_FIELDS.some(function(f){
    return prev[f]!=null&&partial[f]!=null&&prev[f]!==partial[f];
  });
}

export default function HomeFleetBuying({vehicles,restoredIntent,resumableConversation,accessLevel,householdState,householdZip,pendingHandoff,onVehicleAdded,onVehicleRemoved,onOpenGeneralChat}){
  const mode="live";
  // Always starts null now, even when a real handoff exists — intent is
  // what actually drives BuyingModal mounting (and so all its real-money
  // fetches: fred-rate, market-listings, research-incentives), and none
  // of that may fire until the household has explicitly confirmed
  // Clyde's recap of pendingHandoff. pendingHandoff itself is passed
  // straight to ClydeChat below, which owns the confirmation gate and
  // only calls onIntentUpdate (populating this intent, for the first
  // time) once HANDOFF_CONFIRMED actually arrives.
  const[intent,setIntent]=useState(null);
  // Lifted out of ClydeChat (which used to own this as local state) —
  // BuyingModal needs it too (completeDeal posts to
  // /api/deals/:id/complete), and two independent copies of "the linked
  // deal's id" would risk drifting out of sync with each other.
  const[dealId,setDealId]=useState(null);
  // Latest worksheet snapshot (a plain string, or null before a real
  // vehicle exists) — reported up from BuyingModal via onWorksheetChange
  // and threaded down into ClydeChat, which forwards it into
  // buildClydeSystem as a second, uncached system-prompt block. See that
  // function's own comment for why it's a separate block, not folded
  // into the existing cached one.
  const[worksheetSummary,setWorksheetSummary]=useState(null);
  // Structured sibling of worksheetSummary — the live worksheet's own
  // Net price (a plain number, or null before a real vehicle exists),
  // reported up from BuyingModal via onNetPriceChange and threaded down
  // into ClydeChat, which sends it to chat.js as its own request field
  // (not folded into the prose system-prompt text) so calculate_loan can
  // pull the current price server-side without parsing worksheetSummary.
  const[currentNetPrice,setCurrentNetPrice]=useState(null);
  // Every dealer reply across the whole household, not just this deal's.
  // Fetched here rather than inside ClydeChat because the household's own
  // Dealer Responses view reads the same state, and two fetches of the
  // same record would let the panel and Clyde disagree about what a
  // dealer said -- the exact failure the incentive work already fixed
  // once. Refreshed when the linked deal changes and when something in
  // this component records a revision (dealerResponsesTick).
  const[dealerResponses,setDealerResponses]=useState(null);
  const[dealerResponsesTick,setDealerResponsesTick]=useState(0);
  // Structured sibling of worksheetSummary/currentNetPrice, same pattern
  // — the worksheet's own live VinAudit trade-in pull (BuyingModal's
  // liveTradeOffer), reported up via onTradeInfoChange and threaded down
  // into ClydeChat/callClydeAPI/buildClydeSystem, so that real paid call
  // actually reaches Clyde as its own separately-labeled figure instead
  // of being fetched and discarded (see buildClydeSystem's own comment).
  // {tradeId,liveTradeOffer} or null — never just liveTradeOffer alone,
  // since buildClydeSystem needs tradeId to know WHICH fleet vehicle the
  // live figure belongs to.
  const[tradeInfo,setTradeInfo]=useState(null);
  // Structured sibling, same pattern again — the worksheet's own total
  // upfront amount (trade equity + manual cash down payment), reported up
  // via onDownPaymentChange so calculate_loan's own down_payment can
  // default to it server-side instead of silently defaulting to 0 — see
  // BuyingModal's own onDownPaymentChange call for the full reasoning.
  const[currentDownPayment,setCurrentDownPayment]=useState(null);
  // Structured sibling again, for compare_prices: the worksheet's asking
  // price, offer, net price and retail value as displayed, reported up via
  // onWorksheetFiguresChange. See BuyingModal's worksheetFiguresFor.
  const[worksheetFigures,setWorksheetFigures]=useState(null);
  // One-shot signal: Clyde's own MARK_PURCHASED token detection (inside
  // ClydeChat) sets this true; BuyingModal watches it, opens the exact
  // same confirm UI the manual button uses, then reports back via
  // onCompletionHandled so this resets to false — Clyde OFFERS the
  // action here, never completes it silently on token detection alone.
  const[completionRequested,setCompletionRequested]=useState(false);
  // Chat now sits collapsible above the worksheet (not beside it — the
  // old side-by-side desktop grid / mobile horizontal-scroll-snap strip
  // is gone) — the worksheet is the dominant, always-visible element,
  // matching every screen size the same way now, no separate mobile
  // layout needed at all. Starts expanded (nothing else to show yet);
  // auto-collapses once, the same one-time guard the old scroll-snap
  // auto-scroll used to keep from re-firing on every later intent
  // update, just driving a collapse instead of a scroll now. ClydeChat
  // itself is NEVER unmounted by collapsing — only visually hidden (see
  // the render below) — so its own message history/dealId survive
  // freely toggling back and forth.
  const[chatExpanded,setChatExpanded]=useState(true);
  const hasAutoCollapsed=useRef(false);

  // Not metered, not paid -- this reads our own D1 through
  // /api/deal-responses, so unlike the incentives/listings effects it
  // carries no cost per fire. It still depends on two narrow values
  // rather than anything that changes as the household types: a
  // household with a chat open should not re-read the record on every
  // keystroke.
  //
  // There is no push channel from a dealer's submission back to an open
  // browser, so a reply that lands while this page is sitting open is
  // picked up on the next tick, not the instant it arrives. That is a
  // known gap, acceptable while responses arrive by email minutes apart;
  // if it ever needs to be live it wants polling or a socket, not a
  // wider dependency list here.
  useEffect(function(){
    let cancelled=false;
    fetch("/api/deal-responses")
      .then(function(r){return r.ok?r.json():null;})
      .then(function(d){if(!cancelled&&d)setDealerResponses(d.deals||[]);})
      .catch(function(){/* the block is simply omitted; Clyde is told nothing rather than something wrong */});
    return function(){cancelled=true;};
  },[dealId,dealerResponsesTick]);

  // Derived, not stored. buildDealerResponsesBlock returns null when no
  // dealer has replied yet, and buildClydeSystem omits the block entirely
  // on null -- deliberately, because an empty "DEALER RESPONSES: none"
  // block would have Clyde telling a household nobody has replied when
  // the truth is they have not sent anything yet.
  const dealerResponsesBlock=buildDealerResponsesBlock(dealerResponses);
  // Count of dealers who have actually replied, for the worksheet's own
  // button label. Derived from the same fetch the block is built from, so
  // the badge and Clyde cannot disagree about how many replies exist.
  const dealerResponseCount=(dealerResponses||[]).reduce(function(n,d){return n+((d.responses&&d.responses.length)||0);},0);

  // Mirrors `intent` synchronously so onIntentUpdate can read the PREVIOUS
  // value without an updater function. The identity comparison below has
  // to happen outside setIntent's updater: it also bumps vehicleGen, and
  // an updater that fires a second setState is not pure (React may invoke
  // it twice). Every site that sets intent goes through commitIntent so
  // this can't drift -- there are three, and a fourth that forgot would
  // silently make prev stale and suppress a real reset.
  const intentRef=useRef(null);
  function commitIntent(next){intentRef.current=next;setIntent(next);}

  // True while the worksheet is showing a deal that was restored from
  // storage rather than captured in this session. Research is DEFERRED in
  // that state, not skipped: the three lookups below are metered against
  // one shared daily budget (20/24h live, 1 lifetime demo) with no
  // response caching anywhere, so auto-running them on mount would spend
  // three of twenty every time someone reloaded the page -- seven reloads
  // and the household's whole day of research is gone. Deal TERMS restore
  // instantly and for free; the researched figures show as unchecked
  // until a person asks for them. See HOMEFLEET_DEAL_PERSISTENCE_PLAN.md
  // section 1 and its option (A).
  const[researchDeferred,setResearchDeferred]=useState(false);
  const restoreApplied=useRef(false);
  useEffect(function(){
    // Once only, and never over a live conversation. A household who is
    // mid-sentence with Clyde must not have the sheet yanked back to what
    // was stored before they started talking.
    if(restoreApplied.current||!restoredIntent||intentRef.current)return;
    restoreApplied.current=true;
    // Through commitIntent like every other write, so identityChanged and
    // vehicleGen apply to a restored vehicle exactly as they do to a
    // spoken one.
    commitIntent(withResolvedTrade(restoredIntent,vehicles));
    setResearchDeferred(true);
  },[restoredIntent,vehicles]);

  // The deal the stored conversation is linked to, so a resumed thread's
  // next turn appends to the same deal rather than forking a new one.
  // Separate from the intent restore above because the two come from
  // different rows and either can exist without the other: a deal with no
  // open conversation, or a conversation whose deal was completed.
  useEffect(function(){
    if(!resumableConversation||!resumableConversation.linkedDealId)return;
    setDealId(function(cur){return cur||resumableConversation.linkedDealId;});
  },[resumableConversation]);
  // Bumped ONLY on a real vehicle change, and used as BuyingModal's key
  // below. Keying on the identity fields directly would be wrong: the key
  // would also change when trim arrives late (see identityChanged), and a
  // key change remounts, which is exactly the deal-wiping regression the
  // refinement rule exists to prevent. A counter changes when the rule
  // says it changed, not when the string happens to differ.
  const[vehicleGen,setVehicleGen]=useState(0);
  // Deliberately NOT part of `intent`. intent is what extract_deal_intent
  // writes to, so anything parked there is reachable by construction the
  // moment a future field is added to that schema, however careful anyone
  // is about today's. Separate state means the write path is not merely
  // absent right now, it is unreachable from that tool permanently -- the
  // same reasoning as deleting the route rather than guarding it. The cost
  // is the one clearing line in the identity branch below.
  const[offerSuggestion,setOfferSuggestion]=useState(null);
  // Every MSRP Clyde has cited for the CURRENT vehicle, oldest first, kept
  // out of `intent` for the same reason offerSuggestion is (see above).
  //
  // An array rather than a single value, because there is no independent
  // ground truth to check an MSRP against -- no worksheet field holds the
  // real one, the way it holds the price the offer is checked against. So
  // accuracy cannot be verified here and consistency can: a second, different
  // figure for the same vehicle is shown to the household as a disagreement
  // rather than silently replacing the first. Overwriting would hide the one
  // signal actually available. The array is cleared on a vehicle change, so
  // citations for different cars never read as a contradiction.
  const[msrpCitations,setMsrpCitations]=useState([]);
  // Offers Clyde's own conversational research turned up, accumulated for
  // the CURRENT vehicle. Appended rather than replaced: one turn can find
  // several, and a later turn finding one more should not erase the rest.
  // Outside `intent` for the same reason as the two above -- intent is what
  // extract_deal_intent writes to, so anything parked there is reachable by
  // construction the moment a field is added to that schema.
  const[clydeIncentives,setClydeIncentives]=useState([]);

  // Resolves a trade nickname (see withResolvedTrade above) and merges
  // non-destructively — a later partial's fields win per-key over an
  // earlier one's, but nothing already established gets silently wiped
  // just because THIS turn's extraction didn't happen to re-mention it.
  //
  // That merge is right for household context and wrong for deal terms.
  // Deal terms belong to a VEHICLE, and carrying them across a vehicle
  // change is what put a Fiat's $11,799 listing price and $799 discount
  // onto a Honda CR-V worksheet, with the proposal screen still naming
  // the Fiat's VIN (see HOMEFLEET_VEHICLE_IDENTITY_PLAN.md). On a real
  // identity change the seven deal fields are dropped rather than merged
  // forward -- except any the SAME extraction supplied, which describe
  // the new vehicle ('let's look at the CR-V, they're asking $22,000')
  // and are the one case where a deal field legitimately survives.
  function onIntentUpdate(partial){
    const prev=intentRef.current;
    const changed=identityChanged(prev,partial);
    const merged={...(prev||{}),...partial};
    if(changed)DEAL_FIELDS.forEach(function(f){if(partial[f]==null)delete merged[f];});
    // A real capture supersedes a restore -- the household is talking, so
    // research is wanted again on whatever they just said.
    setResearchDeferred(false);
    commitIntent(withResolvedTrade(merged,vehicles));
    if(changed){setVehicleGen(function(g){return g+1;});setOfferSuggestion(null);setMsrpCitations([]);setClydeIncentives([]);}
  }

  useEffect(function(){
    // make&&model, not "any intent field at all" — the guided wizard
    // (ClydeChat) now sets its very first field (a trade answer) well
    // before there's a real vehicle to show, and collapsing that early
    // would bury the wizard's own remaining steps behind the collapsed
    // bar after just one answer. Waiting for real vehicle identity is
    // what actually makes the worksheet worth looking at.
    const hasIntent=!!(intent&&intent.make&&intent.model);
    if(hasIntent&&!hasAutoCollapsed.current){
      hasAutoCollapsed.current=true;
      setChatExpanded(false);
    }
  },[intent]);

  return(
    <div style={{maxWidth:980,margin:"0 auto",padding:"20px 16px 52px"}}>
      {/* HomeFleetApp.jsx's own getStyles(T) injection already covers the
          class names this file shares with the rest of the app (.fade-up,
          .mono, .spin, .blink), but not this file's own .slide-in/.feed-in/
          .recalc or its Clyde-bubble keyframes (clydePulse/clydeTyping/
          clydeIn) — still needed here, just no longer wrapped in this
          file's own removed full-page chrome. */}
      <style>{STYLES}</style>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div style={{fontSize:18,fontWeight:800,color:T.text}}>Vehicle Purchase Center</div>
        {/* General Clyde's own floating button/drawer is hidden while this
            view is showing (see HomeFleetApp.jsx) — this is the small icon
            that replaces it, reopening that exact same conversation
            in-place rather than a separate viewer, so nothing about it is
            lost or restarted. */}
        {onOpenGeneralChat&&(
          <button onClick={onOpenGeneralChat} style={{display:"flex",alignItems:"center",gap:6,background:T.card,border:"1px solid "+T.border,borderRadius:9,padding:"7px 12px",fontSize:12,fontWeight:700,color:T.textMd}}>
            <span>🔑</span><span>General chat</span>
          </button>
        )}
      </div>

      {/* Buying conversation — collapsible, above the worksheet (which is
          the dominant, always-visible element below). ClydeChat itself is
          ALWAYS mounted here, never conditionally rendered — collapsing
          only hides it visually (display:none), so its own message
          history/dealId are never lost by toggling. Starts expanded (there's
          nothing else to show yet); auto-collapses once real intent exists
          (see the useEffect above), staying out of the way of the worksheet
          from then on unless the household reopens it. Same behavior at
          every screen size now — no separate mobile layout needed. */}
      {!chatExpanded&&(
        <button onClick={function(){setChatExpanded(true);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:T.card,border:"1px solid "+T.border,borderRadius:12,padding:"12px 16px",marginBottom:16,boxShadow:T.shadow}}>
          <span style={{fontSize:18}}>🔑</span>
          <span style={{flex:1,textAlign:"left",fontSize:13,fontWeight:700,color:T.text}}>Chat with Clyde about this deal</span>
          <span style={{fontSize:11,color:T.textSm}}>▼ expand</span>
        </button>
      )}
      <div style={{display:chatExpanded?"flex":"none",flexDirection:"column",height:"56vh",minHeight:380,borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.15)",marginBottom:16}}>
        <ClydeChat fleet={vehicles} mode={mode} onOfferSuggested={setOfferSuggestion} onMsrpCited={function(c){setMsrpCitations(function(prev){return prev.concat([c]);});}} onIncentivesFound={function(list){setClydeIncentives(function(prev){return prev.concat(list);});}} householdState={householdState} householdZip={householdZip} onIntentUpdate={onIntentUpdate} dealId={dealId} onDealIdChange={setDealId} onIntentClear={function(){commitIntent(null);setWorksheetSummary(null);setCurrentNetPrice(null);setTradeInfo(null);setCurrentDownPayment(null);setWorksheetFigures(null);setOfferSuggestion(null);setMsrpCitations([]);setClydeIncentives([]);}} onMarkPurchasedRequested={function(){setCompletionRequested(true);}} onCollapse={function(){setChatExpanded(false);}} pendingHandoff={pendingHandoff} resumableConversation={resumableConversation} worksheetSummary={worksheetSummary} dealerResponsesBlock={dealerResponsesBlock} currentNetPrice={currentNetPrice} tradeId={tradeInfo?tradeInfo.tradeId:undefined} liveTradeOffer={tradeInfo?tradeInfo.liveTradeOffer:undefined} currentDownPayment={currentDownPayment} worksheetFigures={worksheetFigures}/>
      </div>

      {/* Worksheet — renders the moment intent has ANY field at all, not
          just once a full picture is established — BuyingModal already
          falls back sensibly for every field it doesn't have yet
          (intent.make||"Ford", etc, confirmed by reading it), so a sparse
          intent is already something it can render meaningfully from.

          key={vehicleGen} remounts this whole subtree on a real vehicle
          change, resetting every one of its useState values at once --
          selectedListing, selectedProgramId, selectedAddOns, creditTier,
          tradeSource, touchedFields, appliedIntentRef, screen, and
          whatever gets added later. Chosen over clearing them one by one
          for the reason resetChat(true) is already complete: it clears by
          construction, so it cannot miss a field and cannot rot when
          someone adds state next month. The bug it fixes IS a missed
          field, so a fix that needs its own list kept in sync would be
          the same bet that just lost.

          Not extra fetches: retailLookup/listings/incentives already list
          targetMake/targetModel in their deps and refire on a vehicle
          change today, and the old instance's cleanup cancels its own
          in-flight results. screen resets to 'research', which is both
          correct (a new vehicle genuinely is being researched) and the
          fix for a fully-formed proposal staying reachable for the
          vehicle the household just abandoned. */}
      <div style={{borderRadius:16,overflow:"hidden",boxShadow:T.shadow,height:"calc(100vh - 260px)",minHeight:420,display:"flex",flexDirection:"column",border:"1px solid "+T.border}}>
        {Object.keys(intent||{}).length>0?(
          <BuyingModal key={vehicleGen} fleet={vehicles} mode={mode} offerSuggestion={offerSuggestion} onOfferSuggestionHandled={function(){setOfferSuggestion(null);}} msrpCitations={msrpCitations} clydeIncentives={clydeIncentives} dealerResponsesBlock={dealerResponsesBlock} dealerResponseCount={dealerResponseCount} researchDeferred={researchDeferred} onRefreshResearch={function(){setResearchDeferred(false);}} onDealerActivity={function(){setDealerResponsesTick(function(n){return n+1;});}} accessLevel={accessLevel} householdState={householdState} householdZip={householdZip} intent={intent} dealId={dealId} onVehicleAdded={onVehicleAdded} onVehicleRemoved={onVehicleRemoved} completionRequested={completionRequested} onCompletionHandled={function(){setCompletionRequested(false);}} onClose={function(){commitIntent(null);setWorksheetSummary(null);setCurrentNetPrice(null);setTradeInfo(null);setCurrentDownPayment(null);setWorksheetFigures(null);setOfferSuggestion(null);setMsrpCitations([]);setClydeIncentives([]);}} onWorksheetChange={setWorksheetSummary} onNetPriceChange={setCurrentNetPrice} onTradeInfoChange={setTradeInfo} onDownPaymentChange={setCurrentDownPayment} onWorksheetFiguresChange={setWorksheetFigures}/>
        ):(
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",textAlign:"center",background:T.card}}>
            <div style={{fontSize:48,marginBottom:16}}>🚗</div>
            <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:8}}>Vehicle Purchase Center</div>
            <div style={{fontSize:14,color:T.textMd,maxWidth:340,lineHeight:1.7,marginBottom:24}}>Tell Clyde what you're looking for. He'll research current incentives, find inventory, build your deal structure, and write the offer you send to dealers yourself — all before you set foot in a showroom.</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:320}}>
              {[{icon:"🎁",text:"Live incentive research — current rebates & APR offers"},{icon:"🔍",text:"Live inventory search — real listings near you"},{icon:"📊",text:"Interactive deal builder — editable, real-time math"},{icon:"📨",text:"Offer sheets you send to the dealers you pick"}].map(function(item){return(
                <div key={item.text} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:9,background:T.slateL,textAlign:"left"}}>
                  <span style={{fontSize:18,flexShrink:0}}>{item.icon}</span>
                  <span style={{fontSize:12,color:T.textMd,lineHeight:1.4}}>{item.text}</span>
                </div>
              );})}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
